import { useEffect, useMemo, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Status } from '@pillar-portal/database/dist/frontend';
import {
    ExternalLink,
    Pencil,
    Plus,
    Trash2,
    Unlink,
    Upload,
} from 'lucide-react';
import { BackButton } from '@/components/ui/back-button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { StatusBadge } from '@/components/ui/status-badge';
import {
    organizationApi,
    authApi,
    authorizationApi,
    userApi,
    sambaConfigApi,
    allowedWebsiteTemplatesApi,
} from '@/lib/pillarApi';
import {
    getOrganizationCapacityLabel,
    organizationHasCapacity,
} from '@/lib/organization-capacity';
import { cn, runInBackground } from '@/lib/utils';
import { editOrgSchema } from '@/lib/validation-organization';
import { useAuthStore } from '@/store/auth';
import { hasInternalAdminPrivileges, hasSuperadminPrivileges } from '@pillar-portal/api-types';
import type { ActiveRoleAssignmentResponse } from '@pillar-portal/api-types';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { SecretsCard } from '@/components/secrets/SecretsCard';
import { OrganizationIpxCredentialPoliciesCard } from '@/components/ipx-credentials/OrganizationIpxCredentialPoliciesCard';
import {
    PolicyPermissionBundlesCard,
    type PolicyPermissionBundleFocusRequest,
} from '@/components/permissions/PolicyPermissionBundlesCard';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { OmnisearchSelect } from '@/components/ui/omnisearch-select';
import { EmbeddedInfiniteScrollList } from '@/components/ui/embedded-infinite-scroll-list';
import { MerchantAccountsListCard } from '@/components/payments-infrastructure/PaymentsInfrastructureListPages';
import { useUnsavedChangesRegistration } from '@/lib/hooks/useUnsavedChangesGuard';
import { useDataInvalidationStore } from '@/store/dataInvalidation';
import { buildRoleScopedQueryKey } from '@/lib/reactQueryRoleScope';
import { appendCustomerOrganizationUuid } from '@/app/customer/customer-organization-search';
import {
    CustomerPortalDeliveryChannel,
    CustomerPortalPaymentRequestsConfigSchema,
    type DocumentTemplateSelector,
    getAllRoleAssignments,
    GetOrganizationResponse,
    OrganizationListItem,
    OrganizationOmnisearchListItem,
    IpgSystemId,
    IpwSystemId,
    type BusinessDetails,
    type CanadaProvince,
    CanadaProvinceKeyList,
    type USState,
    BusinessDetailsSchema,
    formatPhoneNumber,
    isNormalizedPhoneNumberValid,
    normalizeCanadaPostalCode,
    normalizePhoneNumber,
    normalizeUSZipCode,
    USStateKeyList,
    type UserProfileDetailedPublicResponse,
    GetOrganizationResponseSchema,
    IpgSystemIdKeyList,
    IpwSystemIdKeyList,
    type PlatformPaymentMethods,
    type PreviewOrganizationUsersCsvImportResponse,
    type OrganizationUserCsvImportPreviewRow,
    defaultIpgConfigByKey,
    defaultIpwConfigByKey,
    defaultIntegratedWebsiteDisplayNameBySystemId,
    ipgConfigValorDemo,
    ipgConfigValorProd,
    type CustomerPortalPaymentRequestsConfig,
    type IpsConfig,
    type AllowedWebsiteCustomization,
    type AllowedWebsiteBase,
    UrlPatternsRuleAction,
    type UrlPatternsRuleAction as UrlPatternsRuleActionValue,
    type UrlPatternsRule,
    type IpgConfigCustomization,
    type IpwConfigCustomization,
    type IpgConfig,
    type IpwConfig,
    type IpwConfigDealertrack,
    IpgSystemIdKey,
    normalizeNorthAmericanPhoneNumberToE164,
    type MerchantConfig,
    type MerchantBackupMode,
    type PaymentRequestDocumentPolicy,
    type PaymentRequestTemplateSelection,
    type OrganizationDocumentTemplateOverrideDescriptor,
    type OrganizationDocumentTemplateRegistryEntry,
    type RoleAssignmentPermissionsView,
    type ListOrganizationAllowedWebsitesResponse,
    type OrganizationAllowedWebsite,
    type OrganizationAllowedWebsiteCapabilities,
    OrganizationAllowedWebsiteSource,
    type AllowedWebsiteTemplateListItem,
    type AllowedWebsiteTemplateSearchPageResponse,
    IpwConfigDealertrackSchema,
    DealertrackIpwSystemIdKey,
    IpWebsiteType,
    SessionPersistenceType,
    type UnmanagedAllowedWebsite,
    AllowedWebsiteSchemaUnmanaged,
    type ValorIpgConfig,
    IpwSystemIdKey,
    ValorIpgSystemId,
    ValorIpgSystemIdKey,
    ThirdPartySystemId,
    isValidUrlPatternString,
} from '@pillar-portal/api-types';
import { isAdminRoleAssignment } from '@pillar-portal/api-types';
import { formatOrganizationDisplayName } from '@/components/permissions/organizationDisplayName';
import { AllOrganizationalRoles, Role } from '@pillar-portal/database/dist/frontend';

type EditOrgFormInput = z.input<typeof editOrgSchema>;
type EditOrgFormData = z.output<typeof editOrgSchema>;
type OrganizationView = 'ISO' | 'MERCHANT' | 'CUSTOMER';

interface MerchantSystemOption<TSystemId extends number> {
    readonly systemId: TSystemId;
    readonly label: string;
}

export type PendingParentOrganizationAction = 'assign' | 'revoke';
type ParentOrganizationType = 'ISO' | 'MERCHANT';

export interface ParentOrganizationReassignConfirmationState {
    readonly showReassignConfirm: boolean;
    readonly pendingParentAction: PendingParentOrganizationAction | null;
    readonly pendingParentOrganizationName: string | null;
    readonly pendingParentOrganizationType: ParentOrganizationType | null;
}

interface BusinessDetailsEditorState {
    readonly country: 'US' | 'CA';
    readonly legalEntityName: string;
    readonly dba: string;
    readonly department: string;
    readonly mainPhoneNumber: string;
    readonly street: string;
    readonly street2: string;
    readonly city: string;
    readonly usState: USState | '';
    readonly usZip: string;
    readonly caProvince: CanadaProvince | '';
    readonly caPostalCode: string;
}

type DealertrackReconciliationEditorState = IpwConfigDealertrack['reconciliation'];

type TwilioSenderMode = 'from_phone_number' | 'messaging_service_sid';

interface SecurityAllowedWebsiteDisplayRow {
    readonly rowUuid: string;
    readonly source: OrganizationAllowedWebsite['source'];
    readonly websiteConfig: UnmanagedAllowedWebsite;
    readonly status: Status;
    readonly capabilities: OrganizationAllowedWebsiteCapabilities | null;
    readonly templateName: string | null;
    readonly templateUuid: string | null;
    readonly copiedFromTemplateName: string | null;
    readonly templateProvenance: SecurityAllowedWebsiteTemplateProvenance | null;
}

interface SecurityAllowedWebsiteTemplateProvenance {
    readonly name: string;
    readonly templateUuid: string | null;
    readonly canViewTemplate: boolean;
}

interface CustomerPortalPaymentRequestsEditorState {
    readonly enabled: boolean;
    readonly allowEmail: boolean;
    readonly allowSms: boolean;
    readonly templateSelections: readonly PaymentRequestTemplateSelection[];
    readonly documentPolicy?: PaymentRequestDocumentPolicy;
    readonly twilioEnabled: boolean;
    readonly twilioSenderMode: TwilioSenderMode;
    readonly twilioFromPhoneNumber: string;
    readonly twilioMessagingServiceSid: string;
    readonly sendgridEnabled: boolean;
    readonly sendgridFromEmail: string;
    readonly sendgridFromName: string;
    readonly sendgridReplyToEmail: string;
}

interface CustomerPortalPaymentRequestsEditorAvailability {
    readonly canEnableTwilioProvider: boolean;
    readonly canAllowSmsDelivery: boolean;
}

interface MerchantPaymentMethodsEditorState {
    readonly terminal: boolean;
    readonly card: boolean;
    readonly ach: boolean;
    readonly e_invoice: boolean;
}

type PaymentRequestEmailCcEditorState = string[];
type ValorPortalMode = 'PRODUCTION' | 'DEMO' | 'CUSTOM';

export interface DocumentTemplateEditorState {
    readonly selector: DocumentTemplateSelector;
    readonly overrideUuid: string | null;
    readonly defaultTemplateSource: string;
    readonly allowedRootVariables: readonly string[];
    readonly name: string;
    readonly description: string;
    readonly templateSource: string;
    readonly isActive: boolean;
}

interface ValorMerchantConfigEditorState {
    readonly portalMode: ValorPortalMode;
    readonly portalUrl: string;
    readonly defaultApiUrl: string;
    readonly defaultChannel: string;
}

interface UrlPatternsRuleGroupEditorState {
    readonly action: UrlPatternsRuleActionValue;
    readonly urlPatternsText: string;
}

interface AllowedWebsiteCustomizationEditorState {
    readonly allowedUrlPatternsText: string;
    readonly sessionPersistence: SessionPersistenceType;
    readonly promoteSessionCookies: boolean;
    readonly urlPatternsRules: readonly UrlPatternsRuleGroupEditorState[];
    readonly fetchDefaultAction: UrlPatternsRuleActionValue;
    readonly iframeUrlPatterns: readonly UrlPatternsRuleGroupEditorState[];
    readonly iframeDefaultAction: UrlPatternsRuleActionValue;
}

interface IpwConfigCustomizationEditorState {
    readonly urlPattern: string;
    readonly allowedWebsites: readonly AllowedWebsiteCustomizationEditorState[];
    readonly reconciliation?: DealertrackReconciliationEditorState;
}

interface IpgConfigCustomizationEditorState {
    readonly urlPattern: string;
    readonly allowedWebsites: readonly AllowedWebsiteCustomizationEditorState[];
    readonly defaultApiUrl?: string;
    readonly defaultChannel?: string;
}

export interface MerchantConfigValidationIssue {
    readonly fieldLabel: string;
    readonly reason: string;
    readonly anchor: string;
}

type IpwConfigCustomizationEditorStateMap =
    Partial<Record<IpwSystemIdKey, IpwConfigCustomizationEditorState>>;

type IpgConfigCustomizationEditorStateMap =
    Partial<Record<IpgSystemIdKey, IpgConfigCustomizationEditorState>>;

type MerchantConfigEditorCard =
    | 'ipg'
    | 'ipw'
    | 'sidebar'
    | 'security'
    | 'unmanaged-websites'
    | 'merchant';

const MERCHANT_CONFIG_EDITOR_CARD_LABELS: Record<MerchantConfigEditorCard, string> = {
    ipg: 'Integrated Payments Gateways',
    ipw: 'Integrated Payments Websites',
    sidebar: 'Integrated Payment Sidebar',
    security: 'Security',
    'unmanaged-websites': 'Unmanaged Allowed Websites',
    merchant: 'Merchant Configuration',
};

const DEALERTRACK_PAYMENT_CODE_FIELD_LABEL = 'Dealertrack payment code';
const DEALERTRACK_PAYMENT_CODE_FIELD_ANCHOR = 'dealertrack_payment_code';
const DEALERTRACK_PAYMENT_CODE_MAX_LENGTH = 2;

interface UnmanagedWebsiteEditorState {
    readonly name: string;
    readonly url: string;
    readonly allowedUrlPatternsText: string;
    readonly sessionPersistence: SessionPersistenceType;
    readonly promoteSessionCookies: boolean;
    readonly urlPatternsRules: readonly UrlPatternsRuleGroupEditorState[];
    readonly fetchDefaultAction: UrlPatternsRuleActionValue;
    readonly iframeUrlPatterns: readonly UrlPatternsRuleGroupEditorState[];
    readonly iframeDefaultAction: UrlPatternsRuleActionValue;
}

type UnmanagedWebsiteDialogMode = 'add' | 'edit-organization';

interface UnmanagedWebsiteDialogState {
    readonly mode: UnmanagedWebsiteDialogMode;
    readonly organizationAllowedWebsiteUuid: string | null;
    readonly selectedTemplate: AllowedWebsiteTemplateListItem | null;
    readonly editorState: UnmanagedWebsiteEditorState;
    readonly error: string | null;
}

interface PendingUnmanagedWebsiteDeleteState {
    readonly organizationAllowedWebsiteUuid: string;
    readonly websiteName: string;
}

interface OrganizationWebsiteMutationError {
    readonly organizationAllowedWebsiteUuid: string;
    readonly message: string;
}

interface PromoteOrganizationWebsiteDialogState {
    readonly organizationAllowedWebsiteUuid: string;
    readonly websiteName: string;
    readonly templateName: string;
    readonly systemId: ThirdPartySystemId;
    readonly error: string | null;
}

interface AssignUsersToOrganizationDialogProps {
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly organizationName: string;
    readonly availableRoles: readonly Role[];
    readonly onSave: (data: {
        readonly role: Role;
        readonly user_uuids: readonly string[];
    }) => void | Promise<void>;
    readonly isLoading?: boolean;
    readonly error?: Error | null;
}

function buildUserSelectionLabel(user: UserProfileDetailedPublicResponse): string {
    const nameParts: string[] = [user.first_name ?? '', user.last_name ?? '']
        .map((value) => value.trim())
        .filter((value) => value.length > 0);
    const nameLabel: string | null = nameParts.length > 0 ? nameParts.join(' ') : null;
    return nameLabel === null ? user.primary_email : `${nameLabel} (${user.primary_email})`;
}

function getVisibleOrganizationMemberRoleAssignments(
    user: UserProfileDetailedPublicResponse,
    organizationUuid: string,
    showOrganizationMemberSubsidiaries: boolean
): RoleAssignmentPermissionsView[] {
    return getAllRoleAssignments(user).filter((roleAssignment: RoleAssignmentPermissionsView) =>
        showOrganizationMemberSubsidiaries
            ? roleAssignment.organization_uuid !== null
            : roleAssignment.organization_uuid === organizationUuid
    );
}

interface PendingOrganizationMemberImpersonationState {
    readonly role_assignment_uuid: string;
    readonly role: Role;
    readonly organization_name: string | null;
    readonly user_display_name: string;
}

interface OrganizationMembersListProps {
    readonly items: readonly UserProfileDetailedPublicResponse[];
    readonly organizationUuid: string;
    readonly showOrganizationMemberSubsidiaries: boolean;
    readonly onNavigateToUser: (userUuid: string) => void;
    readonly onFocusPermissionPolicy: (permissionsBundleUuid: string) => void;
    readonly onImpersonateRole: (
        roleAssignment: PendingOrganizationMemberImpersonationState
    ) => void;
}

interface OrganizationMemberRoleAssignmentCardItem {
    readonly user_uuid: string;
    readonly primary_email: string;
    readonly user_display_name: string;
    readonly roleAssignment: RoleAssignmentPermissionsView;
}

function OrganizationMembersList({
    items,
    organizationUuid,
    showOrganizationMemberSubsidiaries,
    onNavigateToUser,
    onFocusPermissionPolicy,
    onImpersonateRole,
}: OrganizationMembersListProps): ReactElement {
    const organizationMemberRoleAssignmentItems: OrganizationMemberRoleAssignmentCardItem[] =
        useMemo(
            () =>
                items.flatMap((user: UserProfileDetailedPublicResponse) => {
                    const userDisplayName: string =
                        [user.first_name ?? '', user.last_name ?? '']
                            .map((value: string) => value.trim())
                            .filter((value: string) => value.length > 0)
                            .join(' ') || user.primary_email;
                    return getVisibleOrganizationMemberRoleAssignments(
                        user,
                        organizationUuid,
                        showOrganizationMemberSubsidiaries
                    ).map((roleAssignment: RoleAssignmentPermissionsView) => ({
                        user_uuid: user.user_uuid,
                        primary_email: user.primary_email,
                        user_display_name: userDisplayName,
                        roleAssignment,
                    }));
                }),
            [items, organizationUuid, showOrganizationMemberSubsidiaries]
        );

    return (
        <div className="space-y-2">
            {organizationMemberRoleAssignmentItems.map(
                (item: OrganizationMemberRoleAssignmentCardItem) => {
                    const roleAssignment: RoleAssignmentPermissionsView = item.roleAssignment;
                    return (
                        <div
                            key={roleAssignment.role_assignment_uuid}
                            className="border rounded p-3 hover:bg-gray-50 cursor-pointer flex justify-between items-center gap-4"
                            onClick={() => {
                                onNavigateToUser(item.user_uuid);
                            }}
                        >
                            <div className="min-w-0 flex-1">
                                <div className="font-medium">{item.primary_email}</div>
                                <div className="mt-1 text-sm text-muted-foreground">
                                    Role: {roleAssignment.role}
                                </div>
                                {showOrganizationMemberSubsidiaries ? (
                                    <div className="text-sm text-muted-foreground">
                                        Organization:{' '}
                                        {roleAssignment.organization_name ?? 'Unknown organization'}
                                    </div>
                                ) : null}
                                {roleAssignment.granted_permission_policies.length > 0 ? (
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {roleAssignment.granted_permission_policies.map(
                                            (policy) => (
                                                <Button
                                                    key={policy.permissions_bundle_uuid}
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-6 rounded-full px-2 text-xs"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        onFocusPermissionPolicy(
                                                            policy.permissions_bundle_uuid
                                                        );
                                                    }}
                                                >
                                                    {policy.name}
                                                </Button>
                                            )
                                        )}
                                    </div>
                                ) : null}
                            </div>
                            <div className="flex items-center gap-2 flex-wrap justify-end">
                                <StatusBadge status={roleAssignment.status} />
                                {roleAssignment.status === Status.ACTIVE &&
                                roleAssignment.can_impersonate === true ? (
                                    <Button
                                        variant="destructive"
                                        size="sm"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            onImpersonateRole({
                                                role_assignment_uuid:
                                                    roleAssignment.role_assignment_uuid,
                                                role: roleAssignment.role,
                                                organization_name:
                                                    roleAssignment.organization_name,
                                                user_display_name: item.user_display_name,
                                            });
                                        }}
                                    >
                                        Impersonate
                                    </Button>
                                ) : null}
                            </div>
                        </div>
                    );
                }
            )}
        </div>
    );
}

function AssignUsersToOrganizationDialog({
    open,
    onOpenChange,
    organizationName,
    availableRoles,
    onSave,
    isLoading = false,
    error = null,
}: AssignUsersToOrganizationDialogProps): ReactElement {
    const [selectedRole, setSelectedRole] = useState<Role | ''>('');
    const [selectedUsers, setSelectedUsers] = useState<UserProfileDetailedPublicResponse[]>([]);
    const [userSearchResetToken, setUserSearchResetToken] = useState<string>('0');

    useEffect(() => {
        if (!open) {
            return;
        }
        setSelectedRole('');
        setSelectedUsers([]);
        setUserSearchResetToken('0');
    }, [open]);

    const userSearchFetchPage = useMemo(() => {
        return async (params: {
            readonly queryText?: string;
            readonly limit: number;
            readonly cursor?: string;
        }) => {
            const response = await userApi.searchUsers({
                q: params.queryText,
                limit: params.limit,
                cursor: params.cursor,
            });
            const selectedUserUuidSet: Set<string> = new Set(
                selectedUsers.map((selectedUser) => selectedUser.user_uuid)
            );
            return {
                items: response.users.filter(
                    (user) => !selectedUserUuidSet.has(user.user_uuid)
                ),
                pagination: response.pagination,
            };
        };
    }, [selectedUsers]);

    const canSave: boolean =
        !isLoading && selectedRole !== '' && selectedUsers.length > 0;

    const handleSave = (): void => {
        if (selectedRole === '' || selectedUsers.length === 0) {
            return;
        }
        const result = onSave({
            role: selectedRole,
            user_uuids: selectedUsers.map((selectedUser) => selectedUser.user_uuid),
        });
        if (result instanceof Promise) {
            runInBackground(result, (saveError) => {
                console.error('Error assigning users to organization', saveError);
            });
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Assign Users</DialogTitle>
                    <DialogDescription>
                        Assign one organizational role to one or more existing users in{' '}
                        {organizationName}.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                    {error !== null ? (
                        <Alert variant="destructive">
                            <AlertDescription>{error.message}</AlertDescription>
                        </Alert>
                    ) : null}
                    <div className="space-y-2">
                        <Label>Organization</Label>
                        <div className="rounded-md border px-3 py-2 text-sm">{organizationName}</div>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="assign-users-role">Role</Label>
                        <Select
                            value={selectedRole}
                            onValueChange={(value) => {
                                const nextRole = availableRoles.find((role) => role === value);
                                if (nextRole !== undefined) {
                                    setSelectedRole(nextRole);
                                }
                            }}
                            disabled={isLoading}
                        >
                            <SelectTrigger id="assign-users-role">
                                <SelectValue placeholder="Select role" />
                            </SelectTrigger>
                            <SelectContent>
                                {availableRoles.map((role) => (
                                    <SelectItem key={role} value={role}>
                                        {role}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <OmnisearchSelect<UserProfileDetailedPublicResponse, string>
                            label="Search users"
                            placeholder="Type to search..."
                            minQueryLength={3}
                            pageSize={25}
                            fetchPage={userSearchFetchPage}
                            getItemId={(item) => item.user_uuid}
                            getItemLabel={buildUserSelectionLabel}
                            getItemFilterText={(item) =>
                                `${item.primary_email} ${item.first_name ?? ''} ${item.last_name ?? ''} ${item.user_uuid}`
                            }
                            selectedItemId={null}
                            resetToken={userSearchResetToken}
                            disabled={isLoading}
                            onSelect={(user) => {
                                setSelectedUsers((currentUsers) => [...currentUsers, user]);
                                setUserSearchResetToken((currentToken) => `${Number(currentToken) + 1}`);
                            }}
                        />
                        {selectedUsers.length > 0 ? (
                            <div className="flex flex-wrap gap-2" aria-label="Selected users">
                                {selectedUsers.map((selectedUser) => (
                                    <span
                                        key={selectedUser.user_uuid}
                                        className="inline-flex items-center rounded-full border border-gray-300 bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700"
                                    >
                                        <span>{buildUserSelectionLabel(selectedUser)}</span>
                                        <button
                                            type="button"
                                            className="ml-2 text-gray-500 hover:text-gray-700"
                                            aria-label={`Remove ${selectedUser.primary_email}`}
                                            onClick={() => {
                                                setSelectedUsers((currentUsers) =>
                                                    currentUsers.filter(
                                                        (user) =>
                                                            user.user_uuid !== selectedUser.user_uuid
                                                    )
                                                );
                                            }}
                                        >
                                            x
                                        </button>
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground">
                                No users selected.
                            </p>
                        )}
                    </div>
                </div>
                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                            onOpenChange(false);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button type="button" onClick={handleSave} disabled={!canSave}>
                        Assign Users
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

const defaultBusinessDetailsEditorState: BusinessDetailsEditorState = {
    country: 'US',
    legalEntityName: '',
    dba: '',
    department: '',
    mainPhoneNumber: '',
    street: '',
    street2: '',
    city: '',
    usState: '',
    usZip: '',
    caProvince: '',
    caPostalCode: '',
};

const defaultCustomerPortalPaymentRequestsEditorState: CustomerPortalPaymentRequestsEditorState = {
    enabled: true,
    allowEmail: true,
    allowSms: true,
    templateSelections: [],
    twilioEnabled: true,
    twilioSenderMode: 'from_phone_number',
    twilioFromPhoneNumber: '(844) 875-4604',
    twilioMessagingServiceSid: '',
    sendgridEnabled: true,
    sendgridFromEmail: 'noreply@anchorbase.com',
    sendgridFromName: '',
    sendgridReplyToEmail: '',
};

const defaultPaymentRequestEmailCcEditorState: PaymentRequestEmailCcEditorState = [];
const defaultMerchantPaymentMethodsEditorState: MerchantPaymentMethodsEditorState = {
    terminal: true,
    card: true,
    ach: false,
    e_invoice: true,
};

function CheckboxField(props: {
    readonly label: string;
    readonly checked: boolean;
    readonly disabled?: boolean;
    readonly className?: string;
    readonly onCheckedChange: (checked: boolean) => void;
}): ReactElement {
    const disabled: boolean = props.disabled ?? false;

    return (
        <div
            className={cn(
                'flex items-center gap-2 text-sm',
                disabled ? 'opacity-50' : undefined,
                props.className
            )}
        >
            <span
                className="-m-[3px] inline-flex p-[3px]"
                onClick={(event) => {
                    if (disabled || event.target !== event.currentTarget) {
                        return;
                    }
                    props.onCheckedChange(!props.checked);
                }}
            >
                <Checkbox
                    aria-label={props.label}
                    checked={props.checked}
                    disabled={disabled}
                    onCheckedChange={props.onCheckedChange}
                />
            </span>
            <span>{props.label}</span>
        </div>
    );
}

function RadioField(props: {
    readonly label: string;
    readonly name: string;
    readonly checked: boolean;
    readonly onChange: () => void;
}): ReactElement {
    return (
        <div className="flex items-center gap-2 text-sm">
            <span
                className="-m-[3px] inline-flex p-[3px]"
                onClick={(event) => {
                    if (event.target !== event.currentTarget) {
                        return;
                    }
                    props.onChange();
                }}
            >
                <input
                    type="radio"
                    aria-label={props.label}
                    name={props.name}
                    checked={props.checked}
                    className="h-4 w-4 accent-blue-600"
                    onChange={props.onChange}
                />
            </span>
            <span>{props.label}</span>
        </div>
    );
}

export const PaymentRequestEmailCcEntrySchema: z.ZodType<string> = z
    .string()
    .trim()
    .email('Enter a valid email address');

const RECEIPT_FIELD_WARNING_TOOLTIP: string = 'This field is required to print payment receipts.';

function isActionableOrganizationUserCsvImportRow(
    row: OrganizationUserCsvImportPreviewRow
): boolean {
    return (
        (row.classification === 'insert' || row.classification === 'update')
        && (row.row_errors?.length ?? 0) === 0
    );
}

function getOrganizationUserCsvImportClassificationLabel(
    row: OrganizationUserCsvImportPreviewRow
): string {
    switch (row.classification) {
        case 'insert':
            return 'Insert';
        case 'update':
            return 'Update';
        case 'noop':
            return 'No-op';
        case 'csv_duplicate_error':
            return 'CSV Duplicate';
        case 'db_ambiguous_match_error':
            return 'Ambiguous Match';
        default:
            return row.classification;
    }
}

function isBlankOptionalField(value: string | undefined): boolean {
    return value === undefined || value.trim().length === 0;
}

function formatOptionalFieldValue(value: string | undefined): string {
    if (value === undefined || value.trim().length === 0) {
        return 'Not configured';
    }
    return value;
}

function hasBusinessDetailsCodeForTimezone(org: GetOrganizationResponse): boolean {
    if (org.businessDetails === undefined) {
        return false;
    }
    if (org.businessDetails.country === 'US') {
        return !isBlankOptionalField(org.businessDetails.address?.zip);
    }
    return !isBlankOptionalField(org.businessDetails.address?.postal_code);
}

function getMissingTimezoneCodeLabel(org: GetOrganizationResponse): 'ZIP' | 'Postal' {
    return org.businessDetails?.country === 'US' ? 'ZIP' : 'Postal';
}

function getOrganizationTimezoneDisplayValue(org: GetOrganizationResponse): string {
    if (org.detected_time_zone !== undefined) {
        return org.detected_time_zone;
    }
    if (!hasBusinessDetailsCodeForTimezone(org)) {
        return `Timezone unknown. Set ${getMissingTimezoneCodeLabel(org)} code`;
    }
    return 'Timezone unknown';
}

function renderOptionalReceiptFieldLabel(label: string, showWarning: boolean): ReactElement {
    return (
        <span className="inline-flex items-center gap-1">
            <span>{label}</span>
            {showWarning ? (
                <span title={RECEIPT_FIELD_WARNING_TOOLTIP} aria-label={RECEIPT_FIELD_WARNING_TOOLTIP}>
                    ⚠️
                </span>
            ) : null}
        </span>
    );
}

function serializeDocumentTemplateSelectorJson(
    selectorJson: DocumentTemplateSelector['selector_json']
): string {
    return JSON.stringify(selectorJson ?? null);
}

export function buildDocumentTemplateSelectorKey(
    selector: DocumentTemplateSelector
): string {
    return [
        selector.template_kind,
        selector.template_output_mime,
        selector.payload_type,
        selector.payload_discriminator,
        String(selector.payload_type_version),
        serializeDocumentTemplateSelectorJson(selector.selector_json),
    ].join(':');
}

export function buildDocumentTemplateEditorState(
    registryEntry: OrganizationDocumentTemplateRegistryEntry,
    override: OrganizationDocumentTemplateOverrideDescriptor | null
): DocumentTemplateEditorState {
    return {
        selector: registryEntry.selector,
        overrideUuid: override?.document_template_uuid ?? null,
        defaultTemplateSource: registryEntry.default_template_source,
        allowedRootVariables: registryEntry.allowed_root_variables,
        name: override?.name ?? registryEntry.name,
        description: override?.description ?? registryEntry.description ?? '',
        templateSource: override?.template_source ?? registryEntry.default_template_source,
        isActive: (override?.status ?? Status.ACTIVE) === Status.ACTIVE,
    };
}

export function formatDocumentTemplateSelectorSummary(
    selector: DocumentTemplateSelector
): string {
    return [
        selector.template_kind,
        selector.template_output_mime,
        `${selector.payload_type}:${selector.payload_discriminator}:v${selector.payload_type_version}`,
    ].join(' · ');
}

function buildDealertrackReconciliationEditorState(
    value: DealertrackReconciliationEditorState | undefined
): DealertrackReconciliationEditorState {
    if (value !== undefined) {
        return value;
    }
    return IpwConfigDealertrackSchema.parse(
        defaultIpwConfigByKey.DEALERTRACK
    ).reconciliation;
}

function getOrganizationDealertrackReconciliation(
    organization: GetOrganizationResponse | OrganizationListItem
): DealertrackReconciliationEditorState | undefined {
    const customization = organization.ipwConfigCustomizations?.DEALERTRACK;
    if (customization?.ipw_system_id !== DealertrackIpwSystemIdKey) {
        return undefined;
    }
    return customization.reconciliation;
}

function parseUrlPatternsText(value: string): string[] {
    return value
        .split(/[\n,]/)
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0);
}

function buildUrlPatternsText(urlPatterns: readonly string[]): string {
    return urlPatterns.join('\n');
}

function buildMerchantConfigValidationIssue(input: {
    readonly fieldLabel: string;
    readonly reason: string;
    readonly anchor: string;
}): MerchantConfigValidationIssue {
    return {
        fieldLabel: input.fieldLabel,
        reason: input.reason,
        anchor: input.anchor,
    };
}

const URL_PATTERN_HELP_TEXT = 'Use URLPattern syntax such as https://*.example.com/*.';

function buildInvalidUrlPatternMessage(invalidPattern: string): string {
    return `URL pattern "${invalidPattern}" is invalid. ${URL_PATTERN_HELP_TEXT}`;
}

function getUrlPatternTextValidationIssue(
    value: string,
    fieldLabel: string,
    anchor: string
): MerchantConfigValidationIssue | null {
    const urlPatterns = parseUrlPatternsText(value);
    if (urlPatterns.length === 0) {
        return buildMerchantConfigValidationIssue({
            fieldLabel,
            reason: `At least one URL pattern is required. ${URL_PATTERN_HELP_TEXT}`,
            anchor,
        });
    }
    const invalidPattern = urlPatterns.find(
        (urlPattern) => !isValidUrlPatternString(urlPattern)
    );
    if (invalidPattern === undefined) {
        return null;
    }
    return buildMerchantConfigValidationIssue({
        fieldLabel,
        reason: buildInvalidUrlPatternMessage(invalidPattern),
        anchor,
    });
}

function getUrlPatternTextValidationMessage(value: string): string | null {
    return getUrlPatternTextValidationIssue(value, 'URL patterns', 'url_patterns')
        ?.reason ?? null;
}

function getRequiredUrlPatternValidationIssue(
    value: string,
    fieldLabel: string,
    anchor: string
): MerchantConfigValidationIssue | null {
    const trimmedValue = value.trim();
    if (trimmedValue.length === 0) {
        return buildMerchantConfigValidationIssue({
            fieldLabel,
            reason: `URL pattern is required. ${URL_PATTERN_HELP_TEXT}`,
            anchor,
        });
    }
    if (isValidUrlPatternString(trimmedValue)) {
        return null;
    }
    return buildMerchantConfigValidationIssue({
        fieldLabel,
        reason: buildInvalidUrlPatternMessage(trimmedValue),
        anchor,
    });
}

function getOptionalUrlPatternTextValidationMessage(value: string): string | null {
    return getRequiredUrlPatternValidationIssue(value, 'URL pattern', 'url_pattern')
        ?.reason ?? null;
}

function buildUrlPatternsRuleGroupEditorState(
    urlPatternsRuleGroup?: UrlPatternsRule
): UrlPatternsRuleGroupEditorState {
    return {
        action: urlPatternsRuleGroup?.action ?? UrlPatternsRuleAction.ALLOW,
        urlPatternsText: buildUrlPatternsText(urlPatternsRuleGroup?.urlPatterns ?? []),
    };
}

function buildAllowedWebsiteCustomizationEditorState(
    website: Pick<
        AllowedWebsiteBase,
        | 'allowedUrlPatterns'
        | 'sessionPersistence'
        | 'promoteSessionCookies'
        | 'urlPatternsRules'
        | 'fetchDefaultAction'
        | 'iframeUrlPatterns'
        | 'iframeDefaultAction'
    >
): AllowedWebsiteCustomizationEditorState {
    return {
        allowedUrlPatternsText: buildUrlPatternsText(website.allowedUrlPatterns),
        sessionPersistence: website.sessionPersistence,
        promoteSessionCookies: website.promoteSessionCookies ?? false,
        urlPatternsRules:
            website.urlPatternsRules?.map(buildUrlPatternsRuleGroupEditorState) ?? [],
        fetchDefaultAction: website.fetchDefaultAction,
        iframeUrlPatterns:
            website.iframeUrlPatterns?.map(buildUrlPatternsRuleGroupEditorState) ?? [],
        iframeDefaultAction: website.iframeDefaultAction ?? UrlPatternsRuleAction.ALLOW,
    };
}

function buildAllowedWebsiteCustomizationPayloadOrThrow(
    editorState: AllowedWebsiteCustomizationEditorState
): AllowedWebsiteCustomization {
    const allowedUrlPatternValidationMessage = getUrlPatternTextValidationMessage(
        editorState.allowedUrlPatternsText
    );
    if (allowedUrlPatternValidationMessage !== null) {
        throw new Error(allowedUrlPatternValidationMessage);
    }

    return {
        allowedUrlPatterns: parseUrlPatternsText(editorState.allowedUrlPatternsText),
        sessionPersistence: editorState.sessionPersistence,
        promoteSessionCookies: editorState.promoteSessionCookies,
        urlPatternsRules: editorState.urlPatternsRules.map((urlPatternsRuleGroup) => {
            const validationMessage = getUrlPatternTextValidationMessage(
                urlPatternsRuleGroup.urlPatternsText
            );
            if (validationMessage !== null) {
                throw new Error(validationMessage);
            }
            return {
                action: urlPatternsRuleGroup.action,
                urlPatterns: parseUrlPatternsText(urlPatternsRuleGroup.urlPatternsText),
            };
        }),
        fetchDefaultAction: editorState.fetchDefaultAction,
        iframeUrlPatterns: editorState.iframeUrlPatterns.map((iframeUrlPatternGroup) => {
            const validationMessage = getUrlPatternTextValidationMessage(
                iframeUrlPatternGroup.urlPatternsText
            );
            if (validationMessage !== null) {
                throw new Error(validationMessage);
            }
            return {
                action: iframeUrlPatternGroup.action,
                urlPatterns: parseUrlPatternsText(iframeUrlPatternGroup.urlPatternsText),
            };
        }),
        iframeDefaultAction: editorState.iframeDefaultAction,
    };
}

function getAllowedWebsiteCustomizationEditorValidationMessage(
    editorState: AllowedWebsiteCustomizationEditorState
): string | null {
    return getAllowedWebsiteCustomizationEditorValidationIssues(editorState, {
        fieldLabelPrefix: 'Allowed website',
        idPrefix: 'allowed_website',
    })[0]?.reason ?? null;
}

function getAllowedWebsiteCustomizationEditorValidationIssues(
    editorState: AllowedWebsiteCustomizationEditorState,
    input: {
        readonly fieldLabelPrefix: string;
        readonly idPrefix: string;
    }
): MerchantConfigValidationIssue[] {
    const validationIssues: MerchantConfigValidationIssue[] = [];
    const allowedUrlPatternValidationIssue = getUrlPatternTextValidationIssue(
        editorState.allowedUrlPatternsText,
        `${input.fieldLabelPrefix} allowed URL patterns`,
        `${input.idPrefix}_allowed_url_patterns`
    );
    if (allowedUrlPatternValidationIssue !== null) {
        validationIssues.push(allowedUrlPatternValidationIssue);
    }
    for (const [ruleGroupIndex, urlPatternsRuleGroup] of editorState.urlPatternsRules.entries()) {
        const validationIssue = getUrlPatternTextValidationIssue(
            urlPatternsRuleGroup.urlPatternsText,
            `${input.fieldLabelPrefix} fetch rule ${ruleGroupIndex + 1} URL patterns`,
            `${input.idPrefix}_fetch_rule_${ruleGroupIndex}_patterns`
        );
        if (validationIssue !== null) {
            validationIssues.push(validationIssue);
        }
    }
    for (const [ruleGroupIndex, iframeUrlPatternGroup] of editorState.iframeUrlPatterns.entries()) {
        const validationIssue = getUrlPatternTextValidationIssue(
            iframeUrlPatternGroup.urlPatternsText,
            `${input.fieldLabelPrefix} iframe rule ${ruleGroupIndex + 1} URL patterns`,
            `${input.idPrefix}_iframe_rule_${ruleGroupIndex}_patterns`
        );
        if (validationIssue !== null) {
            validationIssues.push(validationIssue);
        }
    }
    return validationIssues;
}

function getIpwSystemIdKeyOrThrow(systemId: IpwSystemId): IpwSystemIdKey {
    const matchedKey = IpwSystemIdKeyList.find(
        (ipwSystemIdKey) => IpwSystemId[ipwSystemIdKey] === systemId
    );
    if (matchedKey === undefined) {
        throw new Error(`Unknown IPW system id: ${systemId}`);
    }
    return matchedKey;
}

function hasEnabledDealertrackSystem(
    enabledIpwSystemIds: readonly IpwSystemId[]
): boolean {
    for (const ipwSystemId of enabledIpwSystemIds) {
        const ipwSystemIdKey = getIpwSystemIdKeyOrThrow(ipwSystemId);
        switch (ipwSystemIdKey) {
            case IpwSystemIdKey.DEALERTRACK:
                return true;
            case IpwSystemIdKey.PCPARTPICKER:
                break;
            default: {
                const exhaustiveIpwSystemIdKey: never = ipwSystemIdKey;
                throw new Error(
                    `Unhandled IPW system id key: ${exhaustiveIpwSystemIdKey}`
                );
            }
        }
    }
    return false;
}

function getIpgSystemIdKeyOrThrow(systemId: IpgSystemId): IpgSystemIdKey {
    const matchedKey = IpgSystemIdKeyList.find(
        (ipgSystemIdKey) => IpgSystemId[ipgSystemIdKey] === systemId
    );
    if (matchedKey === undefined) {
        throw new Error(`Unknown IPG system id: ${systemId}`);
    }
    return matchedKey;
}

function cloneIpwConfig(config: IpwConfig): IpwConfig {
    return {
        ...config,
        allowedWebsites: config.allowedWebsites.map((allowedWebsite) => ({
            ...allowedWebsite,
            allowedUrlPatterns: [...allowedWebsite.allowedUrlPatterns],
            urlPatternsRules: allowedWebsite.urlPatternsRules.map((fetchPolicy) => ({
                action: fetchPolicy.action,
                urlPatterns: [...fetchPolicy.urlPatterns],
            })),
            iframeUrlPatterns: allowedWebsite.iframeUrlPatterns.map((iframePolicy) => ({
                action: iframePolicy.action,
                urlPatterns: [...iframePolicy.urlPatterns],
            })),
        })),
        rpaModules: [...config.rpaModules],
    };
}

function getEffectiveIpwConfig(
    merchantConfig: MerchantConfig | undefined,
    ipwSystemIdKey: IpwSystemIdKey
): IpwConfig {
    const persistedIpwConfig = merchantConfig?.ipw[ipwSystemIdKey];
    if (persistedIpwConfig !== undefined) {
        return cloneIpwConfig(persistedIpwConfig);
    }
    return cloneIpwConfig(defaultIpwConfigByKey[ipwSystemIdKey]);
}

function buildIpwConfigCustomizationEditorState(
    merchantConfig: MerchantConfig | undefined,
    ipwSystemIdKey: IpwSystemIdKey
): IpwConfigCustomizationEditorState {
    const effectiveIpwConfig = getEffectiveIpwConfig(merchantConfig, ipwSystemIdKey);
    return {
        urlPattern: effectiveIpwConfig.urlPattern,
        allowedWebsites: effectiveIpwConfig.allowedWebsites.map(
            buildAllowedWebsiteCustomizationEditorState
        ),
        ...(ipwSystemIdKey === DealertrackIpwSystemIdKey && {
            reconciliation: IpwConfigDealertrackSchema.parse(effectiveIpwConfig).reconciliation,
        }),
    };
}

function buildIpwConfigCustomizationEditorStateMap(
    merchantConfig: MerchantConfig | undefined
): IpwConfigCustomizationEditorStateMap {
    const editorStateMap: IpwConfigCustomizationEditorStateMap = {};
    for (const ipwSystemIdKey of IpwSystemIdKeyList) {
        editorStateMap[ipwSystemIdKey] = buildIpwConfigCustomizationEditorState(
            merchantConfig,
            ipwSystemIdKey
        );
    }
    return editorStateMap;
}

function buildIpwConfigCustomizationPayloadOrThrow(
    ipwSystemIdKey: IpwSystemIdKey,
    editorState: IpwConfigCustomizationEditorState
): IpwConfigCustomization {
    const urlPatternValidationMessage = getOptionalUrlPatternTextValidationMessage(
        editorState.urlPattern
    );
    if (urlPatternValidationMessage !== null) {
        throw new Error(urlPatternValidationMessage);
    }
    return {
        ipw_system_id: ipwSystemIdKey,
        urlPattern: editorState.urlPattern.trim(),
        allowedWebsites: editorState.allowedWebsites.map(
            buildAllowedWebsiteCustomizationPayloadOrThrow
        ),
        ...(editorState.reconciliation !== undefined && {
            reconciliation: editorState.reconciliation,
        }),
    };
}

function buildIpwConfigCustomizationPayloadMapOrThrow(
    enabledIpwSystemIds: readonly IpwSystemId[],
    editorStateMap: IpwConfigCustomizationEditorStateMap
): Partial<Record<IpwSystemIdKey, IpwConfigCustomization>> | undefined {
    const entries = enabledIpwSystemIds.map((ipwSystemId) => {
        const ipwSystemIdKey = getIpwSystemIdKeyOrThrow(ipwSystemId);
        const editorState = editorStateMap[ipwSystemIdKey];
        if (editorState === undefined) {
            throw new Error(`Missing ${ipwSystemIdKey} configuration editor state`);
        }
        return [
            ipwSystemIdKey,
            buildIpwConfigCustomizationPayloadOrThrow(ipwSystemIdKey, editorState),
        ] as const;
    });
    return entries.length === 0 ? undefined : Object.fromEntries(entries);
}

export function getDealertrackReconciliationValidationIssues(
    enabledIpwSystemIds: readonly IpwSystemId[],
    reconciliation: DealertrackReconciliationEditorState
): MerchantConfigValidationIssue[] {
    if (!hasEnabledDealertrackSystem(enabledIpwSystemIds)) {
        return [];
    }
    if (reconciliation.paymentCode.length === 0) {
        return [
            buildMerchantConfigValidationIssue({
                fieldLabel: DEALERTRACK_PAYMENT_CODE_FIELD_LABEL,
                reason: 'Payment code is required.',
                anchor: DEALERTRACK_PAYMENT_CODE_FIELD_ANCHOR,
            }),
        ];
    }
    if (reconciliation.paymentCode.length > DEALERTRACK_PAYMENT_CODE_MAX_LENGTH) {
        return [
            buildMerchantConfigValidationIssue({
                fieldLabel: DEALERTRACK_PAYMENT_CODE_FIELD_LABEL,
                reason: `Payment code must be ${DEALERTRACK_PAYMENT_CODE_MAX_LENGTH} characters or fewer.`,
                anchor: DEALERTRACK_PAYMENT_CODE_FIELD_ANCHOR,
            }),
        ];
    }
    return [];
}

export function getIpwConfigCustomizationEditorValidationIssues(
    enabledIpwSystemIds: readonly IpwSystemId[],
    editorStateMap: IpwConfigCustomizationEditorStateMap
): MerchantConfigValidationIssue[] {
    const validationIssues: MerchantConfigValidationIssue[] = [];
    for (const ipwSystemId of enabledIpwSystemIds) {
        const ipwSystemIdKey = getIpwSystemIdKeyOrThrow(ipwSystemId);
        const editorState = editorStateMap[ipwSystemIdKey];
        const systemLabel = defaultIpwConfigByKey[ipwSystemIdKey].name;
        if (editorState === undefined) {
            validationIssues.push(
                buildMerchantConfigValidationIssue({
                    fieldLabel: `${systemLabel} configuration`,
                    reason: `Missing ${ipwSystemIdKey} configuration editor state.`,
                    anchor: 'enable_ipw_system_ids_config',
                })
            );
            continue;
        }
        const urlPatternValidationIssue = getRequiredUrlPatternValidationIssue(
            editorState.urlPattern,
            `${systemLabel} URL pattern`,
            `ipw_${ipwSystemIdKey}_url_pattern`
        );
        if (urlPatternValidationIssue !== null) {
            validationIssues.push(urlPatternValidationIssue);
        }
        for (const [websiteIndex, allowedWebsite] of editorState.allowedWebsites.entries()) {
            validationIssues.push(
                ...getAllowedWebsiteCustomizationEditorValidationIssues(allowedWebsite, {
                    fieldLabelPrefix: `${systemLabel} allowed website ${websiteIndex + 1}`,
                    idPrefix: `ipw_${ipwSystemIdKey}_allowed_website_${websiteIndex}`,
                })
            );
        }
    }
    return validationIssues;
}

function buildDealertrackAutomaticCustomerDetailsDetectionEnabledEditorState(
    value: boolean | undefined
): boolean {
    return value ?? true;
}

function buildDealertrackCustomerDetailsDetectionButtonEnabledEditorState(
    value: boolean | undefined
): boolean {
    return value ?? false;
}

function buildAddressRequiredForCardPaymentEditorState(
    value: boolean | undefined
): boolean {
    return value ?? false;
}

function buildRequire2FAEditorState(value: boolean | undefined): boolean {
    return value ?? false;
}

const UsBusinessDetailsEditorSubmissionSchema = z.object({
    country: z.literal('US'),
    legalEntityName: z.string().trim().min(1, 'Legal entity name is required'),
    dba: z.string(),
    department: z.string(),
    mainPhoneNumber: z
        .string()
        .refine(
            (value) =>
                normalizePhoneNumber(value).length === 0 || isNormalizedPhoneNumberValid(value),
            'Main phone number must be 10 digits'
        ),
    street: z.string(),
    street2: z.string(),
    city: z.string(),
    usState: z.union([z.enum(USStateKeyList), z.literal('')]),
    usZip: z.string(),
});

const CaBusinessDetailsEditorSubmissionSchema = z.object({
    country: z.literal('CA'),
    legalEntityName: z.string().trim().min(1, 'Legal entity name is required'),
    dba: z.string(),
    department: z.string(),
    mainPhoneNumber: z
        .string()
        .refine(
            (value) =>
                normalizePhoneNumber(value).length === 0 || isNormalizedPhoneNumberValid(value),
            'Main phone number must be 10 digits'
        ),
    street: z.string(),
    street2: z.string(),
    city: z.string(),
    caProvince: z.union([z.enum(CanadaProvinceKeyList), z.literal('')]),
    caPostalCode: z.string(),
});

const BusinessDetailsEditorSubmissionSchema = z.discriminatedUnion('country', [
    UsBusinessDetailsEditorSubmissionSchema,
    CaBusinessDetailsEditorSubmissionSchema,
]);

function buildBusinessDetailsEditorState(
    businessDetails: OrganizationListItem['businessDetails'] | undefined
): BusinessDetailsEditorState {
    if (businessDetails === undefined) {
        return defaultBusinessDetailsEditorState;
    }
    if (businessDetails.country === 'US') {
        return {
            country: 'US',
            legalEntityName: businessDetails.legalEntityName,
            dba: businessDetails.dba ?? '',
            department: businessDetails.department ?? '',
            mainPhoneNumber: formatPhoneNumber(businessDetails.main_phone_number ?? ''),
            street: businessDetails.address?.street ?? '',
            street2: businessDetails.address?.street2 ?? '',
            city: businessDetails.address?.city ?? '',
            usState: businessDetails.address?.state ?? defaultBusinessDetailsEditorState.usState,
            usZip: businessDetails.address?.zip ?? '',
            caProvince: defaultBusinessDetailsEditorState.caProvince,
            caPostalCode: defaultBusinessDetailsEditorState.caPostalCode,
        };
    }
    return {
        country: 'CA',
        legalEntityName: businessDetails.legalEntityName,
        dba: businessDetails.dba ?? '',
        department: businessDetails.department ?? '',
        mainPhoneNumber: formatPhoneNumber(businessDetails.main_phone_number ?? ''),
        street: businessDetails.address?.street ?? '',
        street2: businessDetails.address?.street2 ?? '',
        city: businessDetails.address?.city ?? '',
        usState: defaultBusinessDetailsEditorState.usState,
        usZip: defaultBusinessDetailsEditorState.usZip,
        caProvince: businessDetails.address?.province ?? defaultBusinessDetailsEditorState.caProvince,
        caPostalCode: businessDetails.address?.postal_code ?? '',
    };
}

function buildBusinessDetailsPayloadOrThrow(
    editorState: BusinessDetailsEditorState
): BusinessDetails {
    const parseResult = BusinessDetailsEditorSubmissionSchema.safeParse(editorState);
    if (!parseResult.success) {
        throw new Error(parseResult.error.message);
    }
    const submissionData = parseResult.data;
    const normalizedDba: string | undefined =
        submissionData.dba.trim().length === 0 ? undefined : submissionData.dba.trim();
    const normalizedDepartment: string | undefined =
        submissionData.department.trim().length === 0 ? undefined : submissionData.department.trim();
    const normalizedMainPhoneNumberValue: string = normalizePhoneNumber(submissionData.mainPhoneNumber);
    const normalizedMainPhoneNumber: string | undefined =
        normalizedMainPhoneNumberValue.length === 0
            ? undefined
            : normalizedMainPhoneNumberValue;
    const normalizedStreet: string | undefined =
        submissionData.street.trim().length === 0 ? undefined : submissionData.street.trim();
    const normalizedStreet2: string | undefined =
        submissionData.street2.trim().length === 0 ? undefined : submissionData.street2.trim();
    const normalizedCity: string | undefined =
        submissionData.city.trim().length === 0 ? undefined : submissionData.city.trim();
    if (submissionData.country === 'US') {
        const normalizedUsZipValue: string = normalizeUSZipCode(submissionData.usZip);
        const normalizedUsZip: string | undefined =
            normalizedUsZipValue.length === 0 ? undefined : normalizedUsZipValue;
        const normalizedUsState: USState | undefined =
            submissionData.usState === '' ? undefined : submissionData.usState;
        const usAddressValues: {
            readonly street?: string;
            readonly street2?: string;
            readonly city?: string;
            readonly state?: USState;
            readonly zip?: string;
        } = {
            ...(normalizedStreet !== undefined ? { street: normalizedStreet } : {}),
            ...(normalizedStreet2 !== undefined ? { street2: normalizedStreet2 } : {}),
            ...(normalizedCity !== undefined ? { city: normalizedCity } : {}),
            ...(normalizedUsState !== undefined ? { state: normalizedUsState } : {}),
            ...(normalizedUsZip !== undefined ? { zip: normalizedUsZip } : {}),
        };
        const usAddressPayload:
            | ({
                  readonly country: 'US';
              } & typeof usAddressValues)
            | undefined =
            Object.keys(usAddressValues).length === 0
                ? undefined
                : { country: 'US', ...usAddressValues };
        return BusinessDetailsSchema.parse({
            country: 'US',
            legalEntityName: submissionData.legalEntityName,
            ...(normalizedDba !== undefined ? { dba: normalizedDba } : {}),
            ...(normalizedDepartment !== undefined ? { department: normalizedDepartment } : {}),
            ...(normalizedMainPhoneNumber !== undefined
                ? { main_phone_number: normalizedMainPhoneNumber }
                : {}),
            ...(usAddressPayload !== undefined ? { address: usAddressPayload } : {}),
        });
    }
    const normalizedCaProvince: CanadaProvince | undefined =
        submissionData.caProvince === '' ? undefined : submissionData.caProvince;
    const normalizedCaPostalCodeValue: string = normalizeCanadaPostalCode(submissionData.caPostalCode);
    const normalizedCaPostalCode: string | undefined =
        normalizedCaPostalCodeValue.length === 0
            ? undefined
            : normalizedCaPostalCodeValue;
    const caAddressValues: {
        readonly street?: string;
        readonly street2?: string;
        readonly city?: string;
        readonly province?: CanadaProvince;
        readonly postal_code?: string;
    } = {
        ...(normalizedStreet !== undefined ? { street: normalizedStreet } : {}),
        ...(normalizedStreet2 !== undefined ? { street2: normalizedStreet2 } : {}),
        ...(normalizedCity !== undefined ? { city: normalizedCity } : {}),
        ...(normalizedCaProvince !== undefined ? { province: normalizedCaProvince } : {}),
        ...(normalizedCaPostalCode !== undefined ? { postal_code: normalizedCaPostalCode } : {}),
    };
    const caAddressPayload:
        | ({
              readonly country: 'CA';
          } & typeof caAddressValues)
        | undefined =
        Object.keys(caAddressValues).length === 0
            ? undefined
            : { country: 'CA', ...caAddressValues };
    return BusinessDetailsSchema.parse({
        country: 'CA',
        legalEntityName: submissionData.legalEntityName,
        ...(normalizedDba !== undefined ? { dba: normalizedDba } : {}),
        ...(normalizedDepartment !== undefined ? { department: normalizedDepartment } : {}),
        ...(normalizedMainPhoneNumber !== undefined ? { main_phone_number: normalizedMainPhoneNumber } : {}),
        ...(caAddressPayload !== undefined ? { address: caAddressPayload } : {}),
    });
}

export function buildCustomerPortalPaymentRequestsEditorState(
    config: OrganizationListItem['customerPortalPaymentRequests'] | undefined,
    businessDetails?: BusinessDetails | null
): CustomerPortalPaymentRequestsEditorState {
    const twilioConfig = config?.twilioSms;
    const sendgridConfig = config?.sendgridEmail;
    const defaultSendgridFromName: string =
        businessDetails?.dba?.trim() ??
        defaultCustomerPortalPaymentRequestsEditorState.sendgridFromName;

    return {
        enabled: config?.enabled ?? defaultCustomerPortalPaymentRequestsEditorState.enabled,
        allowEmail:
            config?.allowedDeliveryChannels.includes(CustomerPortalDeliveryChannel.EMAIL) ??
            defaultCustomerPortalPaymentRequestsEditorState.allowEmail,
        allowSms:
            twilioConfig === undefined
                ? defaultCustomerPortalPaymentRequestsEditorState.allowSms
                : config?.allowedDeliveryChannels.includes(CustomerPortalDeliveryChannel.SMS) ??
                  defaultCustomerPortalPaymentRequestsEditorState.allowSms,
        templateSelections:
            config?.templateSelections ??
            defaultCustomerPortalPaymentRequestsEditorState.templateSelections,
        documentPolicy: config?.documentPolicy,
        twilioEnabled:
            twilioConfig?.enabled ?? defaultCustomerPortalPaymentRequestsEditorState.twilioEnabled,
        twilioSenderMode:
            twilioConfig !== undefined && 'fromPhoneNumber' in twilioConfig
                ? 'from_phone_number'
                : twilioConfig !== undefined && 'messagingServiceSid' in twilioConfig
                  ? 'messaging_service_sid'
                : defaultCustomerPortalPaymentRequestsEditorState.twilioSenderMode,
        twilioFromPhoneNumber:
            twilioConfig !== undefined && 'fromPhoneNumber' in twilioConfig
                ? formatPhoneNumber(twilioConfig.fromPhoneNumber ?? '')
                : defaultCustomerPortalPaymentRequestsEditorState.twilioFromPhoneNumber,
        twilioMessagingServiceSid:
            twilioConfig !== undefined && 'messagingServiceSid' in twilioConfig
                ? (twilioConfig.messagingServiceSid ?? '')
                : defaultCustomerPortalPaymentRequestsEditorState.twilioMessagingServiceSid,
        sendgridEnabled:
            sendgridConfig?.enabled ??
            defaultCustomerPortalPaymentRequestsEditorState.sendgridEnabled,
        sendgridFromEmail:
            sendgridConfig?.fromEmail ??
            defaultCustomerPortalPaymentRequestsEditorState.sendgridFromEmail,
        sendgridFromName:
            sendgridConfig?.fromName ??
            defaultSendgridFromName,
        sendgridReplyToEmail:
            sendgridConfig?.replyToEmail ??
            defaultCustomerPortalPaymentRequestsEditorState.sendgridReplyToEmail,
    };
}

export function isTwilioProviderConfigurationComplete(
    editorState: CustomerPortalPaymentRequestsEditorState
): boolean {
    if (editorState.twilioSenderMode === 'from_phone_number') {
        return (
            editorState.twilioFromPhoneNumber.trim().length > 0
            && normalizeNorthAmericanPhoneNumberToE164(
                editorState.twilioFromPhoneNumber.trim()
            ) !== undefined
        );
    }
    return editorState.twilioMessagingServiceSid.trim().length > 0;
}

export function getCustomerPortalPaymentRequestsEditorAvailability(
    editorState: CustomerPortalPaymentRequestsEditorState
): CustomerPortalPaymentRequestsEditorAvailability {
    const canEnableTwilioProvider: boolean =
        isTwilioProviderConfigurationComplete(editorState);
    const canAllowSmsDelivery: boolean =
        canEnableTwilioProvider && editorState.twilioEnabled;
    return {
        canEnableTwilioProvider,
        canAllowSmsDelivery,
    };
}

export function getTwilioProviderEnablementMessage(
    editorState: CustomerPortalPaymentRequestsEditorState
): string | null {
    if (getCustomerPortalPaymentRequestsEditorAvailability(editorState).canEnableTwilioProvider) {
        return null;
    }
    if (editorState.twilioSenderMode === 'from_phone_number') {
        return 'Twilio cannot be enabled until a valid from phone number is configured.';
    }
    return 'Twilio cannot be enabled until a messaging service SID is configured.';
}

export function getSmsDeliveryEnablementMessage(
    editorState: CustomerPortalPaymentRequestsEditorState
): string | null {
    if (getCustomerPortalPaymentRequestsEditorAvailability(editorState).canAllowSmsDelivery) {
        return null;
    }
    if (!getCustomerPortalPaymentRequestsEditorAvailability(editorState).canEnableTwilioProvider) {
        return 'SMS delivery cannot be enabled until the Twilio provider is fully configured.';
    }
    return 'SMS delivery cannot be enabled until the Twilio provider is enabled.';
}

export function normalizeCustomerPortalPaymentRequestsEditorState(
    editorState: CustomerPortalPaymentRequestsEditorState
): CustomerPortalPaymentRequestsEditorState {
    const availability = getCustomerPortalPaymentRequestsEditorAvailability(editorState);
    return {
        ...editorState,
        twilioEnabled:
            availability.canEnableTwilioProvider && editorState.twilioEnabled,
        allowSms: availability.canAllowSmsDelivery && editorState.allowSms,
    };
}

export function buildMerchantPaymentMethodsEditorState(
    config: OrganizationListItem['merchantPaymentMethods'] | undefined
): MerchantPaymentMethodsEditorState {
    return {
        terminal: config?.terminal ?? defaultMerchantPaymentMethodsEditorState.terminal,
        card: config?.card ?? defaultMerchantPaymentMethodsEditorState.card,
        ach: config?.ach ?? defaultMerchantPaymentMethodsEditorState.ach,
        e_invoice: config?.e_invoice ?? defaultMerchantPaymentMethodsEditorState.e_invoice,
    };
}

function normalizePaymentRequestEmailCcValue(value: string): string {
    return value.trim().toLowerCase();
}

export function buildPaymentRequestEmailCcEditorState(
    emails: readonly string[] | undefined
): PaymentRequestEmailCcEditorState {
    if (emails === undefined) {
        return [...defaultPaymentRequestEmailCcEditorState];
    }
    const normalizedEmails: string[] = [];
    const seenNormalizedEmails = new Set<string>();
    for (const email of emails) {
        const normalizedEmail = normalizePaymentRequestEmailCcValue(email);
        if (normalizedEmail.length === 0 || seenNormalizedEmails.has(normalizedEmail)) {
            continue;
        }
        seenNormalizedEmails.add(normalizedEmail);
        normalizedEmails.push(normalizedEmail);
    }
    return normalizedEmails;
}

function cloneValorConfig(config: ValorIpgConfig): ValorIpgConfig {
    return {
        ...config,
        allowedWebsites: config.allowedWebsites.map((allowedWebsite) => ({
            ...allowedWebsite,
            allowedUrlPatterns: [...allowedWebsite.allowedUrlPatterns],
            urlPatternsRules: allowedWebsite.urlPatternsRules.map((fetchPolicy) => ({
                action: fetchPolicy.action,
                urlPatterns: [...fetchPolicy.urlPatterns],
            })),
            iframeUrlPatterns: allowedWebsite.iframeUrlPatterns.map((iframePolicy) => ({
                action: iframePolicy.action,
                urlPatterns: [...iframePolicy.urlPatterns],
            })),
        })),
    };
}

function cloneIpgConfig(config: IpgConfig): IpgConfig {
    return {
        ...config,
        allowedWebsites: config.allowedWebsites.map((allowedWebsite) => ({
            ...allowedWebsite,
            allowedUrlPatterns: [...allowedWebsite.allowedUrlPatterns],
            urlPatternsRules: allowedWebsite.urlPatternsRules.map((fetchPolicy) => ({
                action: fetchPolicy.action,
                urlPatterns: [...fetchPolicy.urlPatterns],
            })),
            iframeUrlPatterns: allowedWebsite.iframeUrlPatterns.map((iframePolicy) => ({
                action: iframePolicy.action,
                urlPatterns: [...iframePolicy.urlPatterns],
            })),
        })),
        rpaModules: [...config.rpaModules],
    };
}

function getValorPortalUrl(config: ValorIpgConfig): string {
    return config.allowedWebsites[0]?.url ?? '';
}

function getValorDefaultApiUrl(config: ValorIpgConfig): string {
    return config.default_api_url ?? '';
}

function getDefaultValorConfig(): ValorIpgConfig {
    return cloneValorConfig(ipgConfigValorProd);
}

function getEffectiveValorConfig(merchantConfig: MerchantConfig | undefined): ValorIpgConfig {
    const persistedValorConfig = merchantConfig?.ipg[ValorIpgSystemIdKey];
    if (persistedValorConfig?.ipg_id === ValorIpgSystemId) {
        return cloneValorConfig(persistedValorConfig);
    }
    return getDefaultValorConfig();
}

function getEffectiveIpgConfig(
    merchantConfig: MerchantConfig | undefined,
    ipgSystemIdKey: IpgSystemIdKey
): IpgConfig {
    const persistedIpgConfig = merchantConfig?.ipg[ipgSystemIdKey];
    if (persistedIpgConfig !== undefined) {
        return cloneIpgConfig(persistedIpgConfig);
    }
    return cloneIpgConfig(defaultIpgConfigByKey[ipgSystemIdKey]);
}

function deriveValorPortalModeFromConfig(
    merchantConfig: MerchantConfig | undefined
): ValorPortalMode {
    const effectiveValorConfig = getEffectiveValorConfig(merchantConfig);
    const effectivePortalUrl = getValorPortalUrl(effectiveValorConfig);
    const effectiveDefaultApiUrl = getValorDefaultApiUrl(effectiveValorConfig);
    const demoPortalUrl = getValorPortalUrl(ipgConfigValorDemo);
    const demoDefaultApiUrl = getValorDefaultApiUrl(ipgConfigValorDemo);
    const prodPortalUrl = getValorPortalUrl(ipgConfigValorProd);
    const prodDefaultApiUrl = getValorDefaultApiUrl(ipgConfigValorProd);

    if (effectivePortalUrl === demoPortalUrl && effectiveDefaultApiUrl === demoDefaultApiUrl) {
        return 'DEMO';
    }
    if (effectivePortalUrl === prodPortalUrl && effectiveDefaultApiUrl === prodDefaultApiUrl) {
        return 'PRODUCTION';
    }
    return 'CUSTOM';
}

function buildValorMerchantConfigEditorState(
    merchantConfig: MerchantConfig | undefined
): ValorMerchantConfigEditorState {
    const effectiveValorConfig = getEffectiveValorConfig(merchantConfig);
    return {
        portalMode: deriveValorPortalModeFromConfig(merchantConfig),
        portalUrl: getValorPortalUrl(effectiveValorConfig),
        defaultApiUrl: getValorDefaultApiUrl(effectiveValorConfig),
        defaultChannel: effectiveValorConfig.default_channel ?? '',
    };
}

function formatValorPortalModeLabel(portalMode: ValorPortalMode): string {
    if (portalMode === 'PRODUCTION') {
        return 'Production';
    }
    if (portalMode === 'DEMO') {
        return 'Demo';
    }
    return 'Custom';
}

function getManagedValorConfigForPortalMode(
    portalMode: ValorPortalMode
): ValorIpgConfig | null {
    if (portalMode === 'PRODUCTION') {
        return ipgConfigValorProd;
    }
    if (portalMode === 'DEMO') {
        return ipgConfigValorDemo;
    }
    return null;
}

function buildIpgConfigCustomizationEditorStateFromConfig(
    ipgConfig: IpgConfig
): IpgConfigCustomizationEditorState {
    return {
        urlPattern: ipgConfig.urlPattern,
        allowedWebsites: ipgConfig.allowedWebsites.map(
            buildAllowedWebsiteCustomizationEditorState
        ),
        defaultApiUrl: ipgConfig.default_api_url ?? '',
        defaultChannel: ipgConfig.default_channel ?? '',
    };
}

function buildIpgConfigCustomizationEditorState(
    merchantConfig: MerchantConfig | undefined,
    ipgSystemIdKey: IpgSystemIdKey
): IpgConfigCustomizationEditorState {
    return buildIpgConfigCustomizationEditorStateFromConfig(
        getEffectiveIpgConfig(merchantConfig, ipgSystemIdKey)
    );
}

function buildIpgConfigCustomizationEditorStateMap(
    merchantConfig: MerchantConfig | undefined
): IpgConfigCustomizationEditorStateMap {
    const editorStateMap: IpgConfigCustomizationEditorStateMap = {};
    for (const ipgSystemIdKey of IpgSystemIdKeyList) {
        editorStateMap[ipgSystemIdKey] = buildIpgConfigCustomizationEditorState(
            merchantConfig,
            ipgSystemIdKey
        );
    }
    return editorStateMap;
}

function buildIpgConfigCustomizationPayloadOrThrow(
    ipgSystemIdKey: IpgSystemIdKey,
    editorState: IpgConfigCustomizationEditorState
): IpgConfigCustomization {
    const urlPatternValidationMessage = getOptionalUrlPatternTextValidationMessage(
        editorState.urlPattern
    );
    if (urlPatternValidationMessage !== null) {
        throw new Error(urlPatternValidationMessage);
    }
    const trimmedDefaultApiUrl = editorState.defaultApiUrl?.trim();
    const trimmedDefaultChannel = editorState.defaultChannel?.trim();
    return {
        ipg_id: IpgSystemId[ipgSystemIdKey],
        urlPattern: editorState.urlPattern.trim(),
        allowedWebsites: editorState.allowedWebsites.map(
            buildAllowedWebsiteCustomizationPayloadOrThrow
        ),
        ...(trimmedDefaultApiUrl !== undefined && {
            default_api_url:
                trimmedDefaultApiUrl.length > 0 ? trimmedDefaultApiUrl : undefined,
        }),
        ...(trimmedDefaultChannel !== undefined && {
            default_channel:
                trimmedDefaultChannel.length > 0 ? trimmedDefaultChannel : undefined,
        }),
    };
}

function buildIpgConfigCustomizationPayloadMapOrThrow(
    enabledIpgSystemIds: readonly IpgSystemId[],
    editorStateMap: IpgConfigCustomizationEditorStateMap
): Partial<Record<IpgSystemIdKey, IpgConfigCustomization>> {
    const systemIdsToPersist = appendUniqueSystemId(
        enabledIpgSystemIds,
        ValorIpgSystemId
    );
    return Object.fromEntries(
        systemIdsToPersist.map((ipgSystemId) => {
            const ipgSystemIdKey = getIpgSystemIdKeyOrThrow(ipgSystemId);
            const editorState = editorStateMap[ipgSystemIdKey];
            if (editorState === undefined) {
                throw new Error(`Missing ${ipgSystemIdKey} configuration editor state`);
            }
            return [
                ipgSystemIdKey,
                buildIpgConfigCustomizationPayloadOrThrow(ipgSystemIdKey, editorState),
            ] as const;
        })
    );
}

export function getIpgConfigCustomizationEditorValidationIssues(
    enabledIpgSystemIds: readonly IpgSystemId[],
    editorStateMap: IpgConfigCustomizationEditorStateMap
): MerchantConfigValidationIssue[] {
    const validationIssues: MerchantConfigValidationIssue[] = [];
    const systemIdsToValidate = appendUniqueSystemId(
        enabledIpgSystemIds,
        ValorIpgSystemId
    );
    for (const ipgSystemId of systemIdsToValidate) {
        const ipgSystemIdKey = getIpgSystemIdKeyOrThrow(ipgSystemId);
        const editorState = editorStateMap[ipgSystemIdKey];
        const systemLabel = defaultIpgConfigByKey[ipgSystemIdKey].name;
        if (editorState === undefined) {
            validationIssues.push(
                buildMerchantConfigValidationIssue({
                    fieldLabel: `${systemLabel} configuration`,
                    reason: `Missing ${ipgSystemIdKey} configuration editor state.`,
                    anchor: 'enable_ipg_system_ids_config',
                })
            );
            continue;
        }
        const urlPatternValidationIssue = getRequiredUrlPatternValidationIssue(
            editorState.urlPattern,
            `${systemLabel} URL pattern`,
            `ipg_${ipgSystemIdKey}_url_pattern`
        );
        if (urlPatternValidationIssue !== null) {
            validationIssues.push(urlPatternValidationIssue);
        }
        for (const [websiteIndex, allowedWebsite] of editorState.allowedWebsites.entries()) {
            validationIssues.push(
                ...getAllowedWebsiteCustomizationEditorValidationIssues(allowedWebsite, {
                    fieldLabelPrefix: `${systemLabel} allowed website ${websiteIndex + 1}`,
                    idPrefix: `ipg_${ipgSystemIdKey}_allowed_website_${websiteIndex}`,
                })
            );
        }
    }
    return validationIssues;
}

export function getMerchantConfigEditorValidationIssues(input: {
    readonly enabledIpwSystemIds: readonly IpwSystemId[];
    readonly enabledIpgSystemIds: readonly IpgSystemId[];
    readonly ipwConfigCustomizations: IpwConfigCustomizationEditorStateMap;
    readonly ipgConfigCustomizations: IpgConfigCustomizationEditorStateMap;
    readonly dealertrackReconciliation: DealertrackReconciliationEditorState;
}): MerchantConfigValidationIssue[] {
    return [
        ...getIpwConfigCustomizationEditorValidationIssues(
            input.enabledIpwSystemIds,
            input.ipwConfigCustomizations
        ),
        ...getIpgConfigCustomizationEditorValidationIssues(
            input.enabledIpgSystemIds,
            input.ipgConfigCustomizations
        ),
        ...getDealertrackReconciliationValidationIssues(
            input.enabledIpwSystemIds,
            input.dealertrackReconciliation
        ),
    ];
}

function buildUnmanagedWebsiteEditorState(
    website?: UnmanagedAllowedWebsite
): UnmanagedWebsiteEditorState {
    return {
        name: website?.name ?? '',
        url: website?.url ?? '',
        allowedUrlPatternsText: website?.allowedUrlPatterns.join('\n') ?? '',
        sessionPersistence: website?.sessionPersistence ?? SessionPersistenceType.PERSISTENT,
        promoteSessionCookies: website?.promoteSessionCookies ?? false,
        urlPatternsRules:
            website?.urlPatternsRules?.map(buildUrlPatternsRuleGroupEditorState) ?? [],
        fetchDefaultAction: website?.fetchDefaultAction ?? UrlPatternsRuleAction.ALLOW,
        iframeUrlPatterns:
            website?.iframeUrlPatterns?.map(buildUrlPatternsRuleGroupEditorState) ?? [],
        iframeDefaultAction: website?.iframeDefaultAction ?? UrlPatternsRuleAction.ALLOW,
    };
}

function normalizeUnmanagedAllowedWebsiteForComparison(
    website: UnmanagedAllowedWebsite
): UnmanagedAllowedWebsite {
    return {
        type: website.type,
        system_id: website.system_id,
        ...(website.template_name === undefined
            ? {}
            : { template_name: website.template_name }),
        name: website.name,
        url: website.url,
        allowedUrlPatterns: [...website.allowedUrlPatterns],
        sessionPersistence: website.sessionPersistence,
        promoteSessionCookies: website.promoteSessionCookies,
        urlPatternsRules: website.urlPatternsRules.map((rule) => ({
            action: rule.action,
            urlPatterns: [...rule.urlPatterns],
        })),
        fetchDefaultAction: website.fetchDefaultAction,
        iframeUrlPatterns: website.iframeUrlPatterns.map((rule) => ({
            action: rule.action,
            urlPatterns: [...rule.urlPatterns],
        })),
        iframeDefaultAction: website.iframeDefaultAction,
    };
}

function isSameUnmanagedAllowedWebsite(
    left: UnmanagedAllowedWebsite,
    right: UnmanagedAllowedWebsite
): boolean {
    return (
        JSON.stringify(normalizeUnmanagedAllowedWebsiteForComparison(left)) ===
        JSON.stringify(normalizeUnmanagedAllowedWebsiteForComparison(right))
    );
}

function getRuntimeOrganizationAllowedWebsiteConfigs(
    organizationAllowedWebsites: readonly OrganizationAllowedWebsite[]
): UnmanagedAllowedWebsite[] {
    return organizationAllowedWebsites.flatMap((organizationAllowedWebsite) => {
        if (organizationAllowedWebsite.status !== Status.ACTIVE) {
            return [];
        }
        if (
            organizationAllowedWebsite.source ===
                OrganizationAllowedWebsiteSource.TEMPLATE &&
            !organizationAllowedWebsite.included_in_runtime_config
        ) {
            return [];
        }
        if (
            organizationAllowedWebsite.template !== null &&
            organizationAllowedWebsite.template.status !== Status.ACTIVE
        ) {
            return [];
        }
        return [organizationAllowedWebsite.website_config];
    });
}

function getGenericWebsiteCredentialOptionWebsites(
    organizationAllowedWebsites: readonly OrganizationAllowedWebsite[]
): UnmanagedAllowedWebsite[] {
    const websites: UnmanagedAllowedWebsite[] = [];
    for (const organizationWebsiteConfig of getRuntimeOrganizationAllowedWebsiteConfigs(
        organizationAllowedWebsites
    )) {
        if (
            websites.some((website) =>
                isSameUnmanagedAllowedWebsite(website, organizationWebsiteConfig)
            )
        ) {
            continue;
        }
        websites.push(organizationWebsiteConfig);
    }
    return websites;
}

function buildSecurityAllowedWebsiteDisplayRows(
    organizationAllowedWebsites: readonly OrganizationAllowedWebsite[]
): SecurityAllowedWebsiteDisplayRow[] {
    return organizationAllowedWebsites.map(
        (
            organizationAllowedWebsite,
            organizationWebsiteIndex
        ): SecurityAllowedWebsiteDisplayRow => ({
            rowUuid:
                organizationAllowedWebsite.organization_allowed_website_uuid ??
                `organization-custom-${organizationWebsiteIndex}`,
            source: organizationAllowedWebsite.source,
            websiteConfig: organizationAllowedWebsite.website_config,
            status: organizationAllowedWebsite.status,
            capabilities: organizationAllowedWebsite.capabilities,
            templateName: organizationAllowedWebsite.template?.name ?? null,
            templateUuid:
                organizationAllowedWebsite.template
                    ?.allowed_website_template_uuid ?? null,
            copiedFromTemplateName:
                organizationAllowedWebsite.copied_from_template?.name ?? null,
            templateProvenance:
                buildSecurityAllowedWebsiteTemplateProvenance(
                    organizationAllowedWebsite
                ),
        })
    );
}

function buildSecurityAllowedWebsiteTemplateProvenance(
    organizationAllowedWebsite: OrganizationAllowedWebsite
): SecurityAllowedWebsiteTemplateProvenance | null {
    if (
        organizationAllowedWebsite.source !==
        OrganizationAllowedWebsiteSource.LOCAL
    ) {
        return null;
    }
    const provenanceName =
        organizationAllowedWebsite.website_config.template_name ??
        organizationAllowedWebsite.template_provenance?.name ??
        organizationAllowedWebsite.copied_from_template?.name ??
        null;
    if (provenanceName === null) {
        return null;
    }
    return {
        name: provenanceName,
        templateUuid:
            organizationAllowedWebsite.template_provenance
                ?.allowed_website_template_uuid ??
            organizationAllowedWebsite.copied_from_template
                ?.allowed_website_template_uuid ??
            null,
        canViewTemplate:
            organizationAllowedWebsite.template_provenance?.capabilities
                .can_view_template === true,
    };
}

function formatSecurityAllowedWebsiteSource(
    row: SecurityAllowedWebsiteDisplayRow
): string {
    if (row.source === OrganizationAllowedWebsiteSource.TEMPLATE) {
        return 'Template';
    }
    return 'Merchant-local website';
}

function formatAllowedWebsiteSystemName(systemId: ThirdPartySystemId): string {
    if (systemId === ThirdPartySystemId.GENERIC) {
        return 'Generic';
    }
    return (
        defaultIntegratedWebsiteDisplayNameBySystemId[systemId] ??
        'Unsupported allowed website'
    );
}

function buildUnmanagedWebsitePayloadOrThrow(
    editorState: UnmanagedWebsiteEditorState
): UnmanagedAllowedWebsite {
    const validationMessage = getUnmanagedWebsiteEditorValidationMessage(editorState);
    if (validationMessage !== null) {
        throw new Error(validationMessage);
    }
    const allowedUrlPatterns = parseUrlPatternsText(editorState.allowedUrlPatternsText);
    return AllowedWebsiteSchemaUnmanaged.parse({
        type: IpWebsiteType.UNMANAGED,
        system_id: ThirdPartySystemId.GENERIC,
        name: editorState.name.trim(),
        url: editorState.url.trim(),
        allowedUrlPatterns,
        sessionPersistence: editorState.sessionPersistence,
        promoteSessionCookies: editorState.promoteSessionCookies,
        urlPatternsRules: editorState.urlPatternsRules.map((urlPatternsRuleGroup) => ({
            action: urlPatternsRuleGroup.action,
            urlPatterns: parseUrlPatternsText(urlPatternsRuleGroup.urlPatternsText),
        })),
        fetchDefaultAction: editorState.fetchDefaultAction,
        iframeUrlPatterns: editorState.iframeUrlPatterns.map((iframeUrlPatternGroup) => ({
            action: iframeUrlPatternGroup.action,
            urlPatterns: parseUrlPatternsText(iframeUrlPatternGroup.urlPatternsText),
        })),
        iframeDefaultAction: editorState.iframeDefaultAction,
    });
}

function getUnmanagedWebsiteUrlValidationMessage(
    editorState: UnmanagedWebsiteEditorState
): string | null {
    const trimmedUrl = editorState.url.trim();
    if (trimmedUrl.length === 0) {
        return 'URL is required. Enter a complete URL such as https://example.com.';
    }
    if (!isValidUrl(trimmedUrl)) {
        return `URL "${trimmedUrl}" is invalid. Enter a complete URL such as https://example.com.`;
    }
    return null;
}

function getUnmanagedWebsiteEditorValidationMessage(
    editorState: UnmanagedWebsiteEditorState
): string | null {
    const urlValidationMessage = getUnmanagedWebsiteUrlValidationMessage(editorState);
    if (urlValidationMessage !== null) {
        return urlValidationMessage;
    }
    return getAllowedWebsiteCustomizationEditorValidationMessage({
        allowedUrlPatternsText: editorState.allowedUrlPatternsText,
        sessionPersistence: editorState.sessionPersistence,
        promoteSessionCookies: editorState.promoteSessionCookies,
        urlPatternsRules: editorState.urlPatternsRules,
        fetchDefaultAction: editorState.fetchDefaultAction,
        iframeUrlPatterns: editorState.iframeUrlPatterns,
        iframeDefaultAction: editorState.iframeDefaultAction,
    });
}

function getAllowPromoteSessionCookie(
    merchantConfig: MerchantConfig | undefined
): boolean {
    return merchantConfig?.sambaConfig?.allowPromoteSessionCookie ?? false;
}

function isValidUrl(value: string): boolean {
    try {
        new URL(value);
        return true;
    } catch {
        return false;
    }
}

function buildValorConfigForSaveOrThrow(
    editorState: ValorMerchantConfigEditorState
): ValorIpgConfig {
    const managedValorConfig = getManagedValorConfigForPortalMode(editorState.portalMode);
    if (managedValorConfig !== null) {
        return cloneValorConfig(managedValorConfig);
    }

    const trimmedPortalUrl = editorState.portalUrl.trim();
    const trimmedDefaultApiUrl = editorState.defaultApiUrl.trim();
    const trimmedDefaultChannel = editorState.defaultChannel.trim();

    if (!isValidUrl(trimmedPortalUrl)) {
        throw new Error('Valor portal URL must be a valid URL');
    }
    if (!isValidUrl(trimmedDefaultApiUrl)) {
        throw new Error('Valor default API URL must be a valid URL');
    }

    const portalOrigin = new URL(trimmedPortalUrl).origin;
    const productionValorConfig = cloneValorConfig(ipgConfigValorProd);
    const productionAllowedWebsite = productionValorConfig.allowedWebsites[0];
    if (productionAllowedWebsite === undefined) {
        throw new Error('Valor production config is missing an allowed website');
    }

    return {
        ...productionValorConfig,
        urlPattern: `${portalOrigin}/*`,
        allowedWebsites: [
            {
                ...productionAllowedWebsite,
                url: trimmedPortalUrl,
                allowedUrlPatterns: [`${portalOrigin}/*`],
            },
        ],
        default_api_url: trimmedDefaultApiUrl,
        default_channel: trimmedDefaultChannel.length > 0 ? trimmedDefaultChannel : undefined,
    };
}

export function buildCustomerPortalPaymentRequestsPayloadOrThrow(
    editorState: CustomerPortalPaymentRequestsEditorState
): CustomerPortalPaymentRequestsConfig {
    const normalizedEditorState =
        normalizeCustomerPortalPaymentRequestsEditorState(editorState);
    const allowedDeliveryChannels: CustomerPortalDeliveryChannel[] = [];
    if (normalizedEditorState.allowEmail) {
        allowedDeliveryChannels.push(CustomerPortalDeliveryChannel.EMAIL);
    }
    if (normalizedEditorState.allowSms) {
        allowedDeliveryChannels.push(CustomerPortalDeliveryChannel.SMS);
    }

    if (normalizedEditorState.allowSms && !normalizedEditorState.twilioEnabled) {
        throw new Error('SMS delivery channel requires enabled Twilio sender settings');
    }
    if (normalizedEditorState.allowEmail && !normalizedEditorState.sendgridEnabled) {
        throw new Error('Email delivery channel requires enabled SendGrid sender settings');
    }

    const trimmedTwilioFromPhoneNumber = normalizedEditorState.twilioFromPhoneNumber.trim();
    const normalizedTwilioFromPhoneNumber = normalizeNorthAmericanPhoneNumberToE164(
        trimmedTwilioFromPhoneNumber
    );
    const trimmedTwilioMessagingServiceSid =
        normalizedEditorState.twilioMessagingServiceSid.trim();
    const trimmedSendgridFromEmail = normalizedEditorState.sendgridFromEmail.trim();
    const trimmedSendgridFromName = normalizedEditorState.sendgridFromName.trim();
    const trimmedSendgridReplyToEmail =
        normalizedEditorState.sendgridReplyToEmail.trim();

    if (
        normalizedEditorState.twilioEnabled &&
        normalizedEditorState.twilioSenderMode === 'from_phone_number' &&
        trimmedTwilioFromPhoneNumber.length === 0
    ) {
        throw new Error('Twilio from phone number is required when SMS sender routing uses a phone number');
    }
    if (
        normalizedEditorState.twilioEnabled &&
        normalizedEditorState.twilioSenderMode === 'messaging_service_sid' &&
        trimmedTwilioMessagingServiceSid.length === 0
    ) {
        throw new Error(
            'Twilio messaging service SID is required when SMS sender routing uses a messaging service'
        );
    }
    if (normalizedEditorState.sendgridEnabled && trimmedSendgridFromEmail.length === 0) {
        throw new Error('SendGrid from email is required when email delivery is enabled');
    }

    return CustomerPortalPaymentRequestsConfigSchema.parse({
        enabled: normalizedEditorState.enabled,
        allowedDeliveryChannels,
        templateSelections: normalizedEditorState.templateSelections,
        ...(normalizedEditorState.documentPolicy !== undefined
            ? { documentPolicy: normalizedEditorState.documentPolicy }
            : {}),
        ...(normalizedEditorState.twilioEnabled
            ? normalizedEditorState.twilioSenderMode === 'from_phone_number'
                ? {
                      twilioSms: {
                          enabled: true,
                          fromPhoneNumber: normalizedTwilioFromPhoneNumber,
                      },
                  }
                : {
                      twilioSms: {
                          enabled: true,
                          messagingServiceSid: trimmedTwilioMessagingServiceSid,
                      },
                  }
            : {}),
        ...(normalizedEditorState.sendgridEnabled
            ? {
                  sendgridEmail: {
                      enabled: true,
                      fromEmail: trimmedSendgridFromEmail,
                      ...(trimmedSendgridFromName.length > 0
                          ? { fromName: trimmedSendgridFromName }
                          : {}),
                      ...(trimmedSendgridReplyToEmail.length > 0
                          ? { replyToEmail: trimmedSendgridReplyToEmail }
                          : {}),
                  },
              }
            : {}),
    });
}

export function getIsParentOrganizationReassignConfirmationModalOpen(
    state: ParentOrganizationReassignConfirmationState
): boolean {
    return (
        state.showReassignConfirm &&
        state.pendingParentAction !== null &&
        state.pendingParentOrganizationName !== null &&
        state.pendingParentOrganizationType !== null
    );
}

export function getParentOrganizationCandidatesByType(
    parentOrgType: ParentOrganizationType,
    isos: ReadonlyArray<OrganizationListItem>,
    merchants: ReadonlyArray<OrganizationListItem>
): ReadonlyArray<OrganizationListItem> {
    if (parentOrgType === 'ISO') {
        return isos;
    }
    if (parentOrgType === 'MERCHANT') {
        return merchants;
    }
    throw new Error(`Unsupported parent organization type: ${parentOrgType}`);
}

function isIsoOrganization(
    organization: GetOrganizationResponse | OrganizationListItem | null | undefined
): boolean {
    if (organization === null || organization === undefined) {
        return false;
    }
    return organizationHasCapacity(organization, 'is_iso');
}

function isMerchantOrganization(
    organization:
        | GetOrganizationResponse
        | OrganizationListItem
        | OrganizationOmnisearchListItem
        | null
        | undefined
): boolean {
    if (organization === null || organization === undefined) {
        return false;
    }
    return organizationHasCapacity(organization, 'is_merchant');
}

function getOrganizationView(
    organization: GetOrganizationResponse | OrganizationListItem,
    requestedView: string | null
): OrganizationView {
    if (requestedView === 'merchant' && isMerchantOrganization(organization)) {
        return 'MERCHANT';
    }
    if (requestedView === 'iso' && isIsoOrganization(organization)) {
        return 'ISO';
    }
    if (requestedView === 'customer' && organizationHasCapacity(organization, 'is_customer')) {
        return 'CUSTOMER';
    }
    if (isMerchantOrganization(organization)) {
        return 'MERCHANT';
    }
    if (isIsoOrganization(organization)) {
        return 'ISO';
    }
    if (organizationHasCapacity(organization, 'is_customer')) {
        return 'CUSTOMER';
    }
    throw new Error(
        `Organization ${organization.organization_uuid ?? '<unknown>'} has no configured capacities`
    );
}

export function getIpgSystemOptions(): ReadonlyArray<MerchantSystemOption<IpgSystemId>> {
    return IpgSystemIdKeyList.map((ipgSystemIdKey) => {
        const ipgConfig = defaultIpgConfigByKey[ipgSystemIdKey];
        const ipgSystemId: IpgSystemId = IpgSystemId[ipgSystemIdKey];
        return {
            systemId: ipgSystemId,
            label: ipgConfig.name,
        };
    });
}

export function getIpwSystemOptions(): ReadonlyArray<MerchantSystemOption<IpwSystemId>> {
    return IpwSystemIdKeyList.map((ipwSystemIdKey) => {
        const ipwConfig = defaultIpwConfigByKey[ipwSystemIdKey];
        const ipwSystemId: IpwSystemId = IpwSystemId[ipwSystemIdKey];
        return {
            systemId: ipwSystemId,
            label: ipwConfig.name,
        };
    });
}

export function appendUniqueSystemId<TSystemId extends number>(
    currentSystemIds: ReadonlyArray<TSystemId>,
    nextSystemId: TSystemId
): TSystemId[] {
    if (currentSystemIds.includes(nextSystemId)) {
        return [...currentSystemIds];
    }
    return [...currentSystemIds, nextSystemId];
}

export function removeSystemId<TSystemId extends number>(
    currentSystemIds: ReadonlyArray<TSystemId>,
    systemIdToRemove: TSystemId
): TSystemId[] {
    return currentSystemIds.filter((systemId) => systemId !== systemIdToRemove);
}

function renderOrganizationOmnisearchCard(
    organization: OrganizationOmnisearchListItem
): ReactElement {
    const businessDetailsSummary: string =
        organization.businessDetails === undefined
            ? 'Business details not set'
            : organization.businessDetails.legalEntityName;
    return (
        <div className="space-y-1">
            <div className="font-medium">{organization.name}</div>
            <div className="text-xs text-muted-foreground">
                Capacities: {getOrganizationCapacityLabel(organization)}
            </div>
            {isMerchantOrganization(organization) && (
                <div className="text-xs text-muted-foreground">{businessDetailsSummary}</div>
            )}
        </div>
    );
}

function getSystemOptionLabelOrThrow<TSystemId extends number>(
    systemOptions: ReadonlyArray<MerchantSystemOption<TSystemId>>,
    systemId: TSystemId
): string {
    const matchedOption = systemOptions.find((option) => option.systemId === systemId);
    if (matchedOption === undefined) {
        throw new Error(`Unknown system id: ${systemId}`);
    }
    return matchedOption.label;
}

function renderUrlPatternValidationMessage(message: string | null): ReactElement | null {
    if (message === null) {
        return null;
    }
    return <p className="text-sm text-destructive">{message}</p>;
}

function renderMerchantConfigValidationIssueAlert(
    validationIssues: readonly MerchantConfigValidationIssue[]
): ReactElement | null {
    if (validationIssues.length === 0) {
        return null;
    }
    return (
        <Alert variant="destructive">
            <AlertDescription>
                <div className="space-y-2">
                    <p>Configuration cannot be saved until these fields are fixed.</p>
                    <ul className="list-disc space-y-1 pl-5">
                        {validationIssues.map((issue, issueIndex) => (
                            <li key={`${issue.anchor}-${issueIndex}`}>
                                <a className="underline" href={`#${issue.anchor}`}>
                                    {issue.fieldLabel}
                                </a>
                                : {issue.reason}
                            </li>
                        ))}
                    </ul>
                </div>
            </AlertDescription>
        </Alert>
    );
}

function UrlPatternsRuleEditorSection(props: {
    readonly idPrefix: string;
    readonly title: string;
    readonly emptyState: string;
    readonly addButtonLabel: string;
    readonly defaultActionLabel: string;
    readonly defaultAction: UrlPatternsRuleActionValue;
    readonly ruleGroups: readonly UrlPatternsRuleGroupEditorState[];
    readonly disabled?: boolean;
    readonly onDefaultActionChange: (action: UrlPatternsRuleActionValue) => void;
    readonly onRuleGroupsChange: (
        nextGroups: readonly UrlPatternsRuleGroupEditorState[]
    ) => void;
}): ReactElement {
    const disabled: boolean = props.disabled ?? false;
    return (
        <div className="space-y-3">
            <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-2">
                    <Label htmlFor={`${props.idPrefix}_default_action`}>
                        {props.defaultActionLabel}
                    </Label>
                    <select
                        id={`${props.idPrefix}_default_action`}
                        className="w-full rounded-md border px-3 py-2"
                        value={props.defaultAction}
                        disabled={disabled}
                        onChange={(event) =>
                            props.onDefaultActionChange(
                                event.target.value === UrlPatternsRuleAction.DENY
                                    ? UrlPatternsRuleAction.DENY
                                    : UrlPatternsRuleAction.ALLOW
                            )
                        }
                    >
                        <option value={UrlPatternsRuleAction.ALLOW}>Allow</option>
                        <option value={UrlPatternsRuleAction.DENY}>Deny</option>
                    </select>
                </div>
            </div>
            <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-medium">{props.title}</p>
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={disabled}
                        onClick={() =>
                            props.onRuleGroupsChange([
                                ...props.ruleGroups,
                                buildUrlPatternsRuleGroupEditorState(),
                            ])
                        }
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        {props.addButtonLabel}
                    </Button>
                </div>
                {props.ruleGroups.length === 0 ? (
                    <p className="text-sm text-muted-foreground">{props.emptyState}</p>
                ) : (
                    <div className="space-y-3">
                        {props.ruleGroups.map((ruleGroup, groupIndex) => {
                            const groupValidationMessage = getUrlPatternTextValidationMessage(
                                ruleGroup.urlPatternsText
                            );
                            return (
                                <div key={groupIndex} className="space-y-3 rounded-md border p-3">
                                    <div className="flex items-center justify-between gap-3">
                                        <p className="text-sm font-medium">
                                            Rule {groupIndex + 1}
                                        </p>
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            disabled={disabled}
                                            onClick={() =>
                                                props.onRuleGroupsChange(
                                                    props.ruleGroups.filter(
                                                        (_value, currentIndex) =>
                                                            currentIndex !== groupIndex
                                                    )
                                                )
                                            }
                                        >
                                            Remove
                                        </Button>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor={`${props.idPrefix}_rule_${groupIndex}_action`}>
                                            Action
                                        </Label>
                                        <select
                                            id={`${props.idPrefix}_rule_${groupIndex}_action`}
                                            className="w-full rounded-md border px-3 py-2"
                                            value={ruleGroup.action}
                                            disabled={disabled}
                                            onChange={(event) =>
                                                props.onRuleGroupsChange(
                                                    props.ruleGroups.map(
                                                        (currentGroup, currentIndex) =>
                                                            currentIndex === groupIndex
                                                                ? {
                                                                      ...currentGroup,
                                                                      action:
                                                                          event.target.value ===
                                                                          UrlPatternsRuleAction.DENY
                                                                              ? UrlPatternsRuleAction.DENY
                                                                              : UrlPatternsRuleAction.ALLOW,
                                                                  }
                                                                : currentGroup
                                                    )
                                                )
                                            }
                                        >
                                            <option value={UrlPatternsRuleAction.ALLOW}>
                                                Allow
                                            </option>
                                            <option value={UrlPatternsRuleAction.DENY}>
                                                Deny
                                            </option>
                                        </select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label
                                            htmlFor={`${props.idPrefix}_rule_${groupIndex}_patterns`}
                                        >
                                            URL patterns
                                        </Label>
                                        <textarea
                                            id={`${props.idPrefix}_rule_${groupIndex}_patterns`}
                                            className="min-h-20 w-full rounded-md border px-3 py-2 text-sm"
                                            value={ruleGroup.urlPatternsText}
                                            disabled={disabled}
                                            onChange={(event) =>
                                                props.onRuleGroupsChange(
                                                    props.ruleGroups.map(
                                                        (currentGroup, currentIndex) =>
                                                            currentIndex === groupIndex
                                                                ? {
                                                                      ...currentGroup,
                                                                      urlPatternsText:
                                                                          event.target.value,
                                                                  }
                                                                : currentGroup
                                                    )
                                                )
                                            }
                                        />
                                        {renderUrlPatternValidationMessage(
                                            groupValidationMessage
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

function AllowedWebsiteCustomizationEditor(props: {
    readonly idPrefix: string;
    readonly title: string;
    readonly url?: string;
    readonly value: AllowedWebsiteCustomizationEditorState;
    readonly disabled?: boolean;
    readonly onChange: (value: AllowedWebsiteCustomizationEditorState) => void;
}): ReactElement {
    const disabled: boolean = props.disabled ?? false;
    const allowedUrlPatternsValidationMessage = getUrlPatternTextValidationMessage(
        props.value.allowedUrlPatternsText
    );
    return (
        <div className="space-y-3 rounded-md border p-3">
            <div className="space-y-1">
                <p className="text-sm font-medium">{props.title}</p>
                {props.url !== undefined && (
                    <p className="break-all text-sm text-muted-foreground">{props.url}</p>
                )}
            </div>
            <div className="space-y-2">
                <Label htmlFor={`${props.idPrefix}_allowed_url_patterns`}>
                    Allowed URL patterns
                </Label>
                <textarea
                    id={`${props.idPrefix}_allowed_url_patterns`}
                    className="min-h-20 w-full rounded-md border px-3 py-2 text-sm"
                    value={props.value.allowedUrlPatternsText}
                    disabled={disabled}
                    onChange={(event) =>
                        props.onChange({
                            ...props.value,
                            allowedUrlPatternsText: event.target.value,
                        })
                    }
                />
                {renderUrlPatternValidationMessage(allowedUrlPatternsValidationMessage)}
            </div>
            <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-2">
                    <Label htmlFor={`${props.idPrefix}_session_persistence`}>
                        Session persistence
                    </Label>
                    <select
                        id={`${props.idPrefix}_session_persistence`}
                        className="w-full rounded-md border px-3 py-2"
                        value={props.value.sessionPersistence}
                        disabled={disabled}
                        onChange={(event) =>
                            props.onChange({
                                ...props.value,
                                sessionPersistence:
                                    event.target.value === SessionPersistenceType.EPHEMERAL
                                        ? SessionPersistenceType.EPHEMERAL
                                        : SessionPersistenceType.PERSISTENT,
                            })
                        }
                    >
                        <option value={SessionPersistenceType.PERSISTENT}>Persistent</option>
                        <option value={SessionPersistenceType.EPHEMERAL}>Ephemeral</option>
                    </select>
                </div>
                <CheckboxField
                    label="Promote session cookies"
                    checked={props.value.promoteSessionCookies}
                    disabled={disabled}
                    className="self-end"
                    onCheckedChange={(checked) =>
                        props.onChange({
                            ...props.value,
                            promoteSessionCookies: checked,
                        })
                    }
                />
            </div>
            <UrlPatternsRuleEditorSection
                idPrefix={`${props.idPrefix}_fetch`}
                title="Fetch URL patterns"
                emptyState="No explicit fetch URL pattern rules configured."
                addButtonLabel="Fetch rule"
                defaultActionLabel="Fetch default action"
                defaultAction={props.value.fetchDefaultAction}
                ruleGroups={props.value.urlPatternsRules}
                disabled={disabled}
                onDefaultActionChange={(fetchDefaultAction) =>
                    props.onChange({
                        ...props.value,
                        fetchDefaultAction,
                    })
                }
                onRuleGroupsChange={(urlPatternsRules) =>
                    props.onChange({
                        ...props.value,
                        urlPatternsRules,
                    })
                }
            />
            <UrlPatternsRuleEditorSection
                idPrefix={`${props.idPrefix}_iframe`}
                title="Iframe URL patterns"
                emptyState="No explicit iframe URL pattern rules configured."
                addButtonLabel="Iframe rule"
                defaultActionLabel="Iframe default action"
                defaultAction={props.value.iframeDefaultAction}
                ruleGroups={props.value.iframeUrlPatterns}
                disabled={disabled}
                onDefaultActionChange={(iframeDefaultAction) =>
                    props.onChange({
                        ...props.value,
                        iframeDefaultAction,
                    })
                }
                onRuleGroupsChange={(iframeUrlPatterns) =>
                    props.onChange({
                        ...props.value,
                        iframeUrlPatterns,
                    })
                }
            />
        </div>
    );
}

function AllowedWebsitePolicySummary(props: {
    readonly title: string;
    readonly url?: string;
    readonly allowedUrlPatterns: readonly string[];
    readonly sessionPersistence: SessionPersistenceType;
    readonly promoteSessionCookies: boolean;
    readonly urlPatternsRules: readonly UrlPatternsRule[];
    readonly fetchDefaultAction: UrlPatternsRuleActionValue;
    readonly iframeUrlPatterns: readonly UrlPatternsRule[];
    readonly iframeDefaultAction: UrlPatternsRuleActionValue;
}): ReactElement {
    return (
        <div className="rounded-md border p-3">
            <p className="text-sm font-medium">{props.title}</p>
            {props.url !== undefined && (
                <p className="break-all text-sm text-muted-foreground">{props.url}</p>
            )}
            <div className="mt-3 space-y-1 text-sm">
                <p>Session persistence: {props.sessionPersistence}</p>
                <p>Promote session cookies: {props.promoteSessionCookies ? 'yes' : 'no'}</p>
                <p>URL patterns: {props.allowedUrlPatterns.join(', ')}</p>
                <p>Fetch default action: {props.fetchDefaultAction}</p>
                <p>
                    Fetch URL pattern rules:{' '}
                    {props.urlPatternsRules.length === 0
                        ? 'none'
                        : props.urlPatternsRules
                              .map(
                                  (fetchPolicy) =>
                                      `${fetchPolicy.action}: ${fetchPolicy.urlPatterns.join(', ')}`
                              )
                              .join(' | ')}
                </p>
                <p>Iframe default action: {props.iframeDefaultAction}</p>
                <p>
                    Iframe URL pattern rules:{' '}
                    {props.iframeUrlPatterns.length === 0
                        ? 'none'
                        : props.iframeUrlPatterns
                              .map(
                                  (iframePolicy) =>
                                      `${iframePolicy.action}: ${iframePolicy.urlPatterns.join(', ')}`
                              )
                              .join(' | ')}
                </p>
            </div>
        </div>
    );
}

function IntegrationPolicySummary(props: {
    readonly title: string;
    readonly integrations: ReadonlyArray<{
        name: string;
        urlPattern: string;
        allowedWebsites: ReadonlyArray<AllowedWebsiteBase>;
    }>;
}): ReactElement {
    return (
        <div className="space-y-3 rounded-md border p-3">
            <p className="text-sm font-medium">{props.title}</p>
            {props.integrations.map((integration) => (
                <div key={integration.name} className="space-y-3 rounded-md border p-3">
                    <p className="text-sm font-medium">{integration.name}</p>
                    <div className="space-y-1 text-sm">
                        <p>URL pattern: {integration.urlPattern}</p>
                    </div>
                    {integration.allowedWebsites.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            No allowed websites configured
                        </p>
                    ) : (
                        <div className="grid gap-3 md:grid-cols-2">
                            {integration.allowedWebsites.map((website, websiteIndex) => (
                                <AllowedWebsitePolicySummary
                                    key={`${integration.name}-${website.name}-${websiteIndex}`}
                                    title={website.name}
                                    url={website.url}
                                    allowedUrlPatterns={website.allowedUrlPatterns}
                                    sessionPersistence={website.sessionPersistence}
                                    promoteSessionCookies={website.promoteSessionCookies}
                                    urlPatternsRules={website.urlPatternsRules}
                                    fetchDefaultAction={website.fetchDefaultAction}
                                    iframeUrlPatterns={website.iframeUrlPatterns}
                                    iframeDefaultAction={website.iframeDefaultAction}
                                />
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

const CursorPageParamSchema = z.string().trim().min(1);

function parseEmbeddedListCursorParam(pageParam: unknown): string | undefined {
    if (pageParam === null || pageParam === undefined) {
        return undefined;
    }
    return CursorPageParamSchema.parse(pageParam);
}

export function OrganizationDetailContent() {
    const log = createComponentLogger('OrganizationDetailContent');
    const navigate = useNavigate();
    const params = useParams();
    const organizationUuidParseResult = z.string().uuid().safeParse(params.id);
    if (!organizationUuidParseResult.success) {
        throw new Error(`Invalid organization UUID: ${params.id}`);
    }
    const organizationUuidFromRoute = organizationUuidParseResult.data;
    const queryClient = useQueryClient();
    const bumpOrganizationListsVersion: () => void = useDataInvalidationStore(
        (state) => state.bumpOrganizationListsVersion
    );
    const bumpUserListsVersion: () => void = useDataInvalidationStore(
        (state) => state.bumpUserListsVersion
    );
    const [searchParams] = useSearchParams();
    const [isEditing, setIsEditing] = useState(false);
    const [isReassigning, setIsReassigning] = useState(false);
    const [
        focusedPolicyPermissionBundleRequest,
        setFocusedPolicyPermissionBundleRequest,
    ] = useState<PolicyPermissionBundleFocusRequest | null>(null);
    const [isAssigningParentMerchant, setIsAssigningParentMerchant] = useState<boolean>(false);
    const [editingConfigCard, setEditingConfigCard] =
        useState<MerchantConfigEditorCard | null>(null);
    const isEditingConfig: boolean = editingConfigCard !== null;
    const [isEditingBusinessDetails, setIsEditingBusinessDetails] = useState<boolean>(false);
    const [businessDetailsEditorError, setBusinessDetailsEditorError] = useState<string | null>(null);
    const [configEditorError, setConfigEditorError] = useState<string | null>(null);
    const [businessDetailsEditorState, setBusinessDetailsEditorState] =
        useState<BusinessDetailsEditorState>(defaultBusinessDetailsEditorState);
    const [enabledIpgSystemIdsForEdit, setEnabledIpgSystemIdsForEdit] = useState<IpgSystemId[]>([]);
    const [enabledIpwSystemIdsForEdit, setEnabledIpwSystemIdsForEdit] = useState<IpwSystemId[]>([]);
    const [
        dealertrackAutomaticCustomerDetailsDetectionEnabledForEdit,
        setDealertrackAutomaticCustomerDetailsDetectionEnabledForEdit,
    ] = useState<boolean>(
        buildDealertrackAutomaticCustomerDetailsDetectionEnabledEditorState(undefined)
    );
    const [
        dealertrackCustomerDetailsDetectionButtonEnabledForEdit,
        setDealertrackCustomerDetailsDetectionButtonEnabledForEdit,
    ] = useState<boolean>(
        buildDealertrackCustomerDetailsDetectionButtonEnabledEditorState(undefined)
    );
    const [
        addressRequiredForCardPaymentForEdit,
        setAddressRequiredForCardPaymentForEdit,
    ] = useState<boolean>(buildAddressRequiredForCardPaymentEditorState(undefined));
    const [require2FAForEdit, setRequire2FAForEdit] = useState<boolean>(
        buildRequire2FAEditorState(undefined)
    );
    const [dealertrackReconciliationForEdit, setDealertrackReconciliationForEdit] =
        useState<DealertrackReconciliationEditorState>(
            buildDealertrackReconciliationEditorState(undefined)
        );
    const [customerPortalPaymentRequestsForEdit, setCustomerPortalPaymentRequestsForEdit] =
        useState<CustomerPortalPaymentRequestsEditorState>(
            defaultCustomerPortalPaymentRequestsEditorState
        );
    const updateCustomerPortalPaymentRequestsForEdit = (
        nextState: (
            currentState: CustomerPortalPaymentRequestsEditorState
        ) => CustomerPortalPaymentRequestsEditorState
    ): void => {
        setCustomerPortalPaymentRequestsForEdit((currentState) =>
            normalizeCustomerPortalPaymentRequestsEditorState(nextState(currentState))
        );
    };
    const customerPortalPaymentRequestsAvailability =
        getCustomerPortalPaymentRequestsEditorAvailability(
            customerPortalPaymentRequestsForEdit
        );
    const twilioProviderEnablementMessage = getTwilioProviderEnablementMessage(
        customerPortalPaymentRequestsForEdit
    );
    const smsDeliveryEnablementMessage = getSmsDeliveryEnablementMessage(
        customerPortalPaymentRequestsForEdit
    );
    const [merchantPaymentMethodsForEdit, setMerchantPaymentMethodsForEdit] =
        useState<MerchantPaymentMethodsEditorState>(defaultMerchantPaymentMethodsEditorState);
    const [merchantPaymentConfirmationEmailCcForEdit, setMerchantPaymentConfirmationEmailCcForEdit] =
        useState<PaymentRequestEmailCcEditorState>(defaultPaymentRequestEmailCcEditorState);
    const [merchantPaymentConfirmationEmailCcInput, setMerchantPaymentConfirmationEmailCcInput] =
        useState('');
    const [merchantPaymentConfirmationEmailCcInputError, setMerchantPaymentConfirmationEmailCcInputError] =
        useState<string | null>(null);
    const [valorMerchantConfigForEdit, setValorMerchantConfigForEdit] =
        useState<ValorMerchantConfigEditorState>(
            buildValorMerchantConfigEditorState(undefined)
        );
    const [ipwConfigCustomizationsForEdit, setIpwConfigCustomizationsForEdit] =
        useState<IpwConfigCustomizationEditorStateMap>(
            buildIpwConfigCustomizationEditorStateMap(undefined)
        );
    const [ipgConfigCustomizationsForEdit, setIpgConfigCustomizationsForEdit] =
        useState<IpgConfigCustomizationEditorStateMap>(
            buildIpgConfigCustomizationEditorStateMap(undefined)
        );
    const [allowPromoteSessionCookieForEdit, setAllowPromoteSessionCookieForEdit] =
        useState(false);
    const [unmanagedWebsiteDialogState, setUnmanagedWebsiteDialogState] =
        useState<UnmanagedWebsiteDialogState | null>(null);
    const [
        promoteOrganizationWebsiteDialogState,
        setPromoteOrganizationWebsiteDialogState,
    ] = useState<PromoteOrganizationWebsiteDialogState | null>(null);
    const [pendingUnmanagedWebsiteDelete, setPendingUnmanagedWebsiteDelete] =
        useState<PendingUnmanagedWebsiteDeleteState | null>(null);
    const [
        organizationWebsiteDeleteError,
        setOrganizationWebsiteDeleteError,
    ] = useState<OrganizationWebsiteMutationError | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showReassignConfirm, setShowReassignConfirm] = useState(false);
    const [showImpersonateConfirm, setShowImpersonateConfirm] = useState(false);
    const [organizationMembersExpanded, setOrganizationMembersExpanded] = useState(false);
    const [pendingParentOrganizationUuid, setPendingParentOrganizationUuid] = useState<
        string | null
    >(null);
    const [pendingParentOrganizationName, setPendingParentOrganizationName] = useState<
        string | null
    >(null);
    const [pendingParentOrganizationType, setPendingParentOrganizationType] = useState<
        ParentOrganizationType | null
    >(null);
    const [pendingParentAction, setPendingParentAction] =
        useState<PendingParentOrganizationAction | null>(null);
    const [showImportUsersDialog, setShowImportUsersDialog] = useState(false);
    const hasUnsavedChanges: boolean =
        isEditing ||
        isEditingConfig ||
        isEditingBusinessDetails ||
        unmanagedWebsiteDialogState !== null ||
        promoteOrganizationWebsiteDialogState !== null;
    useUnsavedChangesRegistration(hasUnsavedChanges);
    const [showAssignUsersDialog, setShowAssignUsersDialog] = useState(false);
    const [organizationUsersImportCsvText, setOrganizationUsersImportCsvText] = useState('');
    const [organizationUsersImportFileName, setOrganizationUsersImportFileName] = useState<
        string | null
    >(null);
    const [organizationUsersImportPreview, setOrganizationUsersImportPreview] =
        useState<PreviewOrganizationUsersCsvImportResponse | null>(null);
    const [selectedOrganizationUsersImportRowIndexes, setSelectedOrganizationUsersImportRowIndexes] =
        useState<number[]>([]);
    const [organizationUsersImportError, setOrganizationUsersImportError] =
        useState<string | null>(null);
    const [organizationUsersImportSkipVerification, setOrganizationUsersImportSkipVerification] =
        useState<boolean>(false);
    const [showOrganizationMemberSubsidiaries, setShowOrganizationMemberSubsidiaries] =
        useState<boolean>(false);
    const [roleToImpersonate, setRoleToImpersonate] =
        useState<PendingOrganizationMemberImpersonationState | null>(null);
    const user = useAuthStore((state) => state.user);
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );

    useEffect(() => {
        if (showReassignConfirm) {
            return;
        }
        setPendingParentOrganizationUuid(null);
        setPendingParentOrganizationName(null);
        setPendingParentOrganizationType(null);
        setPendingParentAction(null);
    }, [showReassignConfirm]);

    // Fetch active role assignment from current session
    const { data: activeRoleAssignment } = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => {
            return await authApi.getActiveRoleAssignment();
        },
        enabled: !!user,
        staleTime: 30000, // Cache for 30 seconds
    });
    const canManageOrganizations: boolean = hasInternalAdminPrivileges(
        activeRoleAssignment ?? null
    );
    const canSkipOrganizationUserVerification: boolean =
        activeRoleAssignment?.role === Role.SUPERADMIN
        || activeRoleAssignment?.role === Role.INTERNAL_ADMIN;
    const canAuditPermissions: boolean = isAdminRoleAssignment(
        activeRoleAssignment ?? null
    );

    useEffect(() => {
        if (canManageOrganizations) {
            return;
        }
        setIsReassigning(false);
        setIsAssigningParentMerchant(false);
    }, [canManageOrganizations]);

    const {
        data: org,
        isLoading,
        error,
    } = useQuery<GetOrganizationResponse>({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'organization',
            organizationUuidFromRoute,
        ]),
        queryFn: async () => {
            const response = await organizationApi.getOrganization(organizationUuidFromRoute);
            return GetOrganizationResponseSchema.parse(response);
        },
    });

    const organizationUuid: string | undefined = org?.organization_uuid;
    const organizationHasMerchantCapacity: boolean = org?.is_merchant === true;
    const organizationHasIsoCapacity: boolean = org?.is_iso === true;

    const { data: hasSubsidiaries = false } = useQuery<boolean>({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'organization',
            organizationUuidFromRoute,
            'has-subsidiaries',
        ]),
        queryFn: async () => {
            if (organizationUuid === undefined) {
                return false;
            }
            const [merchantResponse, isoResponse] = await Promise.all([
                organizationApi.listSubsidiaryMerchantsPage(organizationUuid, { limit: 1 }),
                organizationApi.listSubsidiaryIsosPage(organizationUuid, { limit: 1 }),
            ]);
            return merchantResponse.merchants.length > 0 || isoResponse.isos.length > 0;
        },
        enabled:
            organizationUuid !== undefined
            && (organizationHasMerchantCapacity || organizationHasIsoCapacity),
    });

    // Check if user has access to this organization (for config editing)
    const { data: canEditConfigData } = useQuery({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'authorization',
            'has_access_to_organization',
            organizationUuid,
        ]),
        queryFn: async () => {
            if (organizationUuid === undefined) {
                return false;
            }
            const response = await authorizationApi.hasAccessToOrganization(organizationUuid);
            return response.has_access;
        },
        enabled: organizationUuid !== undefined,
    });

    const canEditConfig: boolean = canManageOrganizations && (canEditConfigData ?? false);
    const { data: organizationConfigData } = useQuery({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'organization',
            organizationUuidFromRoute,
            'org-config',
        ]),
        queryFn: async () => {
            return await sambaConfigApi.getOrgConfig(organizationUuidFromRoute);
        },
        enabled: organizationHasMerchantCapacity,
        retry: 0,
    });
    const { data: organizationAllowedWebsitesData } =
        useQuery<ListOrganizationAllowedWebsitesResponse>({
            queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                'organization',
                organizationUuidFromRoute,
                'allowed-websites',
            ]),
            queryFn: async () => {
                return await allowedWebsiteTemplatesApi.listOrganizationWebsites(
                    organizationUuidFromRoute
                );
            },
            enabled: organizationHasMerchantCapacity,
            retry: 0,
        });
    const organizationAllowedWebsiteRows: readonly OrganizationAllowedWebsite[] =
        organizationAllowedWebsitesData?.websites ?? [];
    const canCreateUnmanagedAllowedWebsite: boolean =
        organizationAllowedWebsitesData?.capabilities.can_create_local === true ||
        organizationAllowedWebsitesData?.capabilities.can_reference_template === true;
    const securityAllowedWebsiteDisplayRows = useMemo(
        () =>
            buildSecurityAllowedWebsiteDisplayRows(
                organizationAllowedWebsiteRows
            ),
        [organizationAllowedWebsiteRows]
    );
    const resetOrganizationUsersImportDialog = (): void => {
        setShowImportUsersDialog(false);
        setOrganizationUsersImportCsvText('');
        setOrganizationUsersImportFileName(null);
        setOrganizationUsersImportPreview(null);
        setSelectedOrganizationUsersImportRowIndexes([]);
        setOrganizationUsersImportError(null);
        setOrganizationUsersImportSkipVerification(false);
    };

    const previewOrganizationUsersCsvImportMutation = useMutation({
        mutationFn: async (csvText: string) => {
            return await organizationApi.previewOrganizationUsersCsvImport(
                organizationUuidFromRoute,
                {
                    csv_text: csvText,
                }
            );
        },
        onSuccess: (response) => {
            setOrganizationUsersImportPreview(response);
            setSelectedOrganizationUsersImportRowIndexes([]);
            setOrganizationUsersImportError(null);
        },
        onError: (error) => {
            setOrganizationUsersImportPreview(null);
            setSelectedOrganizationUsersImportRowIndexes([]);
            setOrganizationUsersImportError(
                error instanceof Error ? error.message : 'Failed to preview imported users'
            );
        },
    });

    const applyOrganizationUsersCsvImportMutation = useMutation({
        mutationFn: async (selectedRowIndexes: number[]) => {
            return await organizationApi.applyOrganizationUsersCsvImport(
                organizationUuidFromRoute,
                {
                    csv_text: organizationUsersImportCsvText,
                    selected_row_indexes: selectedRowIndexes,
                    skip_verification: organizationUsersImportSkipVerification,
                }
            );
        },
        onSuccess: () => {
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'organization',
                        organizationUuidFromRoute,
                        'users',
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating organization users after CSV apply:', err);
                }
            );
            resetOrganizationUsersImportDialog();
        },
        onError: (error) => {
            setOrganizationUsersImportError(
                error instanceof Error ? error.message : 'Failed to apply imported users'
            );
        },
    });

    const assignUsersToOrganizationMutation = useMutation({
        mutationFn: async (data: {
            readonly role: Role;
            readonly user_uuids: readonly string[];
        }) => {
            if (organizationUuidFromRoute === undefined || organizationUuidFromRoute.length === 0) {
                throw new Error('Organization UUID is required');
            }
            return await userApi.assignRole({
                user_uuids: [...data.user_uuids],
                role: data.role,
                organization_uuid: organizationUuidFromRoute,
            });
        },
        onSuccess: () => {
            setShowAssignUsersDialog(false);
        },
        onSettled: () => {
            bumpUserListsVersion();
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'organization',
                        organizationUuidFromRoute,
                        'users',
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating organization users after role assignment:', err);
                }
            );
        },
    });

    const startImpersonationMutation = useMutation({
        mutationFn: authApi.startImpersonation,
        onSuccess: () => {
            setShowImpersonateConfirm(false);
            setRoleToImpersonate(null);
            runInBackground(queryClient.cancelQueries(), (err) => {
                log.error('Error cancelling queries after impersonation start', err);
            });
            runInBackground(queryClient.resetQueries({ predicate: () => true }), (err) => {
                log.error('Error resetting queries after impersonation start', err);
            });
            runInBackground(navigate('/dashboard'), (err) => {
                log.error('Error navigating to dashboard after impersonation start', err);
            });
        },
    });

    const actionableOrganizationUsersImportRows = useMemo(() => {
        return organizationUsersImportPreview?.rows.filter(
            isActionableOrganizationUserCsvImportRow
        ) ?? [];
    }, [organizationUsersImportPreview]);
    const actionableOrganizationUsersImportRowIndexes = useMemo(() => {
        return actionableOrganizationUsersImportRows.map((row) => row.row_index);
    }, [actionableOrganizationUsersImportRows]);
    const selectedOrganizationUsersImportRowIndexSet = useMemo(() => {
        return new Set(selectedOrganizationUsersImportRowIndexes);
    }, [selectedOrganizationUsersImportRowIndexes]);
    const areAllActionableOrganizationUsersImportRowsSelected =
        actionableOrganizationUsersImportRowIndexes.length > 0
        && actionableOrganizationUsersImportRowIndexes.every((rowIndex) =>
            selectedOrganizationUsersImportRowIndexSet.has(rowIndex)
        );

    const openOrganizationMemberImpersonateDialog = (
        roleAssignment: PendingOrganizationMemberImpersonationState
    ): void => {
        setRoleToImpersonate(roleAssignment);
        setShowImpersonateConfirm(true);
    };

    useEffect(() => {
        const requestedEditMode: string | null = searchParams.get('edit');
        if (requestedEditMode !== 'business-details') {
            return;
        }
        if (!canManageOrganizations || org === undefined || org.status === 'DELETED') {
            return;
        }
        setBusinessDetailsEditorError(null);
        setBusinessDetailsEditorState(buildBusinessDetailsEditorState(org.businessDetails));
        setIsEditingBusinessDetails(true);
    }, [searchParams, canManageOrganizations, org]);
    const { data: parentOrganizationData } = useQuery<GetOrganizationResponse>({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'organization',
            org?.parent_organization_uuid,
        ]),
        queryFn: async () => {
            if (!org?.parent_organization_uuid) {
                throw new Error('Parent organization UUID is required');
            }
            const response = await organizationApi.getOrganization(org.parent_organization_uuid);
            return GetOrganizationResponseSchema.parse(response);
        },
        enabled: !!org?.parent_organization_uuid,
    });
    const { data: isoOrganizationData } = useQuery<GetOrganizationResponse>({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'organization',
            org?.iso_organization_uuid,
        ]),
        queryFn: async () => {
            if (!org?.iso_organization_uuid) {
                throw new Error('ISO organization UUID is required');
            }
            const response = await organizationApi.getOrganization(org.iso_organization_uuid);
            return GetOrganizationResponseSchema.parse(response);
        },
        enabled: !!org?.iso_organization_uuid,
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<EditOrgFormInput, unknown, EditOrgFormData>({
        resolver: zodResolver(editOrgSchema),
        ...(org && { values: { name: org.name, status: org.status } }),
    });

    const updateMutation = useMutation({
        mutationFn: (data: EditOrgFormData) => {
            if (!org?.organization_uuid) {
                throw new Error('Organization UUID is required');
            }
            return organizationApi.updateOrganization(org.organization_uuid, {
                ...data,
                status: data.status,
            });
        },
        onSuccess: () => {
            bumpOrganizationListsVersion();
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'organization',
                        organizationUuidFromRoute,
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            runInBackground(
                queryClient.invalidateQueries({ queryKey: ['organizations'] }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            setIsEditing(false);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!org?.organization_uuid) {
                throw new Error('Organization UUID is required');
            }
            return organizationApi.deleteOrganization(org.organization_uuid);
        },
        onSuccess: () => {
            bumpOrganizationListsVersion();
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'organization',
                        organizationUuidFromRoute,
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            runInBackground(
                queryClient.invalidateQueries({ queryKey: ['organizations'] }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            setShowDeleteConfirm(false);
        },
    });

    const undeleteMutation = useMutation({
        mutationFn: () => {
            if (!org?.organization_uuid) {
                throw new Error('Organization UUID is required');
            }
            return organizationApi.undeleteOrganization(org.organization_uuid);
        },
        onSuccess: () => {
            bumpOrganizationListsVersion();
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'organization',
                        organizationUuidFromRoute,
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            runInBackground(
                queryClient.invalidateQueries({ queryKey: ['organizations'] }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            setShowDeleteConfirm(false);
        },
    });

    const setParentOrganizationMutation = useMutation({
        mutationFn: (parentOrganizationUuid: string | null) => {
            if (!org?.organization_uuid) {
                throw new Error('Organization UUID is required');
            }
            return organizationApi.setOrganizationParent({
                organization_uuid: org.organization_uuid,
                parent_organization_uuid: parentOrganizationUuid,
            });
        },
        onSuccess: () => {
            bumpOrganizationListsVersion();
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'organization',
                        organizationUuidFromRoute,
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'organization',
                        organizationUuidFromRoute,
                        'subsidiary_isos',
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'organization',
                        organizationUuidFromRoute,
                        'subsidiary_merchants',
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'organization',
                        organizationUuidFromRoute,
                        'users',
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            runInBackground(
                queryClient.invalidateQueries({ queryKey: ['organizations'] }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            setIsReassigning(false);
            setShowReassignConfirm(false);
        },
    });

    const setIsoOrganizationMutation = useMutation({
        mutationFn: (isoOrganizationUuid: string | null) => {
            if (!org?.organization_uuid) {
                throw new Error('Organization UUID is required');
            }
            return organizationApi.updateOrganization(org.organization_uuid, {
                iso_organization_uuid: isoOrganizationUuid,
            });
        },
        onSuccess: () => {
            bumpOrganizationListsVersion();
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'organization',
                        organizationUuidFromRoute,
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            runInBackground(
                queryClient.invalidateQueries({ queryKey: ['organizations'] }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            setIsReassigning(false);
            setShowReassignConfirm(false);
        },
    });

    const invalidateOrganizationWebsiteConfigQueries = (): void => {
        runInBackground(
            queryClient.invalidateQueries({
                queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                    'organization',
                    organizationUuidFromRoute,
                    'org-config',
                ]),
            }),
            (err) => {
                log.error('Error invalidating org config query:', err);
            }
        );
        runInBackground(
            queryClient.invalidateQueries({
                queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                    'organization',
                    organizationUuidFromRoute,
                    'allowed-websites',
                ]),
            }),
            (err) => {
                log.error('Error invalidating organization websites query:', err);
            }
        );
    };

    const createLocalOrganizationWebsiteMutation = useMutation({
        mutationFn: async (websiteConfig: UnmanagedAllowedWebsite) => {
            if (!org?.organization_uuid) {
                throw new Error('Organization UUID is required');
            }
            return await allowedWebsiteTemplatesApi.createLocalOrganizationWebsite(
                org.organization_uuid,
                {
                    website_config: websiteConfig,
                }
            );
        },
        onSuccess: () => {
            setUnmanagedWebsiteDialogState(null);
            invalidateOrganizationWebsiteConfigQueries();
        },
        onError: (error) => {
            setUnmanagedWebsiteDialogState((currentState) =>
                currentState === null
                    ? null
                    : {
                          ...currentState,
                          error:
                              error instanceof Error
                                  ? error.message
                                  : 'Failed to create allowed website',
                      }
            );
        },
    });

    const referenceTemplateForOrganizationMutation = useMutation({
        mutationFn: async (allowedWebsiteTemplateUuid: string) => {
            if (!org?.organization_uuid) {
                throw new Error('Organization UUID is required');
            }
            return await allowedWebsiteTemplatesApi.referenceTemplateForOrganization(
                org.organization_uuid,
                {
                    allowed_website_template_uuid: allowedWebsiteTemplateUuid,
                }
            );
        },
        onSuccess: () => {
            setUnmanagedWebsiteDialogState(null);
            invalidateOrganizationWebsiteConfigQueries();
        },
        onError: (error) => {
            setUnmanagedWebsiteDialogState((currentState) =>
                currentState === null
                    ? null
                    : {
                          ...currentState,
                          error:
                              error instanceof Error
                                  ? error.message
                                  : 'Failed to reference allowed website template',
                      }
            );
        },
    });

    const updateOrganizationWebsiteMutation = useMutation({
        mutationFn: async (request: {
            readonly organizationAllowedWebsiteUuid: string;
            readonly websiteConfig: UnmanagedAllowedWebsite;
        }) => {
            if (!org?.organization_uuid) {
                throw new Error('Organization UUID is required');
            }
            return await allowedWebsiteTemplatesApi.updateOrganizationWebsite(
                org.organization_uuid,
                request.organizationAllowedWebsiteUuid,
                {
                    website_config: request.websiteConfig,
                }
            );
        },
        onSuccess: () => {
            setUnmanagedWebsiteDialogState(null);
            invalidateOrganizationWebsiteConfigQueries();
        },
        onError: (error) => {
            setUnmanagedWebsiteDialogState((currentState) =>
                currentState === null
                    ? null
                    : {
                          ...currentState,
                          error:
                              error instanceof Error
                                  ? error.message
                                  : 'Failed to update allowed website',
                      }
            );
        },
    });

    const unlinkOrganizationWebsiteMutation = useMutation({
        mutationFn: async (organizationAllowedWebsiteUuid: string) => {
            if (!org?.organization_uuid) {
                throw new Error('Organization UUID is required');
            }
            return await allowedWebsiteTemplatesApi.deleteOrganizationWebsite(
                org.organization_uuid,
                organizationAllowedWebsiteUuid
            );
        },
        onSuccess: () => {
            setPendingUnmanagedWebsiteDelete(null);
            setOrganizationWebsiteDeleteError(null);
            invalidateOrganizationWebsiteConfigQueries();
        },
        onError: (error, organizationAllowedWebsiteUuid) => {
            setPendingUnmanagedWebsiteDelete(null);
            setOrganizationWebsiteDeleteError({
                organizationAllowedWebsiteUuid,
                message:
                    error instanceof Error
                        ? error.message
                        : 'Failed to delete allowed website',
            });
        },
    });

    const customizeTemplateReferenceMutation = useMutation({
        mutationFn: async (organizationAllowedWebsiteUuid: string) => {
            if (!org?.organization_uuid) {
                throw new Error('Organization UUID is required');
            }
            return await allowedWebsiteTemplatesApi.customizeTemplateReferenceForOrganization(
                org.organization_uuid,
                organizationAllowedWebsiteUuid
            );
        },
        onSuccess: () => {
            invalidateOrganizationWebsiteConfigQueries();
        },
    });

    const promoteOrganizationWebsiteMutation = useMutation({
        mutationFn: async (request: {
            readonly organizationAllowedWebsiteUuid: string;
            readonly systemId: ThirdPartySystemId;
            readonly templateName: string;
        }) => {
            if (!org?.organization_uuid) {
                throw new Error('Organization UUID is required');
            }
            return await allowedWebsiteTemplatesApi.promoteOrganizationWebsiteToTemplate(
                org.organization_uuid,
                {
                    organization_allowed_website_uuid:
                        request.organizationAllowedWebsiteUuid,
                    ipx_system_id: request.systemId,
                    template_name: request.templateName,
                }
            );
        },
        onSuccess: () => {
            setPromoteOrganizationWebsiteDialogState(null);
            invalidateOrganizationWebsiteConfigQueries();
        },
    });

    const updateConfigMutation = useMutation({
        mutationFn: async (updatePayload: {
            readonly enable_ipg_system_ids: IpgSystemId[];
            readonly enable_ipw_system_ids: IpwSystemId[];
            readonly ips: IpsConfig;
            readonly ipwConfigCustomizations?: Partial<
                Record<IpwSystemIdKey, IpwConfigCustomization>
            >;
            readonly ipgConfigCustomizations?: Partial<
                Record<IpgSystemIdKey, IpgConfigCustomization>
            >;
            readonly merchantPaymentMethods?: PlatformPaymentMethods;
            readonly require2FA: boolean;
            readonly customerPortalPaymentRequests?: CustomerPortalPaymentRequestsConfig;
            readonly merchantPaymentConfirmationEmailCc?: string[];
        }) => {
            if (!org?.organization_uuid) {
                throw new Error('Organization UUID is required');
            }
            const updatedOrganization = await organizationApi.updateOrganization(org.organization_uuid, {
                enable_ipg_system_ids: updatePayload.enable_ipg_system_ids,
                enable_ipw_system_ids: updatePayload.enable_ipw_system_ids,
                ips: updatePayload.ips,
                ipwConfigCustomizations: updatePayload.ipwConfigCustomizations,
                ipgConfigCustomizations: updatePayload.ipgConfigCustomizations,
                merchantPaymentMethods: updatePayload.merchantPaymentMethods,
                require2FA: updatePayload.require2FA,
                customerPortalPaymentRequests: updatePayload.customerPortalPaymentRequests,
                merchantPaymentConfirmationEmailCc:
                    updatePayload.merchantPaymentConfirmationEmailCc,
            });
            return updatedOrganization;
        },
        onSuccess: () => {
            bumpOrganizationListsVersion();
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'organization',
                        organizationUuidFromRoute,
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'organization',
                        organizationUuidFromRoute,
                        'org-config',
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating org config query:', err);
                }
            );
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'organization',
                        organizationUuidFromRoute,
                        'allowed-websites',
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating organization websites query:', err);
                }
            );
            setConfigEditorError(null);
            setPromoteOrganizationWebsiteDialogState(null);
            setEditingConfigCard(null);
        },
    });

    const updateBusinessDetailsMutation = useMutation({
        mutationFn: (businessDetails: BusinessDetails) => {
            if (!org?.organization_uuid) {
                throw new Error('Organization UUID is required');
            }
            return organizationApi.updateOrganization(org.organization_uuid, {
                businessDetails: businessDetails,
            });
        },
        onSuccess: () => {
            bumpOrganizationListsVersion();
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'organization',
                        organizationUuidFromRoute,
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating organization query after business details update:', err);
                }
            );
            runInBackground(
                queryClient.invalidateQueries({ queryKey: ['organizations'] }),
                (err) => {
                    log.error('Error invalidating organizations list after business details update:', err);
                }
            );
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'organization',
                        organizationUuidFromRoute,
                        'org-config',
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating org config query after business details update:', err);
                }
            );
            setBusinessDetailsEditorError(null);
            setIsEditingBusinessDetails(false);
        },
    });

    const exportMerchantBackupMutation = useMutation({
        mutationFn: async (mode: MerchantBackupMode) =>
            await organizationApi.exportMerchantBackup(organizationUuidFromRoute, { mode }),
        onSuccess: (response, mode) => {
            const backupPayloadJson: string = JSON.stringify(response.backup, null, 2);
            const backupFileBlob: Blob = new Blob([backupPayloadJson], {
                type: 'application/json',
            });
            const backupDownloadUrl: string = URL.createObjectURL(backupFileBlob);
            const backupDownloadAnchor: HTMLAnchorElement = document.createElement('a');
            backupDownloadAnchor.href = backupDownloadUrl;
            backupDownloadAnchor.download =
                `merchant-${mode}-backup-${organizationUuidFromRoute}.json`;
            backupDownloadAnchor.click();
            URL.revokeObjectURL(backupDownloadUrl);
        },
    });

    const handleEditConfig = (
        organization: GetOrganizationResponse | OrganizationListItem,
        card: MerchantConfigEditorCard
    ) => {
        setConfigEditorError(null);
        setEnabledIpgSystemIdsForEdit(organization.enable_ipg_system_ids ?? []);
        setEnabledIpwSystemIdsForEdit(organization.enable_ipw_system_ids ?? []);
        setDealertrackAutomaticCustomerDetailsDetectionEnabledForEdit(
            buildDealertrackAutomaticCustomerDetailsDetectionEnabledEditorState(
                organization.ips?.automaticCustomerDetailsDetectionEnabled
            )
        );
        setDealertrackCustomerDetailsDetectionButtonEnabledForEdit(
            buildDealertrackCustomerDetailsDetectionButtonEnabledEditorState(
                organization.ips?.customerDetailsDetectionButtonEnabled
            )
        );
        setAddressRequiredForCardPaymentForEdit(
            buildAddressRequiredForCardPaymentEditorState(
                organization.ips?.addressRequiredForCardPayment
            )
        );
        setRequire2FAForEdit(buildRequire2FAEditorState(organization.require2FA));
        setDealertrackReconciliationForEdit(
            buildDealertrackReconciliationEditorState(
                getOrganizationDealertrackReconciliation(organization)
            )
        );
        setMerchantPaymentMethodsForEdit(
            buildMerchantPaymentMethodsEditorState(organization.merchantPaymentMethods)
        );
        updateCustomerPortalPaymentRequestsForEdit(() =>
            buildCustomerPortalPaymentRequestsEditorState(
                organization.customerPortalPaymentRequests,
                organization.businessDetails
            )
        );
        setMerchantPaymentConfirmationEmailCcForEdit(
            buildPaymentRequestEmailCcEditorState(
                organization.merchantPaymentConfirmationEmailCc
            )
        );
        setMerchantPaymentConfirmationEmailCcInput('');
        setMerchantPaymentConfirmationEmailCcInputError(null);
        setValorMerchantConfigForEdit(
            buildValorMerchantConfigEditorState(organizationConfigData)
        );
        setIpwConfigCustomizationsForEdit(
            buildIpwConfigCustomizationEditorStateMap(organizationConfigData)
        );
        setIpgConfigCustomizationsForEdit(
            buildIpgConfigCustomizationEditorStateMap(organizationConfigData)
        );
        setAllowPromoteSessionCookieForEdit(
            getAllowPromoteSessionCookie(organizationConfigData)
        );
        setUnmanagedWebsiteDialogState(null);
        setPromoteOrganizationWebsiteDialogState(null);
        setPendingUnmanagedWebsiteDelete(null);
        setOrganizationWebsiteDeleteError(null);
        setEditingConfigCard(card);
    };

    const commitPaymentRequestEmailCcInput = (): void => {
        const trimmedInput = merchantPaymentConfirmationEmailCcInput.trim();
        if (trimmedInput.length === 0) {
            setMerchantPaymentConfirmationEmailCcInput('');
            setMerchantPaymentConfirmationEmailCcInputError(null);
            return;
        }
        const parseResult = PaymentRequestEmailCcEntrySchema.safeParse(trimmedInput);
        if (!parseResult.success) {
            setMerchantPaymentConfirmationEmailCcInputError(
                parseResult.error.issues[0]?.message ?? 'Enter a valid email address'
            );
            return;
        }
        const normalizedEmail = normalizePaymentRequestEmailCcValue(parseResult.data);
        setMerchantPaymentConfirmationEmailCcForEdit((currentEmails) => {
            if (currentEmails.includes(normalizedEmail)) {
                return currentEmails;
            }
            return [...currentEmails, normalizedEmail];
        });
        setMerchantPaymentConfirmationEmailCcInput('');
        setMerchantPaymentConfirmationEmailCcInputError(null);
    };

    const updateIpwConfigCustomizationForEdit = (
        ipwSystemIdKey: IpwSystemIdKey,
        editorState: IpwConfigCustomizationEditorState
    ): void => {
        setIpwConfigCustomizationsForEdit((currentState) => ({
            ...currentState,
            [ipwSystemIdKey]: editorState,
        }));
    };

    const updateIpgConfigCustomizationForEdit = (
        ipgSystemIdKey: IpgSystemIdKey,
        editorState: IpgConfigCustomizationEditorState
    ): void => {
        setIpgConfigCustomizationsForEdit((currentState) => ({
            ...currentState,
            [ipgSystemIdKey]: editorState,
        }));
    };

    const applyValorPresetToIpgEditorState = (valorConfig: ValorIpgConfig): void => {
        updateIpgConfigCustomizationForEdit(
            ValorIpgSystemIdKey,
            buildIpgConfigCustomizationEditorStateFromConfig(valorConfig)
        );
    };

    const applyValorPortalUrlToIpgEditorState = (portalUrl: string): void => {
        const trimmedPortalUrl = portalUrl.trim();
        if (!isValidUrl(trimmedPortalUrl)) {
            return;
        }
        const portalOrigin = new URL(trimmedPortalUrl).origin;
        setIpgConfigCustomizationsForEdit((currentState) => {
            const currentValorState = currentState[ValorIpgSystemIdKey];
            if (currentValorState === undefined) {
                return currentState;
            }
            const [firstAllowedWebsite, ...remainingAllowedWebsites] =
                currentValorState.allowedWebsites;
            return {
                ...currentState,
                [ValorIpgSystemIdKey]: {
                    ...currentValorState,
                    urlPattern: `${portalOrigin}/*`,
                    allowedWebsites:
                        firstAllowedWebsite === undefined
                            ? currentValorState.allowedWebsites
                            : [
                                  {
                                      ...firstAllowedWebsite,
                                      allowedUrlPatternsText: `${portalOrigin}/*`,
                                  },
                                  ...remainingAllowedWebsites,
                              ],
                },
            };
        });
    };

    const handleSaveConfig = () => {
        try {
            const validationIssues = getMerchantConfigEditorValidationIssues({
                enabledIpwSystemIds: enabledIpwSystemIdsForEdit,
                enabledIpgSystemIds: enabledIpgSystemIdsForEdit,
                ipwConfigCustomizations: ipwConfigCustomizationsForEdit,
                ipgConfigCustomizations: ipgConfigCustomizationsForEdit,
                dealertrackReconciliation: dealertrackReconciliationForEdit,
            });
            const firstValidationIssue = validationIssues[0];
            if (firstValidationIssue !== undefined) {
                throw new Error(
                    `${firstValidationIssue.fieldLabel}: ${firstValidationIssue.reason}`
                );
            }
            const customerPortalPaymentRequestsPayload =
                buildCustomerPortalPaymentRequestsPayloadOrThrow(
                    customerPortalPaymentRequestsForEdit
                );
            buildValorConfigForSaveOrThrow(valorMerchantConfigForEdit);
            const dealertrackIpwEditorState =
                ipwConfigCustomizationsForEdit[DealertrackIpwSystemIdKey];
            const ipwConfigCustomizationsForSave: IpwConfigCustomizationEditorStateMap = {
                ...ipwConfigCustomizationsForEdit,
                ...(dealertrackIpwEditorState !== undefined && {
                    [DealertrackIpwSystemIdKey]: {
                        ...dealertrackIpwEditorState,
                        reconciliation: dealertrackReconciliationForEdit,
                    },
                }),
            };
            const valorIpgEditorState =
                ipgConfigCustomizationsForEdit[ValorIpgSystemIdKey];
            const ipgConfigCustomizationsForSave: IpgConfigCustomizationEditorStateMap = {
                ...ipgConfigCustomizationsForEdit,
                ...(valorIpgEditorState !== undefined && {
                    [ValorIpgSystemIdKey]: {
                        ...valorIpgEditorState,
                        defaultApiUrl: valorMerchantConfigForEdit.defaultApiUrl,
                        defaultChannel: valorMerchantConfigForEdit.defaultChannel,
                    },
                }),
            };
            setConfigEditorError(null);
            updateConfigMutation.mutate({
                enable_ipg_system_ids: enabledIpgSystemIdsForEdit,
                enable_ipw_system_ids: enabledIpwSystemIdsForEdit,
                ips: {
                    automaticCustomerDetailsDetectionEnabled:
                        dealertrackAutomaticCustomerDetailsDetectionEnabledForEdit,
                    customerDetailsDetectionButtonEnabled:
                        dealertrackCustomerDetailsDetectionButtonEnabledForEdit,
                    addressRequiredForCardPayment:
                        addressRequiredForCardPaymentForEdit,
                },
                ipwConfigCustomizations: buildIpwConfigCustomizationPayloadMapOrThrow(
                    enabledIpwSystemIdsForEdit,
                    ipwConfigCustomizationsForSave
                ),
                ipgConfigCustomizations: buildIpgConfigCustomizationPayloadMapOrThrow(
                    enabledIpgSystemIdsForEdit,
                    ipgConfigCustomizationsForSave
                ),
                merchantPaymentMethods: merchantPaymentMethodsForEdit,
                require2FA: require2FAForEdit,
                customerPortalPaymentRequests: customerPortalPaymentRequestsPayload,
                merchantPaymentConfirmationEmailCc:
                    merchantPaymentConfirmationEmailCcForEdit,
            });
        } catch (error: unknown) {
            const errorMessage: string = error instanceof Error ? error.message : String(error);
            log.error('Invalid merchant configuration editor state', errorMessage);
            setConfigEditorError(errorMessage);
        }
    };

    const handleOpenAddUnmanagedWebsiteDialog = (): void => {
        setUnmanagedWebsiteDialogState({
            mode: 'add',
            organizationAllowedWebsiteUuid: null,
            selectedTemplate: null,
            editorState: buildUnmanagedWebsiteEditorState(),
            error: null,
        });
    };

    const handleSelectAddWebsiteTemplate = (
        template: AllowedWebsiteTemplateListItem
    ): void => {
        setUnmanagedWebsiteDialogState((currentState) =>
            currentState === null || currentState.mode !== 'add'
                ? currentState
                : {
                      ...currentState,
                      selectedTemplate: template,
                      editorState: buildUnmanagedWebsiteEditorState(
                          template.website_config
                      ),
                      error: null,
                  }
        );
    };

    const handleClearAddWebsiteTemplate = (): void => {
        setUnmanagedWebsiteDialogState((currentState) =>
            currentState === null || currentState.mode !== 'add'
                ? currentState
                : {
                      ...currentState,
                      selectedTemplate: null,
                      editorState: buildUnmanagedWebsiteEditorState(),
                      error: null,
                  }
        );
    };

    const handleViewAllowedWebsiteTemplateByUuid = (templateUuid: string): void => {
        runInBackground(
            navigate(`/allowed-website-templates/${templateUuid}`),
            (err) => {
                log.error('Error navigating to allowed website template:', err);
            }
        );
    };

    const handleViewAllowedWebsiteTemplate = (
        row: SecurityAllowedWebsiteDisplayRow
    ): void => {
        if (row.templateUuid === null || row.capabilities?.can_view_template !== true) {
            return;
        }
        handleViewAllowedWebsiteTemplateByUuid(row.templateUuid);
    };

    const renderTemplateProvenanceLink = (
        provenance: SecurityAllowedWebsiteTemplateProvenance
    ): ReactNode => {
        const templateUuid = provenance.templateUuid;
        if (templateUuid !== null && provenance.canViewTemplate) {
            return (
                <button
                    type="button"
                    className="font-medium text-primary underline underline-offset-2"
                    onClick={() =>
                        handleViewAllowedWebsiteTemplateByUuid(templateUuid)
                    }
                >
                    {provenance.name}
                </button>
            );
        }
        return (
            <span
                className="font-medium opacity-50"
                title="Template details are unavailable."
            >
                {provenance.name}
            </span>
        );
    };

    const handleUnlinkAllowedWebsiteTemplate = (
        row: SecurityAllowedWebsiteDisplayRow
    ): void => {
        if (row.capabilities?.can_unlink_template !== true) {
            return;
        }
        unlinkOrganizationWebsiteMutation.mutate(row.rowUuid);
    };

    const handleCustomizeAllowedWebsiteTemplate = (
        row: SecurityAllowedWebsiteDisplayRow
    ): void => {
        if (
            row.source !== OrganizationAllowedWebsiteSource.TEMPLATE ||
            row.capabilities?.can_customize_template !== true
        ) {
            return;
        }
        customizeTemplateReferenceMutation.mutate(row.rowUuid);
    };

    const handleOpenEditOrganizationWebsiteDialog = (
        row: OrganizationAllowedWebsite
    ): void => {
        if (
            row.capabilities.can_edit !== true ||
            row.organization_allowed_website_uuid === null
        ) {
            return;
        }
        setUnmanagedWebsiteDialogState({
            mode: 'edit-organization',
            organizationAllowedWebsiteUuid:
                row.organization_allowed_website_uuid,
            selectedTemplate: null,
            editorState: buildUnmanagedWebsiteEditorState(row.website_config),
            error: null,
        });
    };

    const handleOpenPromoteOrganizationWebsiteDialog = (
        row: OrganizationAllowedWebsite
    ): void => {
        if (
            row.capabilities.can_promote_to_template !== true ||
            row.organization_allowed_website_uuid === null
        ) {
            return;
        }
        setPromoteOrganizationWebsiteDialogState({
            organizationAllowedWebsiteUuid:
                row.organization_allowed_website_uuid,
            websiteName: row.website_config.name,
            templateName: row.website_config.name,
            systemId: row.website_config.system_id,
            error: null,
        });
    };

    const handleSavePromoteOrganizationWebsiteDialog = (): void => {
        if (promoteOrganizationWebsiteDialogState === null) {
            return;
        }
        const templateName =
            promoteOrganizationWebsiteDialogState.templateName.trim();
        if (templateName.length === 0) {
            setPromoteOrganizationWebsiteDialogState({
                ...promoteOrganizationWebsiteDialogState,
                error: 'Template name is required',
            });
            return;
        }
        setPromoteOrganizationWebsiteDialogState({
            ...promoteOrganizationWebsiteDialogState,
            error: null,
        });
        promoteOrganizationWebsiteMutation.mutate({
            organizationAllowedWebsiteUuid:
                promoteOrganizationWebsiteDialogState.organizationAllowedWebsiteUuid,
            systemId: promoteOrganizationWebsiteDialogState.systemId,
            templateName,
        });
    };

    const handleSaveUnmanagedWebsiteDialog = (): void => {
        if (unmanagedWebsiteDialogState === null) {
            return;
        }
        try {
            const selectedTemplate = unmanagedWebsiteDialogState.selectedTemplate;
            if (unmanagedWebsiteDialogState.mode === 'add') {
                if (selectedTemplate !== null) {
                    referenceTemplateForOrganizationMutation.mutate(
                        selectedTemplate.allowed_website_template_uuid
                    );
                    return;
                }
                createLocalOrganizationWebsiteMutation.mutate(
                    buildUnmanagedWebsitePayloadOrThrow(
                        unmanagedWebsiteDialogState.editorState
                    )
                );
                return;
            }

            if (unmanagedWebsiteDialogState.mode === 'edit-organization') {
                const organizationAllowedWebsiteUuid =
                    unmanagedWebsiteDialogState.organizationAllowedWebsiteUuid;
                if (organizationAllowedWebsiteUuid === null) {
                    throw new Error('Organization website UUID is required for edit');
                }
                updateOrganizationWebsiteMutation.mutate({
                    organizationAllowedWebsiteUuid,
                    websiteConfig: buildUnmanagedWebsitePayloadOrThrow(
                        unmanagedWebsiteDialogState.editorState
                    ),
                });
                return;
            }
        } catch (error: unknown) {
            setUnmanagedWebsiteDialogState({
                ...unmanagedWebsiteDialogState,
                error: error instanceof Error ? error.message : String(error),
            });
        }
    };

    const handleConfirmDeleteUnmanagedWebsite = (): void => {
        if (pendingUnmanagedWebsiteDelete === null) {
            return;
        }
        unlinkOrganizationWebsiteMutation.mutate(
            pendingUnmanagedWebsiteDelete.organizationAllowedWebsiteUuid
        );
    };

    const handleCancelConfig = (organization: GetOrganizationResponse | OrganizationListItem) => {
        setConfigEditorError(null);
        setEnabledIpgSystemIdsForEdit(organization.enable_ipg_system_ids ?? []);
        setEnabledIpwSystemIdsForEdit(organization.enable_ipw_system_ids ?? []);
        setDealertrackAutomaticCustomerDetailsDetectionEnabledForEdit(
            buildDealertrackAutomaticCustomerDetailsDetectionEnabledEditorState(
                organization.ips?.automaticCustomerDetailsDetectionEnabled
            )
        );
        setDealertrackCustomerDetailsDetectionButtonEnabledForEdit(
            buildDealertrackCustomerDetailsDetectionButtonEnabledEditorState(
                organization.ips?.customerDetailsDetectionButtonEnabled
            )
        );
        setAddressRequiredForCardPaymentForEdit(
            buildAddressRequiredForCardPaymentEditorState(
                organization.ips?.addressRequiredForCardPayment
            )
        );
        setRequire2FAForEdit(buildRequire2FAEditorState(organization.require2FA));
        setDealertrackReconciliationForEdit(
            buildDealertrackReconciliationEditorState(
                getOrganizationDealertrackReconciliation(organization)
            )
        );
        setMerchantPaymentMethodsForEdit(
            buildMerchantPaymentMethodsEditorState(organization.merchantPaymentMethods)
        );
        updateCustomerPortalPaymentRequestsForEdit(() =>
            buildCustomerPortalPaymentRequestsEditorState(
                organization.customerPortalPaymentRequests,
                organization.businessDetails
            )
        );
        setMerchantPaymentConfirmationEmailCcForEdit(
            buildPaymentRequestEmailCcEditorState(
                organization.merchantPaymentConfirmationEmailCc
            )
        );
        setMerchantPaymentConfirmationEmailCcInput('');
        setMerchantPaymentConfirmationEmailCcInputError(null);
        setValorMerchantConfigForEdit(
            buildValorMerchantConfigEditorState(organizationConfigData)
        );
        setIpwConfigCustomizationsForEdit(
            buildIpwConfigCustomizationEditorStateMap(organizationConfigData)
        );
        setIpgConfigCustomizationsForEdit(
            buildIpgConfigCustomizationEditorStateMap(organizationConfigData)
        );
        setUnmanagedWebsiteDialogState(null);
        setPromoteOrganizationWebsiteDialogState(null);
        setPendingUnmanagedWebsiteDelete(null);
        setOrganizationWebsiteDeleteError(null);
        setEditingConfigCard(null);
    };

    const handleEditBusinessDetails = (
        organization: GetOrganizationResponse | OrganizationListItem
    ): void => {
        setBusinessDetailsEditorError(null);
        setBusinessDetailsEditorState(buildBusinessDetailsEditorState(organization.businessDetails));
        setIsEditingBusinessDetails(true);
    };

    const handleCancelBusinessDetails = (
        organization: GetOrganizationResponse | OrganizationListItem
    ): void => {
        setBusinessDetailsEditorError(null);
        setBusinessDetailsEditorState(buildBusinessDetailsEditorState(organization.businessDetails));
        setIsEditingBusinessDetails(false);
    };

    const handleSaveBusinessDetails = (): void => {
        try {
            const businessDetailsPayload: BusinessDetails =
                buildBusinessDetailsPayloadOrThrow(businessDetailsEditorState);
            setBusinessDetailsEditorError(null);
            updateBusinessDetailsMutation.mutate(businessDetailsPayload);
        } catch (error: unknown) {
            const errorMessage: string = error instanceof Error ? error.message : String(error);
            log.error('Invalid business details editor state', errorMessage);
            setBusinessDetailsEditorError(errorMessage);
        }
    };

    const isBusinessDetailsSaveEnabled: boolean = useMemo((): boolean => {
        try {
            buildBusinessDetailsPayloadOrThrow(businessDetailsEditorState);
            return true;
        } catch {
            return false;
        }
    }, [businessDetailsEditorState]);

    const isUnmanagedWebsiteDialogMutationPending: boolean =
        createLocalOrganizationWebsiteMutation.isPending ||
        referenceTemplateForOrganizationMutation.isPending ||
        updateOrganizationWebsiteMutation.isPending;
    const isUnmanagedWebsiteTemplateSelected: boolean =
        unmanagedWebsiteDialogState?.selectedTemplate !== null &&
        unmanagedWebsiteDialogState?.selectedTemplate !== undefined;
    const isUnmanagedWebsiteDialogSaveDisabled: boolean =
        unmanagedWebsiteDialogState === null ||
        isUnmanagedWebsiteDialogMutationPending ||
        (!isUnmanagedWebsiteTemplateSelected &&
            getUnmanagedWebsiteEditorValidationMessage(
                unmanagedWebsiteDialogState.editorState
            ) !== null);

    if (isLoading) {
        return <div>Loading organization...</div>;
    }

    if (error || !org) {
        return <div className="text-red-600">Error loading organization: {String(error)}</div>;
    }

    const organizationView: OrganizationView = getOrganizationView(
        org,
        searchParams.get('view')
    );
    const highlightedCredentialUuid: string | undefined = (() => {
        const credentialUuid = searchParams.get('credential_uuid');
        return credentialUuid === null ? undefined : credentialUuid;
    })();
    const shouldScrollToCredentials: boolean =
        searchParams.get('section') === 'credentials' || highlightedCredentialUuid !== undefined;
    const orgIsIso: boolean = isIsoOrganization(org);
    const orgIsMerchant: boolean = isMerchantOrganization(org);
    const organizationUserCreatePath: string =
        `${orgIsIso ? '/iso-users/create' : '/users/create'}?organization_uuid=${encodeURIComponent(
            organizationUuidFromRoute
        )}`;
    const hierarchyParentIsIso: boolean = isIsoOrganization(parentOrganizationData);
    const parentIsMerchant: boolean = isMerchantOrganization(parentOrganizationData);
    const ipgSystemOptions: ReadonlyArray<MerchantSystemOption<IpgSystemId>> = getIpgSystemOptions();
    const ipwSystemOptions: ReadonlyArray<MerchantSystemOption<IpwSystemId>> = getIpwSystemOptions();
    const readOnlyValorMerchantConfig: ValorMerchantConfigEditorState =
        buildValorMerchantConfigEditorState(organizationConfigData);
    const hasReadOnlyValorConfig: boolean =
        (org.enable_ipg_system_ids ?? []).includes(ValorIpgSystemId) ||
        organizationConfigData?.ipg[ValorIpgSystemIdKey] !== undefined;
    const readOnlyIpgPolicySummaries = appendUniqueSystemId(
        org.enable_ipg_system_ids ?? [],
        ValorIpgSystemId
    ).map((ipgSystemId) => {
        const ipgSystemIdKey = getIpgSystemIdKeyOrThrow(ipgSystemId);
        const effectiveIpgConfig = getEffectiveIpgConfig(organizationConfigData, ipgSystemIdKey);
        return {
            name: getSystemOptionLabelOrThrow(ipgSystemOptions, ipgSystemId),
            urlPattern: effectiveIpgConfig.urlPattern,
            allowedWebsites: effectiveIpgConfig.allowedWebsites,
        };
    });
    const readOnlyIpwPolicySummaries = (org.enable_ipw_system_ids ?? []).map((ipwSystemId) => {
        const ipwSystemIdKey = getIpwSystemIdKeyOrThrow(ipwSystemId);
        const effectiveIpwConfig = getEffectiveIpwConfig(organizationConfigData, ipwSystemIdKey);
        return {
            name: getSystemOptionLabelOrThrow(ipwSystemOptions, ipwSystemId),
            urlPattern: effectiveIpwConfig.urlPattern,
            allowedWebsites: effectiveIpwConfig.allowedWebsites,
        };
    });
    const configEditorValidationIssues = getMerchantConfigEditorValidationIssues({
        enabledIpwSystemIds: enabledIpwSystemIdsForEdit,
        enabledIpgSystemIds: enabledIpgSystemIdsForEdit,
        ipwConfigCustomizations: ipwConfigCustomizationsForEdit,
        ipgConfigCustomizations: ipgConfigCustomizationsForEdit,
        dealertrackReconciliation: dealertrackReconciliationForEdit,
    });
    const isConfigSaveDisabled: boolean =
        updateConfigMutation.isPending ||
        configEditorValidationIssues.length > 0;
    const renderMerchantConfigCardActions = (
        card: MerchantConfigEditorCard,
        organization: GetOrganizationResponse | OrganizationListItem
    ): ReactNode => {
        if (editingConfigCard !== card) {
            if (editingConfigCard !== null) {
                const editingCardLabel = MERCHANT_CONFIG_EDITOR_CARD_LABELS[editingConfigCard];
                return (
                    <span
                        title={`Finish editing ${editingCardLabel} before editing another section.`}
                    >
                        <Button disabled size="sm">
                            Edit
                        </Button>
                    </span>
                );
            }
            return (
                <Button onClick={() => handleEditConfig(organization, card)} size="sm">
                    Edit
                </Button>
            );
        }
        return (
            <div className="flex items-center gap-2">
                <Button
                    onClick={handleSaveConfig}
                    disabled={isConfigSaveDisabled}
                    size="sm"
                >
                    {updateConfigMutation.isPending ? 'Saving...' : 'Save'}
                </Button>
                <Button
                    variant="outline"
                    onClick={() => handleCancelConfig(organization)}
                    size="sm"
                >
                    Cancel
                </Button>
            </div>
        );
    };
    const renderMerchantConfigEditorAlerts = (): ReactNode => (
        <>
            {renderMerchantConfigValidationIssueAlert(configEditorValidationIssues)}
            {configEditorError !== null && (
                <Alert variant="destructive">
                    <AlertDescription>{configEditorError}</AlertDescription>
                </Alert>
            )}
            {updateConfigMutation.error && (
                <Alert variant="destructive">
                    <AlertDescription>
                        {updateConfigMutation.error instanceof Error
                            ? updateConfigMutation.error.message
                            : 'Failed to update configuration'}
                    </AlertDescription>
                </Alert>
            )}
        </>
    );

    const onSubmit = (data: EditOrgFormData) => {
        updateMutation.mutate(data);
    };

    const handleDelete = () => {
        setShowDeleteConfirm(true);
    };

    const handleConfirmDelete = () => {
        if (org?.status === 'DELETED') {
            undeleteMutation.mutate();
        } else {
            deleteMutation.mutate();
        }
    };

    const handleOrganizationUsersImportFileSelected = async (file: File): Promise<void> => {
        const csvText = await file.text();
        setOrganizationUsersImportCsvText(csvText);
        setOrganizationUsersImportFileName(file.name);
        setOrganizationUsersImportError(null);
        await previewOrganizationUsersCsvImportMutation.mutateAsync(csvText);
    };

    const toggleOrganizationUsersImportRowSelection = (rowIndex: number): void => {
        setSelectedOrganizationUsersImportRowIndexes((currentRowIndexes) =>
            currentRowIndexes.includes(rowIndex)
                ? currentRowIndexes.filter((currentRowIndex) => currentRowIndex !== rowIndex)
                : [...currentRowIndexes, rowIndex].sort((left, right) => left - right)
        );
    };

    const handleToggleAllOrganizationUsersImportRows = (checked: boolean): void => {
        setSelectedOrganizationUsersImportRowIndexes(
            checked ? actionableOrganizationUsersImportRowIndexes : []
        );
    };

    const beginAssignParentOrganizationFromItem: (
        parentOrg: OrganizationOmnisearchListItem,
        parentOrgType: ParentOrganizationType
    ) => void = (
        parentOrg: OrganizationOmnisearchListItem,
        parentOrgType: ParentOrganizationType
    ) => {
        if (organizationUuid === undefined) {
            throw new Error('Organization UUID is required');
        }
        if (parentOrg.organization_uuid === organizationUuid) {
            throw new Error('Organization cannot be its own parent');
        }
        setPendingParentOrganizationUuid(parentOrg.organization_uuid);
        setPendingParentOrganizationName(parentOrg.name);
        setPendingParentOrganizationType(parentOrgType);
        setPendingParentAction('assign');
        setShowReassignConfirm(true);
    };

    const beginRevokeParentOrganization: (
        currentParentOrgName: string,
        currentParentOrgType: ParentOrganizationType
    ) => void = (
        currentParentOrgName: string,
        currentParentOrgType: ParentOrganizationType
    ) => {
        setPendingParentOrganizationUuid(null);
        setPendingParentOrganizationName(currentParentOrgName);
        setPendingParentOrganizationType(currentParentOrgType);
        setPendingParentAction('revoke');
        setShowReassignConfirm(true);
    };

    const handleConfirmReassign: () => void = () => {
        if (pendingParentAction === null) {
            throw new Error('No pending parent organization action to confirm');
        }

        if (orgIsMerchant && pendingParentOrganizationType === 'ISO') {
            const nextIsoOrganizationUuid =
                pendingParentAction === 'revoke' ? null : pendingParentOrganizationUuid;
            if (pendingParentAction === 'assign' && nextIsoOrganizationUuid === null) {
                throw new Error('Missing pending ISO organization UUID for assignment');
            }
            setIsoOrganizationMutation.mutate(nextIsoOrganizationUuid);
            setPendingParentAction(null);
            return;
        }

        if (pendingParentAction === 'revoke') {
            setParentOrganizationMutation.mutate(null);
            setPendingParentAction(null);
            return;
        }

        if (pendingParentOrganizationUuid === null) {
            throw new Error('Missing pending parent organization UUID for assignment');
        }

        setParentOrganizationMutation.mutate(pendingParentOrganizationUuid);
        setPendingParentAction(null);
    };

    const buildParentOrganizationConfirmationMessage: () => string = () => {
        if (pendingParentAction === null) {
            throw new Error('Missing pending parent action for confirmation message');
        }
        if (pendingParentOrganizationName === null || pendingParentOrganizationType === null) {
            throw new Error('Missing pending parent organization details for confirmation message');
        }

        if (pendingParentAction === 'revoke') {
            if (orgIsMerchant && pendingParentOrganizationType === 'ISO') {
                return `Revoking the assignment of ${org.name} to ISO ${pendingParentOrganizationName}.`;
            }
            if (orgIsIso && pendingParentOrganizationType === 'ISO') {
                return `Revoking the assignment of ${org.name} to parent ISO ${pendingParentOrganizationName}.`;
            }
            if (orgIsMerchant && pendingParentOrganizationType === 'MERCHANT') {
                return `Revoking the assignment of ${org.name} to parent merchant ${pendingParentOrganizationName}.`;
            }
            throw new Error(
                `Unsupported revoke combination: organization=${getOrganizationCapacityLabel(org)}, parent.type=${pendingParentOrganizationType}`
            );
        }

        if (pendingParentAction === 'assign') {
            if (orgIsMerchant && pendingParentOrganizationType === 'ISO') {
                return `Assign ${org.name} to ISO ${pendingParentOrganizationName}?`;
            }
            if (orgIsIso && pendingParentOrganizationType === 'ISO') {
                return `Assign ${org.name} to parent ISO ${pendingParentOrganizationName}?`;
            }
            if (orgIsMerchant && pendingParentOrganizationType === 'MERCHANT') {
                return `Assign ${org.name} to parent merchant ${pendingParentOrganizationName}?`;
            }
            throw new Error(
                `Unsupported assign combination: organization=${getOrganizationCapacityLabel(org)}, parent.type=${pendingParentOrganizationType}`
            );
        }

        throw new Error(`Unknown pendingParentAction: ${pendingParentAction}`);
    };

    const parentOrganizationReassignConfirmationState: ParentOrganizationReassignConfirmationState =
        {
            showReassignConfirm,
            pendingParentAction,
            pendingParentOrganizationName,
            pendingParentOrganizationType,
        };
    const isParentOrganizationReassignConfirmationModalOpen: boolean =
        getIsParentOrganizationReassignConfirmationModalOpen(
            parentOrganizationReassignConfirmationState
        );
    const isInternalAdminRole: boolean = activeRoleAssignment?.role === 'INTERNAL_ADMIN';
    const isSuperadminRole: boolean = activeRoleAssignment?.role === 'SUPERADMIN';
    const canRenderMerchantBackupCard: boolean =
        orgIsMerchant && (isInternalAdminRole || isSuperadminRole);
    const isMerchantSubsidiary: boolean =
        org.parent_organization_uuid !== null && parentIsMerchant;
    const merchantBackupButtonTooltip: string =
        isMerchantSubsidiary ? 'Cannot export a subsidiary' : '';
    const isTopLevelMerchantOrganization: boolean = orgIsMerchant && !isMerchantSubsidiary;
    const businessDetailsSummary: string =
        org.businessDetails === undefined
            ? 'No business details configured.'
            : `${org.businessDetails.country} | ${org.businessDetails.legalEntityName}`;
    const isMainPhoneMissingForReceipt: boolean =
        org.businessDetails === undefined || isBlankOptionalField(org.businessDetails.main_phone_number);
    const isStreetMissingForReceipt: boolean =
        org.businessDetails === undefined ||
        isBlankOptionalField(org.businessDetails.address?.street);
    const isCityMissingForReceipt: boolean =
        org.businessDetails === undefined || isBlankOptionalField(org.businessDetails.address?.city);
    const isUsStateMissingForReceipt: boolean =
        org.businessDetails !== undefined && org.businessDetails.country === 'US'
            ? isBlankOptionalField(org.businessDetails.address?.state)
            : false;
    const isUsZipMissingForReceipt: boolean =
        org.businessDetails !== undefined && org.businessDetails.country === 'US'
            ? isBlankOptionalField(org.businessDetails.address?.zip)
            : false;
    const isCaProvinceMissingForReceipt: boolean =
        org.businessDetails !== undefined && org.businessDetails.country === 'CA'
            ? isBlankOptionalField(org.businessDetails.address?.province)
            : false;
    const isCaPostalCodeMissingForReceipt: boolean =
        org.businessDetails !== undefined && org.businessDetails.country === 'CA'
            ? isBlankOptionalField(org.businessDetails.address?.postal_code)
            : false;
    const organizationConfigurationTitle: string =
        organizationView === 'MERCHANT' ? 'Merchant Configuration' : 'ISO configuration';
    const genericWebsiteCredentialOptions = getGenericWebsiteCredentialOptionWebsites(
        organizationAllowedWebsiteRows
    ).map((website) => ({
        value: website.name,
        label: website.name,
    }));

    return (
        <div>
            <BackButton
                href={`/organizations?view=${searchParams.get('view') ?? 'iso'}`}
                tooltip="Back to Organizations"
            />

            <div className="flex justify-between items-start mb-6">
                <div>
                    <div className="flex items-center gap-2">
                        <h1 className="text-3xl font-bold">{org.name}</h1>
                        <StatusBadge status={org.status} />
                    </div>
                </div>
                <div className="flex gap-2">
                    {isTopLevelMerchantOrganization && (
                        <Button
                            variant="outline"
                            onClick={() => {
                                runInBackground(
                                    navigate(
                                        `/organizations/${organizationUuidFromRoute}/merchant-checklist`
                                    ),
                                    (err) => {
                                        log.error('Error navigating to merchant checklist page:', err);
                                    }
                                );
                            }}
                        >
                            Open Checklist
                        </Button>
                    )}
                    {!isEditing && org.status !== 'DELETED' && (
                        <Button
                            onClick={() => {
                                setIsEditing(true);
                            }}
                        >
                            Edit
                        </Button>
                    )}
                </div>
            </div>

            {deleteMutation.error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded text-red-800">
                    Error deleting organization: {String(deleteMutation.error)}
                </div>
            )}

            {isEditing && (
                <Card id="business-details-card" className="mb-6">
                    <CardHeader>
                        <CardTitle>Edit Organization</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                            <div>
                                <Label htmlFor="name">Name</Label>
                                <Input id="name" {...register('name')} />
                                {errors.name && (
                                    <p className="text-red-600 text-sm mt-1">
                                        {errors.name.message}
                                    </p>
                                )}
                            </div>

                            <div>
                                <Label htmlFor="status">Status</Label>
                                <select
                                    id="status"
                                    {...register('status')}
                                    className="w-full border rounded px-3 py-2"
                                >
                                    <option value="ACTIVE">Active</option>
                                    <option value="SUSPENDED">Suspended</option>
                                    <option value="DELETED">Deleted</option>
                                </select>
                                {errors.status && (
                                    <p className="text-red-600 text-sm mt-1">
                                        {errors.status.message}
                                    </p>
                                )}
                            </div>

                            {updateMutation.error && (
                                <div className="text-red-600 text-sm">
                                    Error: {String(updateMutation.error)}
                                </div>
                            )}

                            <div className="flex gap-2">
                                <Button type="submit" disabled={updateMutation.isPending}>
                                    {updateMutation.isPending ? 'Saving...' : 'Save'}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        reset();
                                        setIsEditing(false);
                                    }}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            )}

            {/* Organization Configuration Card */}
            {orgIsMerchant && (
                <Card className="mb-6">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>Business Details</CardTitle>
                        {canManageOrganizations && org.status !== 'DELETED' && (
                            <Button
                                size="sm"
                                variant={isEditingBusinessDetails ? 'outline' : 'default'}
                                onClick={() => {
                                    if (isEditingBusinessDetails) {
                                        handleCancelBusinessDetails(org);
                                    } else {
                                        handleEditBusinessDetails(org);
                                    }
                                }}
                            >
                                {isEditingBusinessDetails ? 'Cancel' : 'Edit'}
                            </Button>
                        )}
                    </CardHeader>
                    <CardContent className="space-y-3">
                        {!isEditingBusinessDetails ? (
                            org.businessDetails === undefined ? (
                                <p className="text-sm text-muted-foreground">
                                    No business details configured.
                                </p>
                            ) : (
                                <div className="space-y-1">
                                    <div className="text-sm">
                                        <span className="text-muted-foreground">Country:</span>{' '}
                                        {org.businessDetails.country}
                                    </div>
                                    <div className="text-sm">
                                        <span className="text-muted-foreground">Legal entity:</span>{' '}
                                        {org.businessDetails.legalEntityName}
                                    </div>
                                    {org.businessDetails.dba !== undefined && (
                                        <div className="text-sm">
                                            <span className="text-muted-foreground">DBA:</span>{' '}
                                            {org.businessDetails.dba}
                                        </div>
                                    )}
                                    {org.businessDetails.department !== undefined && (
                                        <div className="text-sm">
                                            <span className="text-muted-foreground">Department:</span>{' '}
                                            {org.businessDetails.department}
                                        </div>
                                    )}
                                    <div className="text-sm">
                                        <span className="text-muted-foreground">
                                            {renderOptionalReceiptFieldLabel(
                                                'Main phone:',
                                                isMainPhoneMissingForReceipt
                                            )}
                                        </span>{' '}
                                        {formatOptionalFieldValue(
                                            org.businessDetails.main_phone_number
                                        )}
                                    </div>
                                    <div className="text-sm">
                                        <span className="text-muted-foreground">
                                            {renderOptionalReceiptFieldLabel(
                                                'Street:',
                                                isStreetMissingForReceipt
                                            )}
                                        </span>{' '}
                                        {formatOptionalFieldValue(
                                            org.businessDetails.address?.street
                                        )}
                                    </div>
                                    {org.businessDetails.address?.street2 !== undefined && (
                                        <div className="text-sm">
                                            <span className="text-muted-foreground">Street 2:</span>{' '}
                                            {org.businessDetails.address.street2}
                                        </div>
                                    )}
                                    <div className="text-sm">
                                        <span className="text-muted-foreground">
                                            {renderOptionalReceiptFieldLabel(
                                                'City:',
                                                isCityMissingForReceipt
                                            )}
                                        </span>{' '}
                                        {formatOptionalFieldValue(org.businessDetails.address?.city)}
                                    </div>
                                    {org.businessDetails.country === 'US' && (
                                        <>
                                            <div className="text-sm">
                                                <span className="text-muted-foreground">
                                                    {renderOptionalReceiptFieldLabel(
                                                        'State:',
                                                        isUsStateMissingForReceipt
                                                    )}
                                                </span>{' '}
                                                {formatOptionalFieldValue(
                                                    org.businessDetails.address?.state
                                                )}
                                            </div>
                                            <div className="text-sm">
                                                <span className="text-muted-foreground">
                                                    {renderOptionalReceiptFieldLabel(
                                                        'ZIP:',
                                                        isUsZipMissingForReceipt
                                                    )}
                                                </span>{' '}
                                                {formatOptionalFieldValue(
                                                    org.businessDetails.address?.zip
                                                )}
                                            </div>
                                        </>
                                    )}
                                    {org.businessDetails.country === 'CA' && (
                                        <>
                                            <div className="text-sm">
                                                <span className="text-muted-foreground">
                                                    {renderOptionalReceiptFieldLabel(
                                                        'Province:',
                                                        isCaProvinceMissingForReceipt
                                                    )}
                                                </span>{' '}
                                                {formatOptionalFieldValue(
                                                    org.businessDetails.address?.province
                                                )}
                                            </div>
                                            <div className="text-sm">
                                                <span className="text-muted-foreground">
                                                    {renderOptionalReceiptFieldLabel(
                                                        'Postal code:',
                                                        isCaPostalCodeMissingForReceipt
                                                    )}
                                                </span>{' '}
                                                {formatOptionalFieldValue(
                                                    org.businessDetails.address?.postal_code
                                                )}
                                            </div>
                                        </>
                                    )}
                                    <div className="text-sm">
                                        <span className="text-muted-foreground">Timezone:</span>{' '}
                                        {getOrganizationTimezoneDisplayValue(org)}
                                    </div>
                                </div>
                            )
                        ) : (
                            <div className="space-y-3">
                                <div>
                                    <Label htmlFor="business_details_country">Country</Label>
                                    <select
                                        id="business_details_country"
                                        className="w-full border rounded px-3 py-2"
                                        value={businessDetailsEditorState.country}
                                        onChange={(event) => {
                                            const selectedCountry: 'US' | 'CA' =
                                                event.target.value === 'CA' ? 'CA' : 'US';
                                            setBusinessDetailsEditorState({
                                                ...businessDetailsEditorState,
                                                country: selectedCountry,
                                            });
                                        }}
                                    >
                                        <option value="US">US</option>
                                        <option value="CA">CA</option>
                                    </select>
                                </div>
                                <div>
                                    <Label htmlFor="business_details_legal_entity_name">
                                        Legal entity name
                                    </Label>
                                    <Input
                                        id="business_details_legal_entity_name"
                                        value={businessDetailsEditorState.legalEntityName}
                                        onChange={(event) => {
                                            setBusinessDetailsEditorState({
                                                ...businessDetailsEditorState,
                                                legalEntityName: event.target.value,
                                            });
                                        }}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="business_details_dba">DBA</Label>
                                    <Input
                                        id="business_details_dba"
                                        value={businessDetailsEditorState.dba}
                                        onChange={(event) => {
                                            setBusinessDetailsEditorState({
                                                ...businessDetailsEditorState,
                                                dba: event.target.value,
                                            });
                                        }}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="business_details_department">Department</Label>
                                    <Input
                                        id="business_details_department"
                                        value={businessDetailsEditorState.department}
                                        onChange={(event) => {
                                            setBusinessDetailsEditorState({
                                                ...businessDetailsEditorState,
                                                department: event.target.value,
                                            });
                                        }}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="business_details_main_phone_number">
                                        {renderOptionalReceiptFieldLabel(
                                            'Main phone number',
                                            normalizePhoneNumber(
                                                businessDetailsEditorState.mainPhoneNumber
                                            ).length === 0
                                        )}
                                    </Label>
                                    <Input
                                        id="business_details_main_phone_number"
                                        value={businessDetailsEditorState.mainPhoneNumber}
                                        onChange={(event) => {
                                            setBusinessDetailsEditorState({
                                                ...businessDetailsEditorState,
                                                mainPhoneNumber: formatPhoneNumber(event.target.value),
                                            });
                                        }}
                                    />
                                </div>
                                {businessDetailsEditorState.country === 'US' && (
                                    <div className="space-y-3 rounded border p-3">
                                        <div>
                                            <Label htmlFor="business_details_street">
                                                {renderOptionalReceiptFieldLabel(
                                                    'Street',
                                                    businessDetailsEditorState.street.trim()
                                                        .length === 0
                                                )}
                                            </Label>
                                            <Input
                                                id="business_details_street"
                                                value={businessDetailsEditorState.street}
                                                onChange={(event) => {
                                                    setBusinessDetailsEditorState({
                                                        ...businessDetailsEditorState,
                                                        street: event.target.value,
                                                    });
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="business_details_street2">Street 2</Label>
                                            <Input
                                                id="business_details_street2"
                                                value={businessDetailsEditorState.street2}
                                                onChange={(event) => {
                                                    setBusinessDetailsEditorState({
                                                        ...businessDetailsEditorState,
                                                        street2: event.target.value,
                                                    });
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="business_details_city">
                                                {renderOptionalReceiptFieldLabel(
                                                    'City',
                                                    businessDetailsEditorState.city.trim().length ===
                                                        0
                                                )}
                                            </Label>
                                            <Input
                                                id="business_details_city"
                                                value={businessDetailsEditorState.city}
                                                onChange={(event) => {
                                                    setBusinessDetailsEditorState({
                                                        ...businessDetailsEditorState,
                                                        city: event.target.value,
                                                    });
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="business_details_state">
                                                {renderOptionalReceiptFieldLabel(
                                                    'State',
                                                    businessDetailsEditorState.usState === ''
                                                )}
                                            </Label>
                                            <select
                                                id="business_details_state"
                                                className="w-full border rounded px-3 py-2"
                                                value={businessDetailsEditorState.usState}
                                                onChange={(event) => {
                                                    const selectedStateCandidate: USState | '' | undefined =
                                                        event.target.value === ''
                                                            ? ''
                                                            : undefined;
                                                    if (selectedStateCandidate === '') {
                                                        setBusinessDetailsEditorState({
                                                            ...businessDetailsEditorState,
                                                            usState: selectedStateCandidate,
                                                        });
                                                        return;
                                                    }
                                                    const selectedUsStateCandidate: USState | undefined =
                                                        USStateKeyList.find(
                                                            (usStateKey) =>
                                                                usStateKey === event.target.value
                                                        );
                                                    if (selectedUsStateCandidate === undefined) {
                                                        throw new Error(
                                                            `Invalid US state selected: ${event.target.value}`
                                                        );
                                                    }
                                                    setBusinessDetailsEditorState({
                                                        ...businessDetailsEditorState,
                                                        usState: selectedUsStateCandidate,
                                                    });
                                                }}
                                            >
                                                <option value="">Unknown</option>
                                                {USStateKeyList.map((usState) => (
                                                    <option key={usState} value={usState}>
                                                        {usState}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <Label htmlFor="business_details_zip">
                                                {renderOptionalReceiptFieldLabel(
                                                    'ZIP',
                                                    businessDetailsEditorState.usZip.trim().length ===
                                                        0
                                                )}
                                            </Label>
                                            <Input
                                                id="business_details_zip"
                                                value={businessDetailsEditorState.usZip}
                                                onChange={(event) => {
                                                    setBusinessDetailsEditorState({
                                                        ...businessDetailsEditorState,
                                                        usZip: normalizeUSZipCode(event.target.value),
                                                    });
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}
                                {businessDetailsEditorState.country === 'CA' && (
                                    <div className="space-y-3 rounded border p-3">
                                        <div>
                                            <Label htmlFor="business_details_street">
                                                {renderOptionalReceiptFieldLabel(
                                                    'Street',
                                                    businessDetailsEditorState.street.trim()
                                                        .length === 0
                                                )}
                                            </Label>
                                            <Input
                                                id="business_details_street"
                                                value={businessDetailsEditorState.street}
                                                onChange={(event) => {
                                                    setBusinessDetailsEditorState({
                                                        ...businessDetailsEditorState,
                                                        street: event.target.value,
                                                    });
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="business_details_street2">Street 2</Label>
                                            <Input
                                                id="business_details_street2"
                                                value={businessDetailsEditorState.street2}
                                                onChange={(event) => {
                                                    setBusinessDetailsEditorState({
                                                        ...businessDetailsEditorState,
                                                        street2: event.target.value,
                                                    });
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="business_details_city">
                                                {renderOptionalReceiptFieldLabel(
                                                    'City',
                                                    businessDetailsEditorState.city.trim().length ===
                                                        0
                                                )}
                                            </Label>
                                            <Input
                                                id="business_details_city"
                                                value={businessDetailsEditorState.city}
                                                onChange={(event) => {
                                                    setBusinessDetailsEditorState({
                                                        ...businessDetailsEditorState,
                                                        city: event.target.value,
                                                    });
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="business_details_ca_province">
                                                {renderOptionalReceiptFieldLabel(
                                                    'Province',
                                                    businessDetailsEditorState.caProvince === ''
                                                )}
                                            </Label>
                                            <select
                                                id="business_details_ca_province"
                                                className="w-full border rounded px-3 py-2"
                                                value={businessDetailsEditorState.caProvince}
                                                onChange={(event) => {
                                                    const selectedProvinceCandidate:
                                                        | CanadaProvince
                                                        | ''
                                                        | undefined =
                                                        event.target.value === '' ? '' : undefined;
                                                    if (selectedProvinceCandidate === '') {
                                                        setBusinessDetailsEditorState({
                                                            ...businessDetailsEditorState,
                                                            caProvince: selectedProvinceCandidate,
                                                        });
                                                        return;
                                                    }
                                                    const selectedCaProvinceCandidate:
                                                        | CanadaProvince
                                                        | undefined = CanadaProvinceKeyList.find(
                                                        (caProvinceKey) =>
                                                            caProvinceKey === event.target.value
                                                    );
                                                    if (selectedCaProvinceCandidate === undefined) {
                                                        throw new Error(
                                                            `Invalid Canada province selected: ${event.target.value}`
                                                        );
                                                    }
                                                    setBusinessDetailsEditorState({
                                                        ...businessDetailsEditorState,
                                                        caProvince: selectedCaProvinceCandidate,
                                                    });
                                                }}
                                            >
                                                <option value="">Unknown</option>
                                                {CanadaProvinceKeyList.map((caProvince) => (
                                                    <option key={caProvince} value={caProvince}>
                                                        {caProvince}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <Label htmlFor="business_details_ca_postal_code">
                                                {renderOptionalReceiptFieldLabel(
                                                    'Postal code',
                                                    businessDetailsEditorState.caPostalCode.trim()
                                                        .length === 0
                                                )}
                                            </Label>
                                            <Input
                                                id="business_details_ca_postal_code"
                                                value={businessDetailsEditorState.caPostalCode}
                                                onChange={(event) => {
                                                    setBusinessDetailsEditorState({
                                                        ...businessDetailsEditorState,
                                                        caPostalCode: normalizeCanadaPostalCode(
                                                            event.target.value
                                                        ),
                                                    });
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}
                                <div className="text-xs text-muted-foreground">
                                    {businessDetailsSummary}
                                </div>
                                {businessDetailsEditorError !== null && (
                                    <Alert variant="destructive">
                                        <AlertDescription>{businessDetailsEditorError}</AlertDescription>
                                    </Alert>
                                )}
                                {updateBusinessDetailsMutation.error && (
                                    <Alert variant="destructive">
                                        <AlertDescription>
                                            {String(updateBusinessDetailsMutation.error)}
                                        </AlertDescription>
                                    </Alert>
                                )}
                                <div className="flex gap-2">
                                    <Button
                                        size="sm"
                                        onClick={handleSaveBusinessDetails}
                                        disabled={
                                            updateBusinessDetailsMutation.isPending ||
                                            !isBusinessDetailsSaveEnabled
                                        }
                                    >
                                        {updateBusinessDetailsMutation.isPending
                                            ? 'Saving...'
                                            : 'Save'}
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleCancelBusinessDetails(org)}
                                        disabled={updateBusinessDetailsMutation.isPending}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            <div className="flex flex-col">
            {orgIsMerchant && canEditConfig && (
                <div
                    id="merchant-accounts-card-wrapper"
                    className="mb-6"
                    style={{ order: 10 }}
                >
                    <MerchantAccountsListCard
                        organizationUuid={organizationUuidFromRoute}
                        showPageHeader={false}
                        cardId="merchant-accounts-card"
                        compactBottomSpacing={true}
                        allowSubsidiariesToggle={hasSubsidiaries}
                    />
                </div>
            )}

            {orgIsMerchant && canEditConfig && (
                <div style={{ order: 50 }}>
                <Card id="document-templates-card" className="mb-6">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>Document Templates</CardTitle>
                        <Button
                            size="sm"
                            onClick={() =>
                                navigate(
                                    `/organizations/${organizationUuidFromRoute}/document-templates`
                                )
                            }
                        >
                            Details
                        </Button>
                    </CardHeader>
                    <CardContent>
                        <p className="text-sm text-muted-foreground">
                            Manage registry-backed document template overrides on a dedicated
                            page.
                        </p>
                    </CardContent>
                </Card>
                </div>
            )}


            {(orgIsIso || orgIsMerchant) && (
                <div style={{ order: 70 }}>
                <Card className="mb-6">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>
                            {organizationView === 'ISO'
                                ? 'Client Merchants'
                                : 'Subsidiary Merchants'}
                        </CardTitle>
                        {canManageOrganizations && (
                            <Button
                                onClick={() => {
                                    const createMerchantUrl =
                                        organizationView === 'ISO'
                                            ? `/organizations/create/merchant?parent_iso_uuid=${org.organization_uuid}`
                                            : `/organizations/create/merchant?parent_merchant_uuid=${org.organization_uuid}`;
                                    runInBackground(navigate(createMerchantUrl), (err) => {
                                        log.error('Error navigating to create merchant:', err);
                                    });
                                }}
                                size="sm"
                            >
                                Create Merchant
                            </Button>
                        )}
                    </CardHeader>
                    <CardContent>
                        <EmbeddedInfiniteScrollList<
                            OrganizationOmnisearchListItem,
                            string,
                            readonly unknown[]
                        >
                            queryKeyPrefix={buildRoleScopedQueryKey(activeRoleAssignmentId, [
                                'organization',
                                organizationUuidFromRoute,
                                'subsidiary_merchants',
                            ])}
                            pageSize={50}
                            maxHeight="50rem"
                            enabled={orgIsIso || orgIsMerchant}
                            parseCursorParam={parseEmbeddedListCursorParam}
                            fetchPage={async ({ limit, cursor }) => {
                                if (organizationUuid === undefined) {
                                    throw new Error('Organization UUID is required');
                                }
                                const response = await organizationApi.listSubsidiaryMerchantsPage(
                                    organizationUuid,
                                    {
                                        limit,
                                        cursor,
                                    }
                                );
                                return {
                                    items: response.merchants,
                                    pagination: response.pagination,
                                };
                            }}
                            stateCopy={{
                                emptyLabel:
                                    organizationView === 'ISO'
                                        ? 'No merchants assigned to this ISO.'
                                        : 'No subsidiary merchants.',
                            }}
                            renderItems={({ items }) => (
                                <div className="space-y-2">
                                    {items.map((merchant) => (
                                        <div
                                            key={merchant.organization_uuid}
                                            className="border rounded p-3 hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                                            onClick={() => {
                                                runInBackground(
                                                    navigate(
                                                        `/organizations/${merchant.organization_uuid}?view=merchant`
                                                    ),
                                                    (err) => {
                                                        log.error(
                                                            'Error navigating to organization:',
                                                            err
                                                        );
                                                    }
                                                );
                                            }}
                                        >
                                            <div>
                                                <div className="font-medium">{merchant.name}</div>
                                            </div>
                                            <StatusBadge status={merchant.status} />
                                        </div>
                                    ))}
                                </div>
                            )}
                        />
                    </CardContent>
                </Card>
                </div>
            )}

            {/* Credential Cards */}
            {orgIsMerchant && (
                <>
                    <div style={{ order: 19 }}>
                        <SecretsCard
                            organizationUuid={org.organization_uuid}
                            organizationName={org.name}
                            allowSubsidiariesToggle={hasSubsidiaries}
                            scrollToCredentialUuid={highlightedCredentialUuid}
                            scrollToSection={shouldScrollToCredentials}
                            credentialTargetFamily="ipg"
                            enabledIpgSystemIds={org.enable_ipg_system_ids ?? []}
                            defaultCollapsed={!shouldScrollToCredentials}
                        />
                    </div>
                    <div style={{ order: 20 }}>
                        <SecretsCard
                            organizationUuid={org.organization_uuid}
                            organizationName={org.name}
                            allowSubsidiariesToggle={hasSubsidiaries}
                            scrollToCredentialUuid={highlightedCredentialUuid}
                            scrollToSection={shouldScrollToCredentials}
                            credentialTargetFamily="ipw"
                            enabledIpwSystemIds={org.enable_ipw_system_ids ?? []}
                            defaultCollapsed={!shouldScrollToCredentials}
                        />
                    </div>
                    <div style={{ order: 21 }}>
                        <SecretsCard
                            organizationUuid={org.organization_uuid}
                            organizationName={org.name}
                            allowSubsidiariesToggle={hasSubsidiaries}
                            scrollToCredentialUuid={highlightedCredentialUuid}
                            scrollToSection={shouldScrollToCredentials}
                            credentialTargetFamily="generic_website"
                            genericWebsiteCredentialOptions={genericWebsiteCredentialOptions}
                            defaultCollapsed={!shouldScrollToCredentials}
                        />
                    </div>
                </>
            )}

            {orgIsMerchant && canAuditPermissions && (
                <div style={{ order: 23 }}>
                    <OrganizationIpxCredentialPoliciesCard
                        organizationUuid={org.organization_uuid}
                        defaultCollapsed={true}
                    />
                </div>
            )}

            {/* Permissions Policies */}
            {canManageOrganizations && (
                <div style={{ order: 17 }}>
                    <PolicyPermissionBundlesCard
                        organizationUuid={org.organization_uuid}
                        organizationName={formatOrganizationDisplayName(org)}
                        compactBottomSpacing={true}
                        allowSubsidiariesToggle={hasSubsidiaries}
                        defaultCollapsed={true}
                        focusPermissionsBundleRequest={focusedPolicyPermissionBundleRequest}
                    />
                </div>
            )}

            {canAuditPermissions && (
                <div style={{ order: 60 }}>
                <Card className="mb-6">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>Permissions Audit</CardTitle>
                        <Button
                            size="sm"
                            onClick={() =>
                                navigate(
                                    `/organizations/${organizationUuidFromRoute}/permissions-audit`
                                )
                            }
                        >
                            Details
                        </Button>
                    </CardHeader>
                    <CardContent>
                        <p className="text-sm text-muted-foreground">
                            Audit persisted permission grants for users in this
                            organization tree.
                        </p>
                    </CardContent>
                </Card>
                </div>
            )}

            {orgIsIso && (
                <div style={{ order: 80 }}>
                <Card className="mb-6">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>Parent ISO</CardTitle>
                        {canManageOrganizations && (
                            <Button
                                size="sm"
                                variant={isReassigning ? 'outline' : 'default'}
                                onClick={() => setIsReassigning(!isReassigning)}
                            >
                                {isReassigning ? 'Cancel' : 'Assign Parent ISO'}
                            </Button>
                        )}
                    </CardHeader>
                    <CardContent>
                        {org.parent_organization_uuid ? (
                            parentOrganizationData ? (
                                <div className="flex justify-between items-center">
                                    <div className="font-medium">{parentOrganizationData.name}</div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                            runInBackground(
                                                navigate(
                                                    `/organizations/${parentOrganizationData.organization_uuid}?view=iso`
                                                ),
                                                (err) => {
                                                    log.error(
                                                        'Error navigating to organization:',
                                                        err
                                                    );
                                                }
                                            );
                                        }}
                                    >
                                        View
                                    </Button>
                                </div>
                            ) : (
                                <p className="text-sm text-muted-foreground">
                                    Loading parent organization...
                                </p>
                            )
                        ) : (
                            <p className="text-sm text-muted-foreground">No parent ISO</p>
                        )}

                        {canManageOrganizations && isReassigning && (
                            <div className="mt-4">
                                <div className="flex items-center justify-between gap-2">
                                    <Label>Parent ISO</Label>
                                    {org.parent_organization_uuid !== null &&
                                        parentOrganizationData !== undefined &&
                                        hierarchyParentIsIso && (
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                onClick={() => {
                                                    beginRevokeParentOrganization(
                                                        parentOrganizationData.name,
                                                        'ISO'
                                                    );
                                                }}
                                            >
                                                Revoke
                                            </Button>
                                        )}
                                </div>
                                <div className="mt-2">
                                    <OmnisearchSelect<
                                        OrganizationOmnisearchListItem,
                                        string
                                    >
                                        label="Search parent ISOs"
                                        placeholder="Type to search..."
                                        pageSize={25}
                                        disabled={setParentOrganizationMutation.isPending}
                                        selectedItemId={null}
                                        fetchPage={async (params) => {
                                            const response =
                                                await organizationApi.searchOrganizations({
                                                    ...(params.terms !== undefined
                                                        ? { terms: [...params.terms] }
                                                        : {}),
                                                    limit: params.limit,
                                                    cursor: params.cursor,
                                                    is_iso: true,
                                                });
                                            return {
                                                items: response.organizations.filter(
                                                    (candidate) =>
                                                        candidate.organization_uuid !==
                                                        organizationUuid
                                                ),
                                                pagination: response.pagination,
                                            };
                                        }}
                                        getItemId={(item) => item.organization_uuid}
                                        getItemLabel={(item) =>
                                            `${item.name} (${getOrganizationCapacityLabel(item)})`
                                        }
                                        getItemFilterText={(item) =>
                                            `${item.name} ${item.organization_uuid}`
                                        }
                                        renderItemCard={renderOrganizationOmnisearchCard}
                                        onSelect={(item) => {
                                            if (!organizationHasCapacity(item, 'is_iso')) {
                                                throw new Error(
                                                    `Expected ISO candidate, got ${getOrganizationCapacityLabel(item)}`
                                                );
                                            }
                                            beginAssignParentOrganizationFromItem(item, 'ISO');
                                        }}
                                    />
                                </div>
                                {setParentOrganizationMutation.error && (
                                    <div className="text-red-600 text-sm">
                                        Error: {String(setParentOrganizationMutation.error)}
                                    </div>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>
                </div>
            )}

            {orgIsMerchant && (
                <div style={{ order: 80 }}>
                <Card className="mb-6">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>ISO of record</CardTitle>
                        {canManageOrganizations && (
                            <Button
                                size="sm"
                                variant={isReassigning ? 'outline' : 'default'}
                                onClick={() => {
                                    setIsAssigningParentMerchant(false);
                                    setIsReassigning(!isReassigning);
                                }}
                            >
                                {isReassigning ? 'Cancel' : 'Assign to ISO'}
                            </Button>
                        )}
                    </CardHeader>
                    <CardContent>
                        {org.iso_organization_uuid ? (
                            isoOrganizationData ? (
                                <div className="flex justify-between items-center">
                                    <div className="font-medium">{isoOrganizationData.name}</div>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                runInBackground(
                                                    navigate(
                                                        `/organizations/${isoOrganizationData.organization_uuid}?view=iso`
                                                    ),
                                                    (err) => {
                                                        log.error(
                                                            'Error navigating to organization:',
                                                            err
                                                        );
                                                    }
                                                );
                                            }}
                                        >
                                            View
                                        </Button>
                                        {canManageOrganizations && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => {
                                                    beginRevokeParentOrganization(
                                                        isoOrganizationData.name,
                                                        'ISO'
                                                    );
                                                }}
                                            >
                                                Revoke
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <p className="text-sm text-muted-foreground">
                                    Loading ISO of record...
                                </p>
                            )
                        ) : (
                            <p className="text-sm text-muted-foreground">
                                Not assigned to any ISO
                            </p>
                        )}

                        {canManageOrganizations && isReassigning && (
                            <div className="mt-4">
                                <Label>ISO of record</Label>
                                <div className="mt-2">
                                    <OmnisearchSelect<
                                        OrganizationOmnisearchListItem,
                                        string
                                    >
                                        label="Search ISOs"
                                        placeholder="Type to search..."
                                        pageSize={25}
                                        disabled={setIsoOrganizationMutation.isPending}
                                        selectedItemId={null}
                                        fetchPage={async (params) => {
                                            const response =
                                                await organizationApi.searchOrganizations({
                                                    ...(params.terms !== undefined
                                                        ? { terms: [...params.terms] }
                                                        : {}),
                                                    limit: params.limit,
                                                    cursor: params.cursor,
                                                    is_iso: true,
                                                });
                                            return {
                                                items: response.organizations,
                                                pagination: response.pagination,
                                            };
                                        }}
                                        getItemId={(item) => item.organization_uuid}
                                        getItemLabel={(item) =>
                                            `${item.name} (${getOrganizationCapacityLabel(item)})`
                                        }
                                        getItemFilterText={(item) =>
                                            `${item.name} ${item.organization_uuid}`
                                        }
                                        renderItemCard={renderOrganizationOmnisearchCard}
                                        onSelect={(item) => {
                                            if (!organizationHasCapacity(item, 'is_iso')) {
                                                throw new Error(
                                                    `Expected ISO candidate, got ${getOrganizationCapacityLabel(item)}`
                                                );
                                            }
                                            beginAssignParentOrganizationFromItem(item, 'ISO');
                                        }}
                                    />
                                </div>
                                {setIsoOrganizationMutation.error && (
                                    <div className="text-red-600 text-sm">
                                        Error: {String(setIsoOrganizationMutation.error)}
                                    </div>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>
                </div>
            )}

            {orgIsMerchant && (
                <div style={{ order: 81 }}>
                <Card className="mb-6">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>Parent Merchant</CardTitle>
                        {canManageOrganizations && (
                            <Button
                                size="sm"
                                variant={isAssigningParentMerchant ? 'outline' : 'default'}
                                onClick={() => {
                                    setIsReassigning(false);
                                    setIsAssigningParentMerchant(!isAssigningParentMerchant);
                                }}
                            >
                                {isAssigningParentMerchant ? 'Cancel' : 'Assign Parent Merchant'}
                            </Button>
                        )}
                    </CardHeader>
                    <CardContent>
                        <div>
                            {org.parent_organization_uuid && parentIsMerchant ? (
                                parentOrganizationData ? (
                                    <div className="flex justify-between items-center">
                                        <div className="font-medium">
                                            {parentOrganizationData.name}
                                        </div>
                                        <div className="flex gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => {
                                                    runInBackground(
                                                        navigate(
                                                            `/organizations/${parentOrganizationData.organization_uuid}?view=merchant`
                                                        ),
                                                        (err) => {
                                                            log.error(
                                                                'Error navigating to organization:',
                                                                err
                                                            );
                                                        }
                                                    );
                                                }}
                                            >
                                                View
                                            </Button>
                                            {canManageOrganizations && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => {
                                                        beginRevokeParentOrganization(
                                                            parentOrganizationData.name,
                                                            'MERCHANT'
                                                        );
                                                    }}
                                                >
                                                    Revoke
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-sm text-muted-foreground">
                                        Loading parent merchant...
                                    </p>
                                )
                            ) : (
                                <p className="text-sm text-muted-foreground">
                                    No parent merchant
                                </p>
                            )}

                            {!canManageOrganizations && (
                                <p className="text-sm text-muted-foreground mt-2">
                                    Access denied. Insufficient permissions.
                                </p>
                            )}

                            {canManageOrganizations && isAssigningParentMerchant && (
                                <div className="mt-4">
                                    <OmnisearchSelect<
                                        OrganizationOmnisearchListItem,
                                        string
                                    >
                                        label="Search parent merchants"
                                        placeholder="Type to search..."
                                        pageSize={25}
                                        disabled={setParentOrganizationMutation.isPending}
                                        selectedItemId={null}
                                        fetchPage={async (params) => {
                                            const response =
                                                await organizationApi.searchOrganizations({
                                                    ...(params.terms !== undefined
                                                        ? { terms: [...params.terms] }
                                                        : {}),
                                                    limit: params.limit,
                                                    cursor: params.cursor,
                                                    is_merchant: true,
                                                });
                                            return {
                                                items: response.organizations.filter(
                                                    (candidate) =>
                                                        candidate.organization_uuid !==
                                                        organizationUuid
                                                ),
                                                pagination: response.pagination,
                                            };
                                        }}
                                        getItemId={(item) => item.organization_uuid}
                                        getItemLabel={(item) =>
                                            `${item.name} (${getOrganizationCapacityLabel(item)})`
                                        }
                                        getItemFilterText={(item) =>
                                            `${item.name} ${item.organization_uuid}`
                                        }
                                        renderItemCard={renderOrganizationOmnisearchCard}
                                        onSelect={(item) => {
                                            if (!organizationHasCapacity(item, 'is_merchant')) {
                                                throw new Error(
                                                    `Expected MERCHANT candidate, got ${getOrganizationCapacityLabel(item)}`
                                                );
                                            }
                                            beginAssignParentOrganizationFromItem(item, 'MERCHANT');
                                        }}
                                    />
                                    {setParentOrganizationMutation.error && (
                                        <div className="text-red-600 text-sm mt-2">
                                            Error: {String(setParentOrganizationMutation.error)}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
                </div>
            )}

            {orgIsIso && (
                <div style={{ order: 71 }}>
                <Card className="mb-6">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>Subsidiary ISOs</CardTitle>
                        {canManageOrganizations && (
                            <Button
                                onClick={() => {
                                    runInBackground(
                                        navigate(
                                            `/organizations/create/iso?parent_iso_uuid=${org.organization_uuid}`
                                        ),
                                        (err) => {
                                            log.error('Error navigating to create ISO:', err);
                                        }
                                    );
                                }}
                                size="sm"
                            >
                                Create ISO
                            </Button>
                        )}
                    </CardHeader>
                    <CardContent>
                        <EmbeddedInfiniteScrollList<
                            OrganizationOmnisearchListItem,
                            string,
                            readonly unknown[]
                        >
                            queryKeyPrefix={buildRoleScopedQueryKey(activeRoleAssignmentId, [
                                'organization',
                                organizationUuidFromRoute,
                                'subsidiary_isos',
                            ])}
                            pageSize={50}
                            maxHeight="50rem"
                            enabled={orgIsIso}
                            parseCursorParam={parseEmbeddedListCursorParam}
                            fetchPage={async ({ limit, cursor }) => {
                                if (organizationUuid === undefined) {
                                    throw new Error('Organization UUID is required');
                                }
                                const response = await organizationApi.listSubsidiaryIsosPage(
                                    organizationUuid,
                                    {
                                        limit,
                                        cursor,
                                    }
                                );
                                return {
                                    items: response.isos,
                                    pagination: response.pagination,
                                };
                            }}
                            stateCopy={{
                                emptyLabel: 'No subsidiary ISOs.',
                            }}
                            renderItems={({ items }) => (
                                <div className="space-y-2">
                                    {items.map((iso) => (
                                        <div
                                            key={iso.organization_uuid}
                                            className="border rounded p-3 hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                                            onClick={() => {
                                                runInBackground(
                                                    navigate(
                                                        `/organizations/${iso.organization_uuid}?view=iso`
                                                    ),
                                                    (err) => {
                                                        log.error(
                                                            'Error navigating to organization:',
                                                            err
                                                        );
                                                    }
                                                );
                                            }}
                                        >
                                            <div>
                                                <div className="font-medium">{iso.name}</div>
                                            </div>
                                            <StatusBadge status={iso.status} />
                                        </div>
                                    ))}
                                </div>
                            )}
                        />
                    </CardContent>
                </Card>
                </div>
            )}

            <div style={{ order: 18 }}>
            <Card className="mb-6">
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Organization Members</CardTitle>
                    <div className="flex items-center gap-3">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            setOrganizationMembersExpanded((currentValue) => !currentValue);
                        }}
                    >
                        {organizationMembersExpanded ? 'Collapse' : 'Expand'}
                    </Button>
                    {organizationMembersExpanded && hasSubsidiaries ? (
                        <CheckboxField
                            label="Show subsidiaries"
                            checked={showOrganizationMemberSubsidiaries}
                            onCheckedChange={setShowOrganizationMemberSubsidiaries}
                        />
                    ) : null}
                    {organizationMembersExpanded && org.status !== 'DELETED' && (
                        <div className="flex items-center gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    setShowAssignUsersDialog(true);
                                }}
                            >
                                Assign Users
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    setShowImportUsersDialog(true);
                                    setOrganizationUsersImportError(null);
                                }}
                            >
                                Import Users
                            </Button>
                            <Button
                                onClick={() => {
                                    runInBackground(
                                        navigate(organizationUserCreatePath),
                                        (err) => {
                                            log.error('Error navigating to create user:', err);
                                        }
                                    );
                                }}
                                size="sm"
                            >
                                Create User
                            </Button>
                        </div>
                    )}
                    </div>
                </CardHeader>
                {organizationMembersExpanded ? (
                <CardContent>
                    <EmbeddedInfiniteScrollList<
                        UserProfileDetailedPublicResponse,
                        string,
                        readonly unknown[]
                    >
                        queryKeyPrefix={buildRoleScopedQueryKey(activeRoleAssignmentId, [
                            'organization',
                            organizationUuidFromRoute,
                            'users',
                            `descendants=${showOrganizationMemberSubsidiaries}`,
                        ])}
                        pageSize={50}
                        maxHeight="42rem"
                        compactBottomSpacing={true}
                        enabled={organizationMembersExpanded}
                        parseCursorParam={parseEmbeddedListCursorParam}
                        fetchPage={async ({ limit, cursor }) => {
                            if (organizationUuid === undefined) {
                                throw new Error('Organization UUID is required');
                            }
                            const response = await organizationApi.listDirectOrganizationUsersPage(
                                organizationUuid,
                                {
                                    limit,
                                    cursor,
                                    include_descendants: showOrganizationMemberSubsidiaries,
                                }
                            );
                            return {
                                items: response.users,
                                pagination: response.pagination,
                            };
                        }}
                        stateCopy={{
                            emptyLabel: 'No users assigned to this organization.',
                        }}
                        renderItems={({ items }) => (
                            <OrganizationMembersList
                                items={items}
                                organizationUuid={organizationUuidFromRoute}
                                showOrganizationMemberSubsidiaries={
                                    showOrganizationMemberSubsidiaries
                                }
                                onNavigateToUser={(userUuid: string) => {
                                    runInBackground(navigate(`/users/${userUuid}`), (err) => {
                                        log.error('Error navigating to user:', err);
                                    });
                                }}
                                onFocusPermissionPolicy={(permissionsBundleUuid: string) => {
                                    setFocusedPolicyPermissionBundleRequest((currentValue) => ({
                                        permissionsBundleUuid,
                                        requestId: (currentValue?.requestId ?? 0) + 1,
                                    }));
                                }}
                                onImpersonateRole={openOrganizationMemberImpersonateDialog}
                            />
                        )}
                    />
                </CardContent>
                ) : null}
            </Card>
            </div>

            <Dialog
                open={showImpersonateConfirm}
                onOpenChange={(nextOpenState) => {
                    if (nextOpenState) {
                        setShowImpersonateConfirm(true);
                    }
                }}
            >
                <DialogContent
                    onInteractOutside={(event) => {
                        event.preventDefault();
                    }}
                    onEscapeKeyDown={(event) => {
                        event.preventDefault();
                    }}
                >
                    <DialogHeader>
                        <DialogTitle>Start Impersonation</DialogTitle>
                        <DialogDescription>
                            {`Do you want to temporarily assume the ${roleToImpersonate?.role ?? ''} role of user ${roleToImpersonate?.user_display_name ?? ''}`.trim()}
                            {roleToImpersonate?.organization_name !== null &&
                            roleToImpersonate?.organization_name !== undefined
                                ? ` @ ${roleToImpersonate.organization_name}`
                                : ''}
                            ?
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowImpersonateConfirm(false);
                                setRoleToImpersonate(null);
                            }}
                            disabled={startImpersonationMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => {
                                if (roleToImpersonate === null) {
                                    return;
                                }
                                startImpersonationMutation.mutate(
                                    roleToImpersonate.role_assignment_uuid
                                );
                            }}
                            disabled={startImpersonationMutation.isPending}
                        >
                            {startImpersonationMutation.isPending
                                ? 'Starting...'
                                : 'Start Impersonating'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog
                open={showImportUsersDialog}
                onOpenChange={(open) => {
                    if (!open) {
                        resetOrganizationUsersImportDialog();
                        return;
                    }
                    setShowImportUsersDialog(true);
                }}
            >
                <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Import Users</DialogTitle>
                        <DialogDescription>
                            Upload a CSV file to preview inserts, updates, no-op rows, and
                            non-actionable errors before saving.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        {organizationUsersImportError !== null ? (
                            <Alert variant="destructive">
                                <AlertDescription>{organizationUsersImportError}</AlertDescription>
                            </Alert>
                        ) : null}

                        {organizationUsersImportPreview === null ? (
                            <div className="space-y-3">
                                <div className="space-y-2">
                                    <Label htmlFor="organization-users-import-file">
                                        Upload CSV
                                    </Label>
                                    <Input
                                        id="organization-users-import-file"
                                        type="file"
                                        accept=".csv,text/csv"
                                        onChange={(event) => {
                                            const file = event.target.files?.[0];
                                            if (file === undefined) {
                                                return;
                                            }
                                            runInBackground(
                                                handleOrganizationUsersImportFileSelected(file),
                                                (err) => {
                                                    log.error(
                                                        'Error previewing imported organization users:',
                                                        err
                                                    );
                                                }
                                            );
                                        }}
                                    />
                                </div>
                                {organizationUsersImportFileName !== null ? (
                                    <div className="text-sm text-muted-foreground">
                                        Selected file: {organizationUsersImportFileName}
                                    </div>
                                ) : null}
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between gap-4">
                                    <div className="text-sm text-muted-foreground">
                                        {organizationUsersImportFileName ?? 'Uploaded CSV'}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Checkbox
                                            checked={areAllActionableOrganizationUsersImportRowsSelected}
                                            disabled={
                                                actionableOrganizationUsersImportRowIndexes.length === 0
                                            }
                                            onCheckedChange={(checked) => {
                                                handleToggleAllOrganizationUsersImportRows(
                                                    checked === true
                                                );
                                            }}
                                        />
                                        <span className="text-sm">Select all</span>
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    {organizationUsersImportPreview.rows.map((row) => {
                                        const isActionable =
                                            isActionableOrganizationUserCsvImportRow(row);
                                        return (
                                            <div
                                                key={row.row_index}
                                                className="rounded-md border p-4 space-y-3"
                                            >
                                                <div className="flex items-start justify-between gap-4">
                                                    <div className="flex items-center gap-3">
                                                        {isActionable ? (
                                                            <Checkbox
                                                                checked={selectedOrganizationUsersImportRowIndexSet.has(
                                                                    row.row_index
                                                                )}
                                                                onCheckedChange={() => {
                                                                    toggleOrganizationUsersImportRowSelection(
                                                                        row.row_index
                                                                    );
                                                                }}
                                                            />
                                                        ) : (
                                                            <div className="w-4" />
                                                        )}
                                                        <div>
                                                            <div className="font-medium">
                                                                Row {row.row_index}
                                                            </div>
                                                            <div className="text-sm text-muted-foreground">
                                                                {row.row_data.primary_email}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <Badge variant="secondary">
                                                        {getOrganizationUserCsvImportClassificationLabel(
                                                            row
                                                        )}
                                                    </Badge>
                                                </div>
                                                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                                                    <div>
                                                        <div className="text-xs text-muted-foreground">
                                                            Name
                                                        </div>
                                                        <div>
                                                            {[
                                                                row.row_data.first_name,
                                                                row.row_data.last_name,
                                                            ]
                                                                .filter(
                                                                    (value): value is string =>
                                                                        value !== undefined
                                                                )
                                                                .join(' ') || '—'}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="text-xs text-muted-foreground">
                                                            Role
                                                        </div>
                                                        <div>{row.row_data.role}</div>
                                                    </div>
                                                    <div>
                                                        <div className="text-xs text-muted-foreground">
                                                            Policies
                                                        </div>
                                                        <div>
                                                            {row.row_data.policies.join(', ') || '—'}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="text-xs text-muted-foreground">
                                                            Phones
                                                        </div>
                                                        <div>
                                                            {row.row_data.phones.join(', ') || '—'}
                                                        </div>
                                                    </div>
                                                </div>
                                                {row.existing_user !== undefined ? (
                                                    <div className="text-sm text-muted-foreground">
                                                        Existing user: {row.existing_user.primary_email}
                                                        {' · '}
                                                        {row.existing_user.role}
                                                    </div>
                                                ) : null}
                                                {row.conflicting_users !== undefined ? (
                                                    <div className="text-sm text-red-600">
                                                        Conflicts with:{' '}
                                                        {row.conflicting_users
                                                            .map((userRow) => userRow.primary_email)
                                                            .join(', ')}
                                                    </div>
                                                ) : null}
                                                {(row.row_errors?.length ?? 0) > 0 ? (
                                                    <div className="space-y-1 text-sm text-red-600">
                                                        {row.row_errors?.map((rowError) => (
                                                            <div key={rowError}>{rowError}</div>
                                                        ))}
                                                    </div>
                                                ) : null}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        {canSkipOrganizationUserVerification ? (
                            <div className="flex items-center space-x-2">
                                <Checkbox
                                    id="organization_users_import_skip_verification"
                                    checked={organizationUsersImportSkipVerification}
                                    onCheckedChange={(checked) => {
                                        setOrganizationUsersImportSkipVerification(
                                            checked === true
                                        );
                                    }}
                                />
                                <Label htmlFor="organization_users_import_skip_verification">
                                    Skip verification (internal admin or higher)
                                </Label>
                            </div>
                        ) : null}
                    </div>
                    <DialogFooter className="gap-2">
                        {organizationUsersImportPreview !== null ? (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setOrganizationUsersImportPreview(null);
                                    setSelectedOrganizationUsersImportRowIndexes([]);
                                    setOrganizationUsersImportError(null);
                                }}
                            >
                                Choose Another CSV
                            </Button>
                        ) : null}
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                resetOrganizationUsersImportDialog();
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            disabled={
                                organizationUsersImportPreview === null
                                || selectedOrganizationUsersImportRowIndexes.length === 0
                                || applyOrganizationUsersCsvImportMutation.isPending
                            }
                            onClick={() => {
                                applyOrganizationUsersCsvImportMutation.mutate(
                                    selectedOrganizationUsersImportRowIndexes
                                );
                            }}
                        >
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AssignUsersToOrganizationDialog
                open={showAssignUsersDialog}
                onOpenChange={setShowAssignUsersDialog}
                organizationName={org.name}
                availableRoles={AllOrganizationalRoles}
                onSave={(data) => {
                    assignUsersToOrganizationMutation.mutate(data);
                }}
                isLoading={assignUsersToOrganizationMutation.isPending}
                error={
                    assignUsersToOrganizationMutation.error instanceof Error
                        ? assignUsersToOrganizationMutation.error
                        : null
                }
            />

            {org.is_customer === true && (
                <Card className="mb-6">
                    <CardHeader>
                        <CardTitle>Payments</CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-wrap gap-3">
                        <Button
                            type="button"
                            onClick={() => {
                                runInBackground(
                                    navigate(
                                        appendCustomerOrganizationUuid(
                                            '/customer/payment-requests',
                                            org.organization_uuid
                                        )
                                    ),
                                    (err) => {
                                        log.error('Error navigating to pending payments:', err);
                                    }
                                );
                            }}
                        >
                            Pending Payments
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                runInBackground(
                                    navigate(
                                        appendCustomerOrganizationUuid(
                                            '/customer/payment-history',
                                            org.organization_uuid
                                        )
                                    ),
                                    (err) => {
                                        log.error('Error navigating to recent payments:', err);
                                    }
                                );
                            }}
                        >
                            Recent Payments
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                runInBackground(
                                    navigate(
                                        appendCustomerOrganizationUuid(
                                            '/customer/payment-methods',
                                            org.organization_uuid
                                        )
                                    ),
                                    (err) => {
                                        log.error('Error navigating to payment methods:', err);
                                    }
                                );
                            }}
                        >
                            Payment Methods
                        </Button>
                    </CardContent>
                </Card>
            )}

            {orgIsMerchant && canEditConfig && (
                <>
                    <div style={{ order: 12 }}>
                        <Card id="integrated-payments-gateways-card" className="mb-6">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle>Integrated Payments Gateways</CardTitle>
                                {renderMerchantConfigCardActions('ipg', org)}
                            </CardHeader>
                            <CardContent>
                                {editingConfigCard === 'ipg' ? (
                                    <div className="space-y-4">
                                {renderMerchantConfigEditorAlerts()}
                                <div className="space-y-2">
                                    <Label htmlFor="enable_ipg_system_ids_config">Enabled Integrated Payments Gateways</Label>
                                    <select
                                        id="enable_ipg_system_ids_config"
                                        className="w-full border rounded px-3 py-2"
                                        value=""
                                        onChange={(event) => {
                                            const selectedIpgOption = ipgSystemOptions.find(
                                                (option) =>
                                                    String(option.systemId) === event.target.value
                                            );
                                            if (selectedIpgOption === undefined) {
                                                throw new Error(
                                                    `Unknown selected gateway id: ${event.target.value}`
                                                );
                                            }
                                            setEnabledIpgSystemIdsForEdit(
                                                appendUniqueSystemId(
                                                    enabledIpgSystemIdsForEdit,
                                                    selectedIpgOption.systemId
                                                )
                                            );
                                        }}
                                    >
                                        <option value="" disabled>
                                            Select integrated payments gateway to add...
                                        </option>
                                        {ipgSystemOptions.map((ipgOption) => (
                                            <option
                                                key={ipgOption.systemId}
                                                value={String(ipgOption.systemId)}
                                            >
                                                {ipgOption.label}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="flex flex-wrap gap-2">
                                        {enabledIpgSystemIdsForEdit.map((ipgSystemId) => (
                                            <Badge
                                                key={ipgSystemId}
                                                variant="secondary"
                                                className="gap-2 pr-1"
                                            >
                                                {getSystemOptionLabelOrThrow(
                                                    ipgSystemOptions,
                                                    ipgSystemId
                                                )}
                                                <button
                                                    type="button"
                                                    aria-label={`Remove gateway ${ipgSystemId}`}
                                                    className="rounded px-1 hover:bg-muted-foreground/20"
                                                    onClick={() => {
                                                        setEnabledIpgSystemIdsForEdit(
                                                            removeSystemId(
                                                                enabledIpgSystemIdsForEdit,
                                                                ipgSystemId
                                                            )
                                                        );
                                                    }}
                                                >
                                                    x
                                                </button>
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                                <div className="space-y-3 rounded-md border p-3">
                                    <p className="text-sm font-medium">Valor defaults</p>
                                    <div className="space-y-2">
                                        <p className="text-sm font-medium">Portal mode</p>
                                        <div className="flex flex-wrap gap-4">
                                            {(['PRODUCTION', 'DEMO', 'CUSTOM'] as const).map(
                                                (portalMode) => (
                                                    <RadioField
                                                        key={portalMode}
                                                        name="valor_portal_mode"
                                                        label={
                                                            portalMode === 'PRODUCTION'
                                                                ? 'Production'
                                                                : portalMode === 'DEMO'
                                                                  ? 'Demo'
                                                                  : 'Custom'
                                                        }
                                                        checked={
                                                            valorMerchantConfigForEdit.portalMode
                                                            === portalMode
                                                        }
                                                        onChange={() => {
                                                            const managedValorConfig =
                                                                getManagedValorConfigForPortalMode(
                                                                    portalMode
                                                                );
                                                            if (managedValorConfig !== null) {
                                                                setValorMerchantConfigForEdit({
                                                                    ...valorMerchantConfigForEdit,
                                                                    portalMode,
                                                                    portalUrl: getValorPortalUrl(
                                                                        managedValorConfig
                                                                    ),
                                                                    defaultApiUrl:
                                                                        getValorDefaultApiUrl(
                                                                            managedValorConfig
                                                                        ),
                                                                    defaultChannel:
                                                                        managedValorConfig.default_channel
                                                                        ?? '',
                                                                });
                                                                applyValorPresetToIpgEditorState(
                                                                    managedValorConfig
                                                                );
                                                                return;
                                                            }
                                                            setValorMerchantConfigForEdit({
                                                                ...valorMerchantConfigForEdit,
                                                                portalMode,
                                                            });
                                                        }}
                                                    />
                                                )
                                            )}
                                        </div>
                                    </div>
                                    {valorMerchantConfigForEdit.portalMode === 'CUSTOM' && (
                                        <div className="grid gap-4 md:grid-cols-2">
                                            <div className="space-y-2">
                                                <Label htmlFor="valor_portal_url">
                                                    Valor portal URL
                                                </Label>
                                                <Input
                                                    id="valor_portal_url"
                                                    value={valorMerchantConfigForEdit.portalUrl}
                                                    onChange={(event) => {
                                                        applyValorPortalUrlToIpgEditorState(
                                                            event.target.value
                                                        );
                                                        setValorMerchantConfigForEdit({
                                                            ...valorMerchantConfigForEdit,
                                                            portalUrl: event.target.value,
                                                        });
                                                    }}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="valor_default_api_url">
                                                    Valor default API URL
                                                </Label>
                                                <Input
                                                    id="valor_default_api_url"
                                                    value={valorMerchantConfigForEdit.defaultApiUrl}
                                                    onChange={(event) => {
                                                        setValorMerchantConfigForEdit({
                                                            ...valorMerchantConfigForEdit,
                                                            defaultApiUrl: event.target.value,
                                                        });
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {valorMerchantConfigForEdit.portalMode === 'CUSTOM' && (
                                        <div className="space-y-2">
                                            <Label htmlFor="valor_default_channel">
                                                Valor default channel
                                            </Label>
                                            <Input
                                                id="valor_default_channel"
                                                value={valorMerchantConfigForEdit.defaultChannel}
                                                onChange={(event) => {
                                                    setValorMerchantConfigForEdit({
                                                        ...valorMerchantConfigForEdit,
                                                        defaultChannel: event.target.value,
                                                    });
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className="space-y-3 rounded-md border p-3">
                                    <p className="text-sm font-medium">
                                        Integrated Payments Gateway Configuration
                                    </p>
                                    {appendUniqueSystemId(
                                        enabledIpgSystemIdsForEdit,
                                        ValorIpgSystemId
                                    ).map((ipgSystemId) => {
                                        const ipgSystemIdKey =
                                            getIpgSystemIdKeyOrThrow(ipgSystemId);
                                        const editorState =
                                            ipgConfigCustomizationsForEdit[ipgSystemIdKey];
                                        if (editorState === undefined) {
                                            return null;
                                        }
                                        const effectiveIpgConfig = getEffectiveIpgConfig(
                                            organizationConfigData,
                                            ipgSystemIdKey
                                        );
                                        const urlPatternValidationMessage =
                                            getOptionalUrlPatternTextValidationMessage(
                                                editorState.urlPattern
                                            );
                                        return (
                                            <div
                                                key={ipgSystemIdKey}
                                                className="space-y-3 rounded-md border p-3"
                                            >
                                                <p className="text-sm font-medium">
                                                    {getSystemOptionLabelOrThrow(
                                                        ipgSystemOptions,
                                                        ipgSystemId
                                                    )}
                                                </p>
                                                <div className="space-y-2">
                                                    <Label
                                                        htmlFor={`ipg_${ipgSystemIdKey}_url_pattern`}
                                                    >
                                                        URL pattern
                                                    </Label>
                                                    <Input
                                                        id={`ipg_${ipgSystemIdKey}_url_pattern`}
                                                        value={editorState.urlPattern}
                                                        onChange={(event) =>
                                                            updateIpgConfigCustomizationForEdit(
                                                                ipgSystemIdKey,
                                                                {
                                                                    ...editorState,
                                                                    urlPattern:
                                                                        event.target.value,
                                                                }
                                                            )
                                                        }
                                                    />
                                                    {renderUrlPatternValidationMessage(
                                                        urlPatternValidationMessage
                                                    )}
                                                </div>
                                                <div className="space-y-3">
                                                    {editorState.allowedWebsites.map(
                                                        (allowedWebsite, websiteIndex) => {
                                                            const inheritedWebsite =
                                                                effectiveIpgConfig.allowedWebsites[
                                                                    websiteIndex
                                                                ];
                                                            return (
                                                                <AllowedWebsiteCustomizationEditor
                                                                    key={websiteIndex}
                                                                    idPrefix={`ipg_${ipgSystemIdKey}_allowed_website_${websiteIndex}`}
                                                                    title={
                                                                        inheritedWebsite?.name ??
                                                                        `Allowed website ${websiteIndex + 1}`
                                                                    }
                                                                    url={inheritedWebsite?.url}
                                                                    value={allowedWebsite}
                                                                    onChange={(nextWebsite) =>
                                                                        updateIpgConfigCustomizationForEdit(
                                                                            ipgSystemIdKey,
                                                                            {
                                                                                ...editorState,
                                                                                allowedWebsites:
                                                                                    editorState.allowedWebsites.map(
                                                                                        (
                                                                                            currentWebsite,
                                                                                            currentIndex
                                                                                        ) =>
                                                                                            currentIndex ===
                                                                                            websiteIndex
                                                                                                ? nextWebsite
                                                                                                : currentWebsite
                                                                                    ),
                                                                            }
                                                                        )
                                                                    }
                                                                />
                                                            );
                                                        }
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                    <div className="space-y-2">
                                        <p className="text-sm font-medium">Enabled Integrated Payments Gateways</p>
                                        <div className="flex flex-wrap gap-2">
                                            {(org.enable_ipg_system_ids ?? []).map(
                                                (ipgSystemId: IpgSystemId) => (
                                                    <Badge key={ipgSystemId} variant="secondary">
                                                        {getSystemOptionLabelOrThrow(
                                                            ipgSystemOptions,
                                                            ipgSystemId
                                                        )}
                                                    </Badge>
                                                )
                                            )}
                                            {(org.enable_ipg_system_ids ?? []).length === 0 && (
                                                <p className="text-sm text-muted-foreground">
                                                    No integrated payments gateways enabled
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    {hasReadOnlyValorConfig && (
                                        <div className="space-y-2">
                                            <p className="text-sm font-medium">
                                                Valor configuration
                                            </p>
                                            <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
                                                <p>
                                                    Portal mode:{' '}
                                                    {formatValorPortalModeLabel(
                                                        readOnlyValorMerchantConfig.portalMode
                                                    )}
                                                </p>
                                                <p>
                                                    Valor default channel:{' '}
                                                    {readOnlyValorMerchantConfig.defaultChannel.trim()
                                                        .length > 0
                                                        ? readOnlyValorMerchantConfig.defaultChannel
                                                        : 'Not configured'}
                                                </p>
                                                {readOnlyValorMerchantConfig.portalMode ===
                                                    'CUSTOM' && (
                                                    <>
                                                        <p>
                                                            Valor portal URL:{' '}
                                                            {
                                                                readOnlyValorMerchantConfig.portalUrl
                                                            }
                                                        </p>
                                                        <p>
                                                            Valor default API URL:{' '}
                                                            {
                                                                readOnlyValorMerchantConfig.defaultApiUrl
                                                            }
                                                        </p>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {readOnlyIpgPolicySummaries.length > 0 && (
                                        <IntegrationPolicySummary
                                            title="Integrated Payments Gateway Configuration"
                                            integrations={readOnlyIpgPolicySummaries}
                                        />
                                    )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    <div style={{ order: 13 }}>
                        <Card id="integrated-payments-websites-card" className="mb-6">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle>Integrated Payments Websites</CardTitle>
                                {renderMerchantConfigCardActions('ipw', org)}
                            </CardHeader>
                            <CardContent>
                                {editingConfigCard === 'ipw' ? (
                                    <div className="space-y-4">
                                {renderMerchantConfigEditorAlerts()}
                                <div className="space-y-2">
                                    <Label htmlFor="enable_ipw_system_ids_config">Enabled Integrated Payments Websites</Label>
                                    <select
                                        id="enable_ipw_system_ids_config"
                                        className="w-full border rounded px-3 py-2"
                                        value=""
                                        onChange={(event) => {
                                            const selectedIpwOption = ipwSystemOptions.find(
                                                (option) =>
                                                    String(option.systemId) === event.target.value
                                            );
                                            if (selectedIpwOption === undefined) {
                                                throw new Error(
                                                    `Unknown selected website id: ${event.target.value}`
                                                );
                                            }
                                            setEnabledIpwSystemIdsForEdit(
                                                appendUniqueSystemId(
                                                    enabledIpwSystemIdsForEdit,
                                                    selectedIpwOption.systemId
                                                )
                                            );
                                        }}
                                    >
                                        <option value="" disabled>
                                            Select integrated payments website to add...
                                        </option>
                                        {ipwSystemOptions.map((ipwOption) => (
                                            <option
                                                key={ipwOption.systemId}
                                                value={String(ipwOption.systemId)}
                                            >
                                                {ipwOption.label}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="flex flex-wrap gap-2">
                                        {enabledIpwSystemIdsForEdit.map((ipwSystemId) => (
                                            <Badge
                                                key={ipwSystemId}
                                                variant="secondary"
                                                className="gap-2 pr-1"
                                            >
                                                {getSystemOptionLabelOrThrow(
                                                    ipwSystemOptions,
                                                    ipwSystemId
                                                )}
                                                <button
                                                    type="button"
                                                    aria-label={`Remove website ${ipwSystemId}`}
                                                    className="rounded px-1 hover:bg-muted-foreground/20"
                                                    onClick={() => {
                                                        setEnabledIpwSystemIdsForEdit(
                                                            removeSystemId(
                                                                enabledIpwSystemIdsForEdit,
                                                                ipwSystemId
                                                            )
                                                        );
                                                    }}
                                                >
                                                    x
                                                </button>
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                                {enabledIpwSystemIdsForEdit.length > 0 && (
                                    <div className="space-y-3 rounded-md border p-3">
                                        <p className="text-sm font-medium">
                                            Integrated Payments Website Configuration
                                        </p>
                                        {enabledIpwSystemIdsForEdit.map((ipwSystemId) => {
                                            const ipwSystemIdKey =
                                                getIpwSystemIdKeyOrThrow(ipwSystemId);
                                            const editorState =
                                                ipwConfigCustomizationsForEdit[ipwSystemIdKey];
                                            if (editorState === undefined) {
                                                return null;
                                            }
                                            const effectiveIpwConfig = getEffectiveIpwConfig(
                                                organizationConfigData,
                                                ipwSystemIdKey
                                            );
                                            const urlPatternValidationMessage =
                                                getOptionalUrlPatternTextValidationMessage(
                                                    editorState.urlPattern
                                                );
                                            return (
                                                <div
                                                    key={ipwSystemIdKey}
                                                    className="space-y-3 rounded-md border p-3"
                                                >
                                                    <p className="text-sm font-medium">
                                                        {getSystemOptionLabelOrThrow(
                                                            ipwSystemOptions,
                                                            ipwSystemId
                                                        )}
                                                    </p>
                                                    <div className="space-y-2">
                                                        <Label
                                                            htmlFor={`ipw_${ipwSystemIdKey}_url_pattern`}
                                                        >
                                                            URL pattern
                                                        </Label>
                                                        <Input
                                                            id={`ipw_${ipwSystemIdKey}_url_pattern`}
                                                            value={editorState.urlPattern}
                                                            onChange={(event) =>
                                                                updateIpwConfigCustomizationForEdit(
                                                                    ipwSystemIdKey,
                                                                    {
                                                                        ...editorState,
                                                                        urlPattern:
                                                                            event.target.value,
                                                                    }
                                                                )
                                                            }
                                                        />
                                                        {renderUrlPatternValidationMessage(
                                                            urlPatternValidationMessage
                                                        )}
                                                    </div>
                                                    <div className="space-y-3">
                                                        {editorState.allowedWebsites.map(
                                                            (allowedWebsite, websiteIndex) => {
                                                                const inheritedWebsite =
                                                                    effectiveIpwConfig.allowedWebsites[
                                                                        websiteIndex
                                                                    ];
                                                                return (
                                                                    <AllowedWebsiteCustomizationEditor
                                                                        key={websiteIndex}
                                                                        idPrefix={`ipw_${ipwSystemIdKey}_allowed_website_${websiteIndex}`}
                                                                        title={
                                                                            inheritedWebsite?.name ??
                                                                            `Allowed website ${websiteIndex + 1}`
                                                                        }
                                                                        url={inheritedWebsite?.url}
                                                                        value={allowedWebsite}
                                                                        onChange={(nextWebsite) =>
                                                                            updateIpwConfigCustomizationForEdit(
                                                                                ipwSystemIdKey,
                                                                                {
                                                                                    ...editorState,
                                                                                    allowedWebsites:
                                                                                        editorState.allowedWebsites.map(
                                                                                            (
                                                                                                currentWebsite,
                                                                                                currentIndex
                                                                                            ) =>
                                                                                                currentIndex ===
                                                                                                websiteIndex
                                                                                                    ? nextWebsite
                                                                                                    : currentWebsite
                                                                                        ),
                                                                                }
                                                                            )
                                                                        }
                                                                    />
                                                                );
                                                            }
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                {enabledIpwSystemIdsForEdit.includes(IpwSystemId.DEALERTRACK) && (
                                    <div className="space-y-3 rounded-md border p-3">
                                        <p className="text-sm font-medium">
                                            Dealertrack integration configuration
                                        </p>
                                        <div className="space-y-2">
                                            <Label htmlFor="dealertrack_payment_code">
                                                Payment code
                                            </Label>
                                            <Input
                                                id="dealertrack_payment_code"
                                                value={dealertrackReconciliationForEdit.paymentCode}
                                                onChange={(event) => {
                                                    setDealertrackReconciliationForEdit({
                                                        ...dealertrackReconciliationForEdit,
                                                        paymentCode: event.target.value,
                                                    });
                                                }}
                                            />
                                        </div>
                                        <CheckboxField
                                            label="Inject merchant transaction reference"
                                            checked={
                                                dealertrackReconciliationForEdit.doInjectApprovalNumber
                                            }
                                            onCheckedChange={(checked) => {
                                                setDealertrackReconciliationForEdit({
                                                    ...dealertrackReconciliationForEdit,
                                                    doInjectApprovalNumber: checked,
                                                });
                                            }}
                                        />
                                        <CheckboxField
                                            label="Finalize reconciliation"
                                            checked={
                                                dealertrackReconciliationForEdit.doFinalizeReconciliation
                                            }
                                            onCheckedChange={(checked) => {
                                                setDealertrackReconciliationForEdit({
                                                    ...dealertrackReconciliationForEdit,
                                                    doFinalizeReconciliation: checked,
                                                });
                                            }}
                                        />
                                    </div>
                                )}

                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                    <div className="space-y-2">
                                        <p className="text-sm font-medium">Enabled Integrated Payments Websites</p>
                                        <div className="flex flex-wrap gap-2">
                                            {(org.enable_ipw_system_ids ?? []).map(
                                                (ipwSystemId: IpwSystemId) => (
                                                    <Badge key={ipwSystemId} variant="secondary">
                                                        {getSystemOptionLabelOrThrow(
                                                            ipwSystemOptions,
                                                            ipwSystemId
                                                        )}
                                                    </Badge>
                                                )
                                            )}
                                            {(org.enable_ipw_system_ids ?? []).length === 0 && (
                                                <p className="text-sm text-muted-foreground">
                                                    No integrated payments websites enabled
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    {readOnlyIpwPolicySummaries.length > 0 && (
                                        <IntegrationPolicySummary
                                            title="Integrated Payments Website Configuration"
                                            integrations={readOnlyIpwPolicySummaries}
                                        />
                                    )}
                                    {(org.enable_ipw_system_ids ?? []).includes(
                                        IpwSystemId.DEALERTRACK
                                    ) && (
                                        <div className="space-y-2">
                                            <p className="text-sm font-medium">
                                                Dealertrack integration configuration
                                            </p>
                                            <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-3">
                                            <p>
                                                Payment code:{' '}
                                                {getOrganizationDealertrackReconciliation(org)
                                                    ?.paymentCode ?? '-'}
                                                </p>
                                                <p>
                                                    Inject merchant transaction reference:{' '}
                                                    {getOrganizationDealertrackReconciliation(org)
                                                        ?.doInjectApprovalNumber
                                                        ? 'Yes'
                                                        : 'No'}
                                                </p>
                                                <p>
                                                    Finalize reconciliation:{' '}
                                                    {getOrganizationDealertrackReconciliation(org)
                                                        ?.doFinalizeReconciliation
                                                        ? 'Yes'
                                                        : 'No'}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    <div style={{ order: 14 }}>
                        <Card id="integrated-payment-sidebar-card" className="mb-6">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle>Integrated Payment Sidebar</CardTitle>
                                {renderMerchantConfigCardActions('sidebar', org)}
                            </CardHeader>
                            <CardContent>
                                {editingConfigCard === 'sidebar' ? (
                                    <div className="space-y-3">
                                    {renderMerchantConfigEditorAlerts()}
                                    <CheckboxField
                                        label="Require billing address for card payment"
                                        checked={addressRequiredForCardPaymentForEdit}
                                        onCheckedChange={(checked) => {
                                            setAddressRequiredForCardPaymentForEdit(checked);
                                        }}
                                    />
                                    <CheckboxField
                                        label="Automatically detect customer details in IPS"
                                        checked={
                                            dealertrackAutomaticCustomerDetailsDetectionEnabledForEdit
                                        }
                                        onCheckedChange={(checked) => {
                                            setDealertrackAutomaticCustomerDetailsDetectionEnabledForEdit(
                                                checked
                                            );
                                        }}
                                    />
                                    <CheckboxField
                                        label="Show manual customer details detection button in IPS"
                                        checked={
                                            dealertrackCustomerDetailsDetectionButtonEnabledForEdit
                                        }
                                        onCheckedChange={(checked) => {
                                            setDealertrackCustomerDetailsDetectionButtonEnabledForEdit(
                                                checked
                                            );
                                        }}
                                    />
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
                                            <p>
                                                Require billing address for card payment:{' '}
                                                {org.ips?.addressRequiredForCardPayment === false
                                                    ? 'No'
                                                    : 'Yes'}
                                            </p>
                                            <p>
                                                Automatic customer details detection:{' '}
                                                {org.ips?.automaticCustomerDetailsDetectionEnabled ===
                                                false
                                                    ? 'Disabled'
                                                    : 'Enabled'}
                                            </p>
                                            <p>
                                                Manual customer detection button:{' '}
                                                {org.ips?.customerDetailsDetectionButtonEnabled ===
                                                false
                                                    ? 'Disabled'
                                                    : 'Enabled'}
                                            </p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    <div style={{ order: 15 }}>
                        <Card id="merchant-security-card" className="mb-6">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle>Security</CardTitle>
                                {renderMerchantConfigCardActions('security', org)}
                            </CardHeader>
                            <CardContent>
                                {editingConfigCard === 'security' ? (
                                    <div className="space-y-4">
                                        {renderMerchantConfigEditorAlerts()}
                                        <CheckboxField
                                            label="Require Multifactor Authentication (MFA) for all users"
                                            checked={require2FAForEdit}
                                            onCheckedChange={setRequire2FAForEdit}
                                        />
                                        <CheckboxField
                                            label="Allow session cookie promotion"
                                            checked={allowPromoteSessionCookieForEdit}
                                            onCheckedChange={setAllowPromoteSessionCookieForEdit}
                                        />
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
                                        <p>
                                            Require Multifactor Authentication (MFA) for all
                                            users:{' '}
                                            {org.require2FA === true ? 'Enabled' : 'Disabled'}
                                        </p>
                                        <p>
                                            Allow session cookie promotion:{' '}
                                            {getAllowPromoteSessionCookie(organizationConfigData)
                                                ? 'yes'
                                                : 'no'}
                                        </p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    <div style={{ order: 16 }}>
                        <Card id="unmanaged-allowed-websites-card" className="mb-6">
                            <CardHeader className="flex flex-row items-center justify-between gap-3">
                                <CardTitle>Unmanaged Allowed Websites</CardTitle>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={handleOpenAddUnmanagedWebsiteDialog}
                                    disabled={!canCreateUnmanagedAllowedWebsite}
                                    aria-label="+ Website"
                                    title={
                                        canCreateUnmanagedAllowedWebsite
                                            ? 'Add website'
                                            : 'You do not have permission to add websites.'
                                    }
                                >
                                    <Plus className="mr-2 h-4 w-4" />
                                    Website
                                </Button>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    {(organizationWebsiteDeleteError !== null ||
                                        unlinkOrganizationWebsiteMutation.error ||
                                        customizeTemplateReferenceMutation.error) && (
                                        <Alert variant="destructive">
                                            <AlertDescription>
                                                {organizationWebsiteDeleteError?.message ??
                                                    (unlinkOrganizationWebsiteMutation.error instanceof
                                                    Error
                                                        ? unlinkOrganizationWebsiteMutation.error
                                                              .message
                                                        : customizeTemplateReferenceMutation.error instanceof
                                                            Error
                                                          ? customizeTemplateReferenceMutation.error
                                                                .message
                                                          : 'Failed to update allowed website')}
                                            </AlertDescription>
                                        </Alert>
                                    )}
                                    {securityAllowedWebsiteDisplayRows.length === 0 ? (
                                        <p className="text-sm text-muted-foreground">
                                            No unmanaged websites configured
                                        </p>
                                    ) : (
                                        <div className="grid gap-3 md:grid-cols-2">
                                            {securityAllowedWebsiteDisplayRows.map((row) => {
                                                const rowMutationPending =
                                                    unlinkOrganizationWebsiteMutation.isPending ||
                                                    customizeTemplateReferenceMutation.isPending ||
                                                    updateOrganizationWebsiteMutation.isPending;
                                                const canViewTemplate =
                                                    row.templateUuid !== null &&
                                                    row.capabilities?.can_view_template === true;
                                                const canUnlinkTemplate =
                                                    row.capabilities?.can_unlink_template === true;
                                                const canCustomizeTemplate =
                                                    row.capabilities?.can_customize_template === true;
                                                const canEditWebsite =
                                                    row.capabilities?.can_edit === true;
                                                const canDeleteWebsite =
                                                    row.capabilities?.can_delete === true;
                                                const canPromoteWebsite =
                                                    row.capabilities
                                                        ?.can_promote_to_template === true;
                                                const canNavigateTemplateCard =
                                                    row.source ===
                                                        OrganizationAllowedWebsiteSource.TEMPLATE &&
                                                    canViewTemplate &&
                                                    !rowMutationPending;
                                                const templateViewLabel =
                                                    'View template ' +
                                                    (row.templateName ?? row.websiteConfig.name);
                                                const templateCustomizeLabel =
                                                    'Customize template ' +
                                                    (row.templateName ?? row.websiteConfig.name);
                                                const templateUnlinkLabel =
                                                    'Unlink template ' +
                                                    (row.templateName ?? row.websiteConfig.name);
                                                return (
                                                    <div
                                                        key={row.rowUuid}
                                                        className={cn(
                                                            'space-y-2 rounded-md border p-3',
                                                            canNavigateTemplateCard &&
                                                                'cursor-pointer'
                                                        )}
                                                        role={
                                                            canNavigateTemplateCard
                                                                ? 'link'
                                                                : undefined
                                                        }
                                                        tabIndex={
                                                            canNavigateTemplateCard
                                                                ? 0
                                                                : undefined
                                                        }
                                                        aria-label={
                                                            canNavigateTemplateCard
                                                                ? templateViewLabel
                                                                : undefined
                                                        }
                                                        title={
                                                            canNavigateTemplateCard
                                                                ? templateViewLabel
                                                                : undefined
                                                        }
                                                        onClick={() => {
                                                            if (canNavigateTemplateCard) {
                                                                handleViewAllowedWebsiteTemplate(row);
                                                            }
                                                        }}
                                                        onKeyDown={(event) => {
                                                            if (
                                                                !canNavigateTemplateCard ||
                                                                event.currentTarget !== event.target
                                                            ) {
                                                                return;
                                                            }
                                                            if (
                                                                event.key === 'Enter' ||
                                                                event.key === ' '
                                                            ) {
                                                                event.preventDefault();
                                                                handleViewAllowedWebsiteTemplate(row);
                                                            }
                                                        }}
                                                    >
                                                        <div className="flex flex-wrap items-start justify-between gap-3">
                                                            <div className="space-y-2">
                                                                <div className="flex flex-wrap items-center gap-2">
                                                                    <p className="text-sm text-muted-foreground">
                                                                        Source:{' '}
                                                                        {formatSecurityAllowedWebsiteSource(
                                                                            row
                                                                        )}
                                                                    </p>
                                                                    <StatusBadge status={row.status} />
                                                                </div>
                                                                <div className="space-y-1 text-sm text-muted-foreground">
                                                                    {row.templateName !== null ? (
                                                                        <p>
                                                                            Template:{' '}
                                                                            {row.templateName}
                                                                        </p>
                                                                    ) : null}
                                                                    {row.templateProvenance !==
                                                                    null ? (
                                                                        <p>
                                                                            Template:{' '}
                                                                            {renderTemplateProvenanceLink(
                                                                                row.templateProvenance
                                                                            )}
                                                                        </p>
                                                                    ) : row.copiedFromTemplateName !==
                                                                      null ? (
                                                                        <p>
                                                                            Copied from:{' '}
                                                                            {
                                                                                row.copiedFromTemplateName
                                                                            }
                                                                        </p>
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                            <div className="flex flex-wrap gap-2">
                                                                {row.source ===
                                                                OrganizationAllowedWebsiteSource.TEMPLATE ? (
                                                                    <>
                                                                        <Button
                                                                            type="button"
                                                                            size="sm"
                                                                            variant="outline"
                                                                            aria-label={templateViewLabel}
                                                                            title={
                                                                                canViewTemplate
                                                                                    ? 'View template'
                                                                                    : 'You do not have permission to view this template.'
                                                                            }
                                                                            disabled={
                                                                                rowMutationPending ||
                                                                                !canViewTemplate
                                                                            }
                                                                            onClick={(event) => {
                                                                                event.stopPropagation();
                                                                                handleViewAllowedWebsiteTemplate(
                                                                                    row
                                                                                );
                                                                            }}
                                                                        >
                                                                            <ExternalLink className="mr-2 h-4 w-4" />
                                                                            View Template
                                                                        </Button>
                                                                        <Button
                                                                            type="button"
                                                                            size="sm"
                                                                            variant="outline"
                                                                            aria-label={templateCustomizeLabel}
                                                                            title={
                                                                                canCustomizeTemplate
                                                                                    ? 'Customize template'
                                                                                    : 'You do not have permission to customize this template.'
                                                                            }
                                                                            disabled={
                                                                                rowMutationPending ||
                                                                                !canCustomizeTemplate
                                                                            }
                                                                            onClick={(event) => {
                                                                                event.stopPropagation();
                                                                                handleCustomizeAllowedWebsiteTemplate(
                                                                                    row
                                                                                );
                                                                            }}
                                                                        >
                                                                            <Pencil className="mr-2 h-4 w-4" />
                                                                            Customize
                                                                        </Button>
                                                                        <Button
                                                                            type="button"
                                                                            size="sm"
                                                                            variant="destructive"
                                                                            aria-label={templateUnlinkLabel}
                                                                            title={
                                                                                canUnlinkTemplate
                                                                                    ? 'Unlink template from this merchant'
                                                                                    : 'You do not have permission to unlink this template.'
                                                                            }
                                                                            disabled={
                                                                                rowMutationPending ||
                                                                                !canUnlinkTemplate
                                                                            }
                                                                            onClick={(event) => {
                                                                                event.stopPropagation();
                                                                                handleUnlinkAllowedWebsiteTemplate(
                                                                                    row
                                                                                );
                                                                            }}
                                                                        >
                                                                            <Unlink className="mr-2 h-4 w-4" />
                                                                            Unlink
                                                                        </Button>
                                                                    </>
                                                                ) : null}
                                                                <Button
                                                                    type="button"
                                                                    variant="outline"
                                                                    size="sm"
                                                                    title={
                                                                        canEditWebsite
                                                                            ? 'Edit merchant-local website'
                                                                            : 'You do not have permission to edit this website.'
                                                                    }
                                                                    disabled={
                                                                        rowMutationPending ||
                                                                        !canEditWebsite
                                                                    }
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        const matchingRow =
                                                                            organizationAllowedWebsiteRows.find(
                                                                                (
                                                                                    organizationAllowedWebsite
                                                                                ) =>
                                                                                    organizationAllowedWebsite.organization_allowed_website_uuid ===
                                                                                    row.rowUuid
                                                                            );
                                                                        if (matchingRow !== undefined) {
                                                                            handleOpenEditOrganizationWebsiteDialog(
                                                                                matchingRow
                                                                            );
                                                                        }
                                                                    }}
                                                                >
                                                                    <Pencil className="h-4 w-4" />
                                                                </Button>
                                                                <Button
                                                                    type="button"
                                                                    variant="destructive"
                                                                    size="sm"
                                                                    title={
                                                                        canDeleteWebsite
                                                                            ? 'Delete merchant-local website'
                                                                            : 'You do not have permission to delete this website.'
                                                                    }
                                                                    disabled={
                                                                        rowMutationPending ||
                                                                        !canDeleteWebsite
                                                                    }
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        setPendingUnmanagedWebsiteDelete({
                                                                            organizationAllowedWebsiteUuid:
                                                                                row.rowUuid,
                                                                            websiteName:
                                                                                row.websiteConfig.name,
                                                                        });
                                                                    }}
                                                                >
                                                                    <Trash2 className="h-4 w-4" />
                                                                </Button>
                                                                <Button
                                                                    type="button"
                                                                    variant="outline"
                                                                    size="sm"
                                                                    title={
                                                                        canPromoteWebsite
                                                                            ? 'Make template'
                                                                            : 'You do not have permission to make a template from this website.'
                                                                    }
                                                                    disabled={
                                                                        promoteOrganizationWebsiteMutation.isPending ||
                                                                        rowMutationPending ||
                                                                        !canPromoteWebsite
                                                                    }
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        const matchingRow =
                                                                            organizationAllowedWebsiteRows.find(
                                                                                (
                                                                                    organizationAllowedWebsite
                                                                                ) =>
                                                                                    organizationAllowedWebsite.organization_allowed_website_uuid ===
                                                                                    row.rowUuid
                                                                            );
                                                                        if (matchingRow !== undefined) {
                                                                            handleOpenPromoteOrganizationWebsiteDialog(
                                                                                matchingRow
                                                                            );
                                                                        }
                                                                    }}
                                                                >
                                                                    <Upload className="mr-2 h-4 w-4" />
                                                                    Make template
                                                                </Button>
                                                            </div>
                                                        </div>
                                                        <AllowedWebsitePolicySummary
                                                            title={row.websiteConfig.name}
                                                            url={row.websiteConfig.url}
                                                            allowedUrlPatterns={
                                                                row.websiteConfig.allowedUrlPatterns
                                                            }
                                                            sessionPersistence={
                                                                row.websiteConfig.sessionPersistence
                                                            }
                                                            promoteSessionCookies={
                                                                row.websiteConfig
                                                                    .promoteSessionCookies
                                                            }
                                                            urlPatternsRules={
                                                                row.websiteConfig.urlPatternsRules
                                                            }
                                                            fetchDefaultAction={
                                                                row.websiteConfig.fetchDefaultAction
                                                            }
                                                            iframeUrlPatterns={
                                                                row.websiteConfig.iframeUrlPatterns
                                                            }
                                                            iframeDefaultAction={
                                                                row.websiteConfig.iframeDefaultAction
                                                            }
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    <div style={{ order: 11 }}>
                        <Card id="merchant-configuration-card" className="mb-6">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle>{organizationConfigurationTitle}</CardTitle>
                                {renderMerchantConfigCardActions('merchant', org)}
                            </CardHeader>
                            <CardContent>
                                {editingConfigCard === 'merchant' ? (
                                    <div className="space-y-4">
                                <div className="space-y-3 rounded-md border p-3">
                                    <p className="text-sm font-medium">Payment methods</p>
                                    <CheckboxField
                                        label="Terminal"
                                        checked={merchantPaymentMethodsForEdit.terminal}
                                        onCheckedChange={(checked) => {
                                            setMerchantPaymentMethodsForEdit({
                                                ...merchantPaymentMethodsForEdit,
                                                terminal: checked,
                                            });
                                        }}
                                    />
                                    <CheckboxField
                                        label="Card"
                                        checked={merchantPaymentMethodsForEdit.card}
                                        onCheckedChange={(checked) => {
                                            setMerchantPaymentMethodsForEdit({
                                                ...merchantPaymentMethodsForEdit,
                                                card: checked,
                                            });
                                        }}
                                    />
                                    <CheckboxField
                                        label="ACH"
                                        checked={merchantPaymentMethodsForEdit.ach}
                                        onCheckedChange={(checked) => {
                                            setMerchantPaymentMethodsForEdit({
                                                ...merchantPaymentMethodsForEdit,
                                                ach: checked,
                                            });
                                        }}
                                    />
                                    <CheckboxField
                                        label="E-Invoice"
                                        checked={merchantPaymentMethodsForEdit.e_invoice}
                                        onCheckedChange={(checked) => {
                                            setMerchantPaymentMethodsForEdit({
                                                ...merchantPaymentMethodsForEdit,
                                                e_invoice: checked,
                                            });
                                        }}
                                    />
                                </div>
                                <div className="space-y-3 rounded-md border p-3">
                                    <p className="text-sm font-medium">
                                        Customer portal payment requests
                                    </p>
                                    <CheckboxField
                                        label="Enable payment requests"
                                        checked={customerPortalPaymentRequestsForEdit.enabled}
                                        onCheckedChange={(checked) => {
                                            updateCustomerPortalPaymentRequestsForEdit(() => ({
                                                ...customerPortalPaymentRequestsForEdit,
                                                enabled: checked,
                                            }));
                                        }}
                                    />
                                    <div className="space-y-2">
                                        <p className="text-sm font-medium">
                                            Allowed customer delivery channels
                                        </p>
                                        <CheckboxField
                                            label="Allow Email delivery"
                                            checked={customerPortalPaymentRequestsForEdit.allowEmail}
                                            onCheckedChange={(checked) => {
                                                updateCustomerPortalPaymentRequestsForEdit(() => ({
                                                    ...customerPortalPaymentRequestsForEdit,
                                                    allowEmail: checked,
                                                }));
                                            }}
                                        />
                                        <CheckboxField
                                            label="Allow SMS delivery"
                                            disabled={
                                                !customerPortalPaymentRequestsAvailability.canAllowSmsDelivery
                                            }
                                            checked={customerPortalPaymentRequestsForEdit.allowSms}
                                            onCheckedChange={(checked) => {
                                                updateCustomerPortalPaymentRequestsForEdit(() => ({
                                                    ...customerPortalPaymentRequestsForEdit,
                                                    allowSms: checked,
                                                }));
                                            }}
                                        />
                                        {smsDeliveryEnablementMessage !== null && (
                                            <p className="text-xs text-gray-500">
                                                {smsDeliveryEnablementMessage}
                                            </p>
                                        )}
                                    </div>
                                    <div className="space-y-3 rounded-md border p-3">
                                        <p className="text-sm font-medium">Twilio SMS provider</p>
                                        <CheckboxField
                                            label="Enable Twilio SMS provider"
                                            disabled={
                                                !customerPortalPaymentRequestsAvailability.canEnableTwilioProvider
                                            }
                                            checked={customerPortalPaymentRequestsForEdit.twilioEnabled}
                                            onCheckedChange={(checked) => {
                                                updateCustomerPortalPaymentRequestsForEdit(() => ({
                                                    ...customerPortalPaymentRequestsForEdit,
                                                    twilioEnabled: checked,
                                                }));
                                            }}
                                        />
                                        {twilioProviderEnablementMessage !== null && (
                                            <p className="text-xs text-gray-500">
                                                {twilioProviderEnablementMessage}
                                            </p>
                                        )}
                                        <div className="space-y-2">
                                            <Label htmlFor="customer_portal_twilio_sender_mode">
                                                Sender routing
                                            </Label>
                                            <select
                                                id="customer_portal_twilio_sender_mode"
                                                className="w-full border rounded px-3 py-2"
                                                value={
                                                    customerPortalPaymentRequestsForEdit.twilioSenderMode
                                                }
                                                onChange={(event) => {
                                                    const nextMode: TwilioSenderMode =
                                                        event.target.value === 'from_phone_number'
                                                            ? 'from_phone_number'
                                                            : 'messaging_service_sid';
                                                    updateCustomerPortalPaymentRequestsForEdit(() => ({
                                                        ...customerPortalPaymentRequestsForEdit,
                                                        twilioSenderMode: nextMode,
                                                    }));
                                                }}
                                            >
                                                <option value="messaging_service_sid">
                                                    Messaging Service SID
                                                </option>
                                                <option value="from_phone_number">
                                                    From phone number
                                                </option>
                                            </select>
                                        </div>
                                        {customerPortalPaymentRequestsForEdit.twilioSenderMode ===
                                        'from_phone_number' ? (
                                            <div className="space-y-2">
                                                <Label htmlFor="customer_portal_twilio_from_phone">
                                                    From phone number
                                                </Label>
                                                    <Input
                                                        id="customer_portal_twilio_from_phone"
                                                        value={
                                                            customerPortalPaymentRequestsForEdit.twilioFromPhoneNumber
                                                        }
                                                        onChange={(event) => {
                                                            updateCustomerPortalPaymentRequestsForEdit(() => ({
                                                                ...customerPortalPaymentRequestsForEdit,
                                                                twilioFromPhoneNumber:
                                                                    formatPhoneNumber(
                                                                        event.target.value
                                                                    ),
                                                            }));
                                                        }}
                                                    />
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                <Label htmlFor="customer_portal_twilio_messaging_service_sid">
                                                    Messaging Service SID
                                                </Label>
                                                <Input
                                                    id="customer_portal_twilio_messaging_service_sid"
                                                    value={
                                                        customerPortalPaymentRequestsForEdit.twilioMessagingServiceSid
                                                    }
                                                    onChange={(event) => {
                                                        updateCustomerPortalPaymentRequestsForEdit(() => ({
                                                            ...customerPortalPaymentRequestsForEdit,
                                                            twilioMessagingServiceSid:
                                                                event.target.value,
                                                        }));
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="space-y-3 rounded-md border p-3">
                                        <p className="text-sm font-medium">SendGrid email provider</p>
                                        <CheckboxField
                                            label="Enable SendGrid email provider"
                                            checked={
                                                customerPortalPaymentRequestsForEdit.sendgridEnabled
                                            }
                                            onCheckedChange={(checked) => {
                                                updateCustomerPortalPaymentRequestsForEdit(() => ({
                                                    ...customerPortalPaymentRequestsForEdit,
                                                    sendgridEnabled: checked,
                                                }));
                                            }}
                                        />
                                        <div className="space-y-2">
                                            <Label htmlFor="customer_portal_sendgrid_from_email">
                                                From email
                                            </Label>
                                            <Input
                                                id="customer_portal_sendgrid_from_email"
                                                value={
                                                    customerPortalPaymentRequestsForEdit.sendgridFromEmail
                                                }
                                                onChange={(event) => {
                                                    updateCustomerPortalPaymentRequestsForEdit(() => ({
                                                        ...customerPortalPaymentRequestsForEdit,
                                                        sendgridFromEmail: event.target.value,
                                                    }));
                                                }}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="customer_portal_sendgrid_from_name">
                                                From name
                                            </Label>
                                            <Input
                                                id="customer_portal_sendgrid_from_name"
                                                value={
                                                    customerPortalPaymentRequestsForEdit.sendgridFromName
                                                }
                                                onChange={(event) => {
                                                    updateCustomerPortalPaymentRequestsForEdit(() => ({
                                                        ...customerPortalPaymentRequestsForEdit,
                                                        sendgridFromName: event.target.value,
                                                    }));
                                                }}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="customer_portal_sendgrid_reply_to_email">
                                                Reply-to email
                                            </Label>
                                            <Input
                                                id="customer_portal_sendgrid_reply_to_email"
                                                value={
                                                    customerPortalPaymentRequestsForEdit.sendgridReplyToEmail
                                                }
                                                onChange={(event) => {
                                                    updateCustomerPortalPaymentRequestsForEdit(() => ({
                                                        ...customerPortalPaymentRequestsForEdit,
                                                        sendgridReplyToEmail: event.target.value,
                                                    }));
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-3 rounded-md border p-3">
                                        <p className="text-sm font-medium">
                                            Merchant payment confirmation CC emails
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {merchantPaymentConfirmationEmailCcForEdit.map((email) => (
                                                <Badge
                                                    key={email}
                                                    variant="secondary"
                                                    className="gap-2 pr-1"
                                                >
                                                    {email}
                                                    <button
                                                        type="button"
                                                        aria-label={`Remove CC email ${email}`}
                                                        className="rounded px-1 hover:bg-muted-foreground/20"
                                                        onClick={() => {
                                                            setMerchantPaymentConfirmationEmailCcForEdit(
                                                                merchantPaymentConfirmationEmailCcForEdit.filter(
                                                                    (currentEmail) =>
                                                                        currentEmail !== email
                                                                )
                                                            );
                                                        }}
                                                    >
                                                        x
                                                    </button>
                                                </Badge>
                                            ))}
                                            {merchantPaymentConfirmationEmailCcForEdit.length === 0 && (
                                                <p className="text-sm text-muted-foreground">
                                                    No CC emails configured
                                                </p>
                                            )}
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="payment_request_email_cc_input">
                                                Add CC email
                                            </Label>
                                            <Input
                                                id="payment_request_email_cc_input"
                                                value={merchantPaymentConfirmationEmailCcInput}
                                                onChange={(event) => {
                                                    setMerchantPaymentConfirmationEmailCcInput(
                                                        event.target.value
                                                    );
                                                    setMerchantPaymentConfirmationEmailCcInputError(
                                                        null
                                                    );
                                                }}
                                                onKeyDown={(event) => {
                                                    if (
                                                        event.key === 'Enter' ||
                                                        event.key === 'Tab'
                                                    ) {
                                                        event.preventDefault();
                                                        commitPaymentRequestEmailCcInput();
                                                    }
                                                }}
                                            />
                                            {merchantPaymentConfirmationEmailCcInputError !== null && (
                                                <p className="text-sm text-destructive">
                                                    {merchantPaymentConfirmationEmailCcInputError}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {renderMerchantConfigEditorAlerts()}
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                    <div className="space-y-2">
                                        <p className="text-sm font-medium">Payment methods</p>
                                        <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
                                            <p>
                                                Terminal:{' '}
                                                {org.merchantPaymentMethods?.terminal ? 'Yes' : 'No'}
                                            </p>
                                            <p>
                                                Card:{' '}
                                                {org.merchantPaymentMethods?.card ? 'Yes' : 'No'}
                                            </p>
                                            <p>
                                                ACH:{' '}
                                                {org.merchantPaymentMethods?.ach ? 'Yes' : 'No'}
                                            </p>
                                            <p>
                                                E-Invoice:{' '}
                                                {org.merchantPaymentMethods?.e_invoice ? 'Yes' : 'No'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <p className="text-sm font-medium">
                                            Customer portal payment requests
                                        </p>
                                        <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
                                            <p>
                                                Enabled:{' '}
                                                {org.customerPortalPaymentRequests?.enabled
                                                    ? 'Yes'
                                                    : 'No'}
                                            </p>
                                            <p>
                                                Allowed delivery channels:{' '}
                                                {(org.customerPortalPaymentRequests?.allowedDeliveryChannels
                                                    ?.length ?? 0) > 0
                                                    ? org.customerPortalPaymentRequests?.allowedDeliveryChannels.join(
                                                          ', '
                                                      )
                                                    : 'None'}
                                            </p>
                                            <p>
                                                Twilio SMS sender:{' '}
                                                {org.customerPortalPaymentRequests?.twilioSms ===
                                                undefined
                                                    ? 'Not configured'
                                                    : 'fromPhoneNumber' in
                                                          org.customerPortalPaymentRequests.twilioSms
                                                      ? formatPhoneNumber(
                                                            org.customerPortalPaymentRequests
                                                                .twilioSms.fromPhoneNumber ?? ''
                                                        )
                                                      : org.customerPortalPaymentRequests.twilioSms
                                                            .messagingServiceSid}
                                            </p>
                                            <p>
                                                SendGrid from email:{' '}
                                                {org.customerPortalPaymentRequests?.sendgridEmail
                                                    ?.fromEmail ?? 'Not configured'}
                                            </p>
                                            <p>
                                                SendGrid from name:{' '}
                                                {org.customerPortalPaymentRequests?.sendgridEmail
                                                    ?.fromName ?? 'Not configured'}
                                            </p>
                                            <p>
                                                SendGrid reply-to email:{' '}
                                                {org.customerPortalPaymentRequests?.sendgridEmail
                                                    ?.replyToEmail ?? 'Not configured'}
                                            </p>
                                            <div className="space-y-2 md:col-span-2">
                                                <p>Merchant payment confirmation CC emails:</p>
                                                <div className="flex flex-wrap gap-2">
                                                    {(org.merchantPaymentConfirmationEmailCc ?? [])
                                                        .length > 0 ? (
                                                        (org.merchantPaymentConfirmationEmailCc ?? []).map(
                                                            (email) => (
                                                                <Badge
                                                                    key={email}
                                                                    variant="secondary"
                                                                >
                                                                    {email}
                                                                </Badge>
                                                            )
                                                        )
                                                    ) : (
                                                        <p className="text-sm text-muted-foreground">
                                                            None
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </>
            )}
            </div>

            {canRenderMerchantBackupCard && (
                <div style={{ order: 110 }}>
                    <Card className="mb-6">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0">
                            <CardTitle>Merchant Backup</CardTitle>
                            <div title={merchantBackupButtonTooltip} className="flex gap-2">
                                <Button
                                    onClick={() => {
                                        exportMerchantBackupMutation.mutate('configuration');
                                    }}
                                    disabled={
                                        exportMerchantBackupMutation.isPending || isMerchantSubsidiary
                                    }
                                >
                                    {exportMerchantBackupMutation.isPending
                                        ? 'Preparing backup...'
                                        : 'Configuration Backup'}
                                </Button>
                                <Button
                                    onClick={() => {
                                        exportMerchantBackupMutation.mutate('full');
                                    }}
                                    disabled={
                                        exportMerchantBackupMutation.isPending || isMerchantSubsidiary
                                    }
                                >
                                    {exportMerchantBackupMutation.isPending
                                        ? 'Preparing backup...'
                                        : 'Full Backup'}
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <p className="text-sm text-muted-foreground">
                                Download a JSON backup for this merchant organization.
                            </p>
                            {exportMerchantBackupMutation.error !== null && (
                                <p className="text-sm text-destructive">
                                    {String(exportMerchantBackupMutation.error)}
                                </p>
                            )}
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Delete/Restore Organization Card - Only for superadmins */}
            {hasSuperadminPrivileges(activeRoleAssignment ?? null) && (
                <div style={{ order: 120 }}>
                    <Card className="mb-6">
                        <CardHeader>
                            <CardTitle>
                                {org.status === 'DELETED'
                                    ? 'Restore Organization'
                                    : 'Delete Organization'}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center justify-between">
                                <p className="text-sm text-muted-foreground">
                                    {org.status === 'DELETED'
                                        ? 'Restore this organization.'
                                        : 'Delete this organization.'}
                                </p>
                                <Button
                                    variant={org.status === 'DELETED' ? 'default' : 'destructive'}
                                    onClick={handleDelete}
                                    disabled={deleteMutation.isPending || undeleteMutation.isPending}
                                >
                                    {deleteMutation.isPending || undeleteMutation.isPending
                                        ? org.status === 'DELETED'
                                            ? 'Restoring...'
                                            : 'Deleting...'
                                        : org.status === 'DELETED'
                                          ? 'Undelete'
                                          : 'Delete'}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            <Dialog
                open={promoteOrganizationWebsiteDialogState !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        setPromoteOrganizationWebsiteDialogState(null);
                    }
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Make Website Template</DialogTitle>
                        <DialogDescription>
                            Create a reusable allowed website template from this merchant
                            website.
                        </DialogDescription>
                    </DialogHeader>
                    {promoteOrganizationWebsiteDialogState !== null ? (
                        <div className="space-y-4">
                            {(promoteOrganizationWebsiteDialogState.error !== null ||
                                promoteOrganizationWebsiteMutation.error) && (
                                <Alert variant="destructive">
                                    <AlertDescription>
                                        {promoteOrganizationWebsiteDialogState.error ??
                                            (promoteOrganizationWebsiteMutation.error instanceof
                                            Error
                                                ? promoteOrganizationWebsiteMutation.error
                                                      .message
                                                : 'Failed to promote website to template')}
                                    </AlertDescription>
                                </Alert>
                            )}
                            <p className="text-sm text-muted-foreground">
                                Source website:{' '}
                                {promoteOrganizationWebsiteDialogState.websiteName}
                            </p>
                            <div className="space-y-2">
                                <Label htmlFor="promote_template_name">
                                    Template name
                                </Label>
                                <Input
                                    id="promote_template_name"
                                    value={
                                        promoteOrganizationWebsiteDialogState.templateName
                                    }
                                    onChange={(event) =>
                                        setPromoteOrganizationWebsiteDialogState({
                                            ...promoteOrganizationWebsiteDialogState,
                                            templateName: event.target.value,
                                            error: null,
                                        })
                                    }
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Allowed website</Label>
                                <p className="text-sm">
                                    {formatAllowedWebsiteSystemName(
                                        promoteOrganizationWebsiteDialogState.systemId
                                    )}
                                </p>
                            </div>
                        </div>
                    ) : null}
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() =>
                                setPromoteOrganizationWebsiteDialogState(null)
                            }
                            disabled={promoteOrganizationWebsiteMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={handleSavePromoteOrganizationWebsiteDialog}
                            disabled={promoteOrganizationWebsiteMutation.isPending}
                        >
                            {promoteOrganizationWebsiteMutation.isPending
                                ? 'Making template...'
                                : 'Make template'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog
                open={unmanagedWebsiteDialogState !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        setUnmanagedWebsiteDialogState(null);
                    }
                }}
            >
                <DialogContent className="max-h-[calc(100vh-4rem)] w-[min(calc(100vw-2rem),42rem)] overflow-hidden">
                    <DialogHeader>
                        <DialogTitle>
                            {unmanagedWebsiteDialogState?.mode === 'edit-organization'
                                ? 'Edit Website'
                                : 'Add Website'}
                        </DialogTitle>
                        <DialogDescription>
                            Configure one unmanaged website for the Anchorbase App Navigate menu.
                        </DialogDescription>
                    </DialogHeader>
                    {unmanagedWebsiteDialogState !== null && (
                        <div
                            className="max-h-[calc(100vh-20rem)] space-y-4 overflow-y-auto px-1 pb-1"
                            data-testid="add-website-dialog-body"
                        >
                            <div className="space-y-2">
                                <Label htmlFor="unmanaged_website_name">Name</Label>
                                <Input
                                    id="unmanaged_website_name"
                                    value={unmanagedWebsiteDialogState.editorState.name}
                                    disabled={isUnmanagedWebsiteTemplateSelected}
                                    onChange={(event) =>
                                        setUnmanagedWebsiteDialogState({
                                            ...unmanagedWebsiteDialogState,
                                            editorState: {
                                                ...unmanagedWebsiteDialogState.editorState,
                                                name: event.target.value,
                                            },
                                            error: null,
                                        })
                                    }
                                />
                            </div>
                            {unmanagedWebsiteDialogState.mode === 'add' ? (
                                <div
                                    className="space-y-2"
                                    title={
                                        isUnmanagedWebsiteTemplateSelected
                                            ? 'Template-provided fields are read-only.'
                                            : undefined
                                    }
                                >
                                    <OmnisearchSelect<AllowedWebsiteTemplateListItem, string>
                                        label="Select Website Template"
                                        placeholder="Search templates..."
                                        pageSize={25}
                                        selectedItemId={
                                            unmanagedWebsiteDialogState.selectedTemplate
                                                ?.allowed_website_template_uuid ?? null
                                        }
                                        selectedItemLabel={
                                            unmanagedWebsiteDialogState.selectedTemplate
                                                ?.name ?? '<none>'
                                        }
                                        overlayMenu={true}
                                        menuViewportClassName="h-64"
                                        fetchPage={async (params) => {
                                            if (org?.organization_uuid === undefined) {
                                                throw new Error(
                                                    'Organization is required to search usable templates.'
                                                );
                                            }
                                            const response:
                                                AllowedWebsiteTemplateSearchPageResponse =
                                                await allowedWebsiteTemplatesApi.searchUsableTemplatesForOrganization(
                                                    org.organization_uuid,
                                                    {
                                                        limit: params.limit,
                                                        cursor: params.cursor,
                                                        ...(params.terms !== undefined
                                                            ? { terms: [...params.terms] }
                                                            : {}),
                                                    }
                                                );
                                            return {
                                                items: response.templates,
                                                pagination: response.pagination,
                                            };
                                        }}
                                        getItemId={(template) =>
                                            template.allowed_website_template_uuid
                                        }
                                        getItemLabel={(template) => template.name}
                                        getItemFilterText={(template) =>
                                            [
                                                template.name,
                                                template.primary_url,
                                                template.website_config.name,
                                                ...template.website_config
                                                    .allowedUrlPatterns,
                                            ].join(' ')
                                        }
                                        renderItemCard={(template) => (
                                            <div>
                                                <p className="font-medium">
                                                    {template.name}
                                                </p>
                                                <p className="break-all text-muted-foreground">
                                                    {template.primary_url}
                                                </p>
                                            </div>
                                        )}
                                        onSelect={handleSelectAddWebsiteTemplate}
                                    />
                                    {unmanagedWebsiteDialogState.selectedTemplate !==
                                    null ? (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={handleClearAddWebsiteTemplate}
                                        >
                                            Clear template
                                        </Button>
                                    ) : null}
                                </div>
                            ) : null}
                            <div className="space-y-2">
                                <Label htmlFor="unmanaged_website_url">URL</Label>
                                <Input
                                    id="unmanaged_website_url"
                                    value={unmanagedWebsiteDialogState.editorState.url}
                                    disabled={isUnmanagedWebsiteTemplateSelected}
                                    onChange={(event) =>
                                        setUnmanagedWebsiteDialogState({
                                            ...unmanagedWebsiteDialogState,
                                            editorState: {
                                                ...unmanagedWebsiteDialogState.editorState,
                                                url: event.target.value,
                                            },
                                            error: null,
                                        })
                                    }
                                />
                                {renderUrlPatternValidationMessage(
                                    getUnmanagedWebsiteUrlValidationMessage(
                                        unmanagedWebsiteDialogState.editorState
                                    )
                                )}
                            </div>
                            <AllowedWebsiteCustomizationEditor
                                idPrefix="unmanaged_website"
                                title="Website policy"
                                value={{
                                    allowedUrlPatternsText:
                                        unmanagedWebsiteDialogState.editorState
                                            .allowedUrlPatternsText,
                                    sessionPersistence:
                                        unmanagedWebsiteDialogState.editorState
                                            .sessionPersistence,
                                    promoteSessionCookies:
                                        unmanagedWebsiteDialogState.editorState
                                            .promoteSessionCookies,
                                    urlPatternsRules:
                                        unmanagedWebsiteDialogState.editorState
                                            .urlPatternsRules,
                                    fetchDefaultAction:
                                        unmanagedWebsiteDialogState.editorState
                                            .fetchDefaultAction,
                                    iframeUrlPatterns:
                                        unmanagedWebsiteDialogState.editorState
                                            .iframeUrlPatterns,
                                    iframeDefaultAction:
                                        unmanagedWebsiteDialogState.editorState
                                            .iframeDefaultAction,
                                }}
                                disabled={isUnmanagedWebsiteTemplateSelected}
                                onChange={(nextPolicy) =>
                                    setUnmanagedWebsiteDialogState({
                                        ...unmanagedWebsiteDialogState,
                                        editorState: {
                                            ...unmanagedWebsiteDialogState.editorState,
                                            allowedUrlPatternsText:
                                                nextPolicy.allowedUrlPatternsText,
                                            sessionPersistence:
                                                nextPolicy.sessionPersistence,
                                            promoteSessionCookies:
                                                nextPolicy.promoteSessionCookies,
                                            urlPatternsRules: nextPolicy.urlPatternsRules,
                                            fetchDefaultAction:
                                                nextPolicy.fetchDefaultAction,
                                            iframeUrlPatterns: nextPolicy.iframeUrlPatterns,
                                            iframeDefaultAction:
                                                nextPolicy.iframeDefaultAction,
                                        },
                                        error: null,
                                    })
                                }
                            />
                            {unmanagedWebsiteDialogState.error !== null && (
                                <Alert variant="destructive">
                                    <AlertDescription>
                                        {unmanagedWebsiteDialogState.error}
                                    </AlertDescription>
                                </Alert>
                            )}
                        </div>
                    )}
                    <DialogFooter className="border-t pt-4">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setUnmanagedWebsiteDialogState(null)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={handleSaveUnmanagedWebsiteDialog}
                            disabled={isUnmanagedWebsiteDialogSaveDisabled}
                        >
                            {isUnmanagedWebsiteDialogMutationPending ? 'Saving...' : 'Save'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ConfirmationModal
                open={pendingUnmanagedWebsiteDelete !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        setPendingUnmanagedWebsiteDelete(null);
                    }
                }}
                title="Delete unmanaged website"
                message={
                    pendingUnmanagedWebsiteDelete === null
                        ? ''
                        : `Delete ${pendingUnmanagedWebsiteDelete.websiteName}?`
                }
                onConfirm={handleConfirmDeleteUnmanagedWebsite}
                confirmLabel="Delete"
            />

            {/* Delete/Restore Organization Confirmation Modal */}
            {org && (
                <ConfirmationModal
                    open={showDeleteConfirm}
                    onOpenChange={setShowDeleteConfirm}
                    message={
                        org.status === 'DELETED'
                            ? `Are you sure you want to undelete ${org.name}?`
                            : `Are you sure you want to delete ${org.name}?`
                    }
                    onConfirm={handleConfirmDelete}
                    confirmLabel={org.status === 'DELETED' ? 'Undelete' : 'Delete'}
                    isLoading={deleteMutation.isPending || undeleteMutation.isPending}
                />
            )}

            {/* Reassign Organization Confirmation Modal */}
            {org && (
                <ConfirmationModal
                    open={isParentOrganizationReassignConfirmationModalOpen}
                    onOpenChange={(open: boolean) => {
                        setShowReassignConfirm(open);
                    }}
                    message={
                        isParentOrganizationReassignConfirmationModalOpen
                            ? buildParentOrganizationConfirmationMessage()
                            : ''
                    }
                    onConfirm={handleConfirmReassign}
                    confirmLabel={pendingParentAction === 'revoke' ? 'Revoke' : 'Assign'}
                    isLoading={
                        setParentOrganizationMutation.isPending ||
                        setIsoOrganizationMutation.isPending
                    }
                />
            )}

        </div>
    );
}
